import type {
  AdminAppSignalKey,
  AdminAppSignalStage,
  AdminAccountDeletionStatus,
  AdminLatestFailure,
  AdminLatestSuccessfulSave,
  AdminFileData,
  AdminIncidentLogArtifactData,
  AdminJobData,
  AdminJobStatus,
  AdminMediaArtifactData,
  AdminProjectionFreshness,
  AdminQueryMeta,
  AdminReceiptData,
  AdminRecordData,
  AdminRecordType,
  AdminUploadDiagnosisEnrichment,
  AdminUserDataDetail,
  AdminUserSummary,
} from "../../contracts/admin-read-models";
import type { AdminUserLookupInput } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { localizeAdminUserRiskItems } from "../../shared/locale/admin-user-risk-copy";
import {
  formatAdminAccountDeletionStatusLabel,
  formatAdminJobStatusLabel,
  formatAdminRiskLevelLabel,
} from "../../shared/locale/admin-dashboard-status-copy";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import {
  formatAdminAccountIdentityLabel,
  formatAdminLinkedProviderCountLabel,
  formatAdminProviderIdentityLabel,
} from "../../shared/formatting/format-admin-account-identity";
import { formatAdminAuthProviderKindLabel } from "../../shared/formatting/format-admin-auth-provider";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { formatAdminDuration } from "../../shared/formatting/format-admin-duration";
import {
  formatAdminBooleanLabel,
  formatAdminChatMembershipCountBucketLabel,
  formatAdminChatMessageLengthBucketLabel,
  formatAdminChatRoomKindLabel,
  formatAdminCommunityContentLengthBucketLabel,
  formatAdminCommunityImageCountBucketLabel,
  formatAdminCommunityTopicTagLabel,
  formatAdminCommunityUploadSourceBucketLabel,
  formatAdminMedicationDosePresenceLabel,
  formatAdminMedicationOccurrenceActionKindLabel,
  formatAdminMedicationScheduleRecurrenceBucketLabel,
  formatAdminMedicationSnoozeWindowBucketLabel,
  formatAdminPetAttemptCountBucketLabel,
  formatAdminPetBreedSelectionBucketLabel,
  formatAdminPetProfileCompletionStepLabel,
  formatAdminPetSpeciesBucketLabel,
  formatAdminReceiptCategoryMutationTypeLabel,
  formatAdminReceiptExtractSourceBucketLabel,
  formatAdminReceiptImageByteSizeBucketLabel,
  formatAdminReceiptOcrConfidenceBucketLabel,
  formatAdminRideLongestAddressLengthBucketLabel,
  formatAdminRideVisitLeadTimeBucketLabel,
  formatAdminSocialProfileMutationFieldSetBucketLabel,
  formatAdminSocialPublicPetCountBucketLabel,
  formatAdminSocialTargetUserLookupKindLabel,
  formatAdminTelemetryAppBuildNumberLabel,
  formatAdminTelemetryDeviceModelBucketLabel,
  formatAdminTelemetryDeviceModelNameBucketLabel,
  formatAdminTelemetryLocaleLabel,
  formatAdminTelemetryNetworkTypeBucketLabel,
  formatAdminTelemetryOsMajorBucketLabel,
  formatAdminTelemetryOsVersionMinorBucketLabel,
  formatAdminTelemetryPhysicalDeviceBucketLabel,
  formatAdminTelemetryReauthMethodBucketLabel,
  formatAdminTelemetryRecentAuthAgeBucketLabel,
  formatAdminTelemetryRuntimeCoverageLabel,
  formatAdminTelemetryRuntimeSummaryLabel,
  formatAdminTelemetryTimezoneNameBucketLabel,
  formatAdminTelemetryTimezoneOffsetBucketLabel,
} from "../../shared/formatting/format-admin-runtime-context";
import type { AdminUserDataRiskSummary } from "./users-use-cases";

type MediaScenarioKey =
  | "all"
  | "avatar"
  | "pet_profile"
  | "daily_record"
  | "receipt"
  | "health_record"
  | "community_post"
  | "unknown";

type UploadDiagnosisScenarioKey = "health" | "receipt" | "daily";
type UploadDiagnosisStatusValue =
  | "ready"
  | "processing"
  | "queued"
  | "succeeded"
  | "failed"
  | "attention"
  | "unavailable";

type UploadDiagnosisEnrichmentRowViewModel = {
  key: string;
  label: string;
  value: string;
};

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return pickAdminDashboardLocaleText(locale, { en, ko });
}

function formatScenarioCount(
  count: number,
  locale: AdminDashboardLocale,
  englishNoun: string,
  koreanNoun: string,
): string {
  return locale === "ko"
    ? `${formatAdminCount(count)}개 ${koreanNoun}`
    : `${formatAdminCount(count)} ${englishNoun}`;
}

function formatMediaScenarioLabel(
  scenarioKey: Exclude<MediaScenarioKey, "all">,
  locale: AdminDashboardLocale,
): string {
  switch (scenarioKey) {
    case "avatar":
      return text(locale, "Avatar", "아바타");
    case "pet_profile":
      return text(locale, "Pet profile", "펫 프로필");
    case "daily_record":
      return text(locale, "Daily record", "일상 기록");
    case "receipt":
      return text(locale, "Receipt", "영수증");
    case "health_record":
      return text(locale, "Health record", "건강 기록");
    case "community_post":
      return text(locale, "Community post", "커뮤니티 게시물");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatFilePurposeLabel(
  purpose: AdminFileData["purpose"],
  locale: AdminDashboardLocale,
): string {
  switch (purpose) {
    case "pet_profile_photo":
      return text(locale, "Pet profile photo", "펫 프로필 사진");
    case "user_avatar":
      return text(locale, "User avatar", "사용자 아바타");
    case "daily_record_photo":
      return text(locale, "Daily record photo", "일상 기록 사진");
    case "daily_record_video":
      return text(locale, "Daily record video", "일상 기록 동영상");
    case "community_post_image":
      return text(locale, "Community post image", "커뮤니티 게시물 이미지");
    case "health_record_source":
    case "health_source":
      return text(locale, "Health source file", "건강 기록 원본 파일");
    case "receipt_source":
      return text(locale, "Legacy receipt source file", "구형 영수증 원본 파일");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatFileStatusLabel(
  status: AdminFileData["status"],
  locale: AdminDashboardLocale,
): string {
  switch (status) {
    case "temp":
      return text(locale, "Temporary", "임시");
    case "confirmed":
      return text(locale, "Confirmed", "확정");
    case "expired":
      return text(locale, "Expired", "만료");
    case "deleted":
      return text(locale, "Deleted", "삭제됨");
    case "cleanup_scheduled":
      return text(locale, "Cleanup scheduled", "정리 예정");
    case "cleanup_processed":
      return text(locale, "Cleanup processed", "정리 완료");
    case "cleanup_failed":
      return text(locale, "Cleanup failed", "정리 실패");
  }
}

function formatFileCleanupScheduledAgeBucketLabel(
  value: AdminFileData["cleanupScheduledAgeBucket"] | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "immediate":
      return text(locale, "Immediate", "즉시");
    case "lt_5m":
      return text(locale, "Under 5m", "5분 미만");
    case "5m_to_30m":
      return text(locale, "5m to 30m", "5분~30분");
    case "30m_plus":
      return text(locale, "30m+", "30분 이상");
    case "unknown":
      return text(locale, "Unknown", "미확인");
    default:
      return "-";
  }
}

function formatFileCleanupAttemptCountBucketLabel(
  value: AdminFileData["cleanupAttemptCountBucket"] | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "0":
      return text(locale, "0", "0");
    case "1":
      return text(locale, "1", "1");
    case "2_to_3":
      return text(locale, "2 to 3", "2~3");
    case "4_plus":
      return text(locale, "4+", "4 이상");
    case "unknown":
      return text(locale, "Unknown", "미확인");
    default:
      return "-";
  }
}

function formatFileOrphanClassificationLabel(
  value: AdminFileData["orphanClassification"] | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "expired_temp_file":
      return text(locale, "Expired temp file", "만료된 임시 파일");
    case "deleted_file_pending_storage_cleanup":
      return text(locale, "Deleted file pending storage cleanup", "스토리지 정리 대기 삭제 파일");
    case "unknown":
      return text(locale, "Unknown", "미확인");
    default:
      return "-";
  }
}

function formatFileStorageDeleteOutcomeCategoryLabel(
  value: AdminFileData["storageDeleteOutcomeCategory"] | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "not_started":
      return text(locale, "Not started", "시작 전");
    case "zero_candidates":
      return text(locale, "Zero candidates", "대상 없음");
    case "all_deleted":
      return text(locale, "All deleted", "전체 삭제");
    case "partial_deleted":
      return text(locale, "Partially deleted", "부분 삭제");
    case "none_deleted":
      return text(locale, "None deleted", "삭제 없음");
    case "request_failed":
      return text(locale, "Request failed", "요청 실패");
    case "unknown":
      return text(locale, "Unknown", "미확인");
    default:
      return "-";
  }
}

function formatFileLinkedEntityKindLabel(
  value: AdminFileData["linkedEntityKind"] | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "pet":
      return text(locale, "Pet", "펫");
    case "user":
      return text(locale, "User", "사용자");
    case "record":
      return text(locale, "Record", "기록");
    case "receipt":
      return text(locale, "Receipt", "영수증");
    case "mixed":
      return text(locale, "Mixed", "혼합");
    case "unknown":
      return text(locale, "Unknown", "미확인");
    default:
      return "-";
  }
}

function formatMediaRelationTypeLabel(
  relationType: AdminMediaArtifactData["relationType"],
  locale: AdminDashboardLocale,
): string {
  switch (relationType) {
    case "direct":
      return text(locale, "Direct", "직접 연결");
    case "contextual":
      return text(locale, "Contextual", "문맥 연결");
  }
}

function formatMediaArtifactRoleLabel(
  artifactRole: AdminMediaArtifactData["artifactRole"],
  locale: AdminDashboardLocale,
): string {
  switch (artifactRole) {
    case "representative":
      return text(locale, "Representative", "대표");
    case "gallery_member":
      return text(locale, "Gallery member", "갤러리 구성원");
    case "original_only":
      return text(locale, "Original only", "원본 전용");
    case "evidence_sidecar":
      return text(locale, "Evidence sidecar", "증거 보조");
  }
}

function formatMediaKindLabel(
  mediaKind: AdminMediaArtifactData["mediaKind"],
  locale: AdminDashboardLocale,
): string {
  switch (mediaKind) {
    case "image":
      return text(locale, "Image", "이미지");
    case "video":
      return text(locale, "Video", "동영상");
    case "document":
      return text(locale, "Document", "문서");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatMediaAvailabilityLabel(
  availability: AdminMediaArtifactData["previewAvailability"],
  locale: AdminDashboardLocale,
): string {
  switch (availability) {
    case "available":
      return text(locale, "Available", "가능");
    case "pending_policy_check":
      return text(locale, "Pending policy check", "정책 확인 대기");
    case "unsupported_type":
      return text(locale, "Unsupported type", "지원되지 않는 형식");
    case "not_generated":
      return text(locale, "Not generated", "생성되지 않음");
    case "deleted":
      return text(locale, "Deleted", "삭제됨");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatMediaFallbackReasonLabel(
  fallbackReason: AdminMediaArtifactData["fallbackReason"],
  locale: AdminDashboardLocale,
): string {
  switch (fallbackReason) {
    case undefined:
      return "-";
    case "missing":
      return text(locale, "Missing", "누락");
    case "build_not_supported":
      return text(locale, "Build not supported", "빌드 미지원");
    case "capture_pending":
      return text(locale, "Capture pending", "수집 대기");
    case "upload_failed":
      return text(locale, "Upload failed", "업로드 실패");
    case "unsupported_type":
      return text(locale, "Unsupported type", "지원되지 않는 형식");
    case "pending_confirm":
      return text(locale, "Pending confirm", "확정 대기");
    case "cleanup_scheduled":
      return text(locale, "Cleanup scheduled", "정리 예정");
    case "deleted":
      return text(locale, "Deleted", "삭제됨");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "manual_resend_required":
      return text(locale, "Manual resend required", "수동 재전송 필요");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatMediaAuditScopeLabel(
  auditScope: AdminMediaArtifactData["auditScope"],
  locale: AdminDashboardLocale,
): string {
  switch (auditScope) {
    case "preview_only":
      return text(locale, "Preview only", "미리보기 전용");
    case "download_only":
      return text(locale, "Download only", "다운로드 전용");
    case "preview_and_download":
      return text(locale, "Preview and download", "미리보기 및 다운로드");
    case "metadata_only":
      return text(locale, "Metadata only", "메타데이터만");
  }
}

function formatSameDeviceContextLabel(
  sameDeviceContextAvailable: boolean,
  locale: AdminDashboardLocale,
): string {
  return sameDeviceContextAvailable
    ? text(locale, "Available", "가능")
    : text(locale, "Unavailable", "불가");
}

function formatMediaStorageStatusLabel(
  storageStatus: AdminMediaArtifactData["storageStatus"],
  locale: AdminDashboardLocale,
): string {
  switch (storageStatus) {
    case "available":
      return text(locale, "Available", "가능");
    case "pending_confirm":
      return text(locale, "Pending confirm", "확정 대기");
    case "soft_deleted":
      return text(locale, "Soft deleted", "소프트 삭제");
    case "cleanup_scheduled":
      return text(locale, "Cleanup scheduled", "정리 예정");
    case "deleted":
      return text(locale, "Deleted", "삭제됨");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatMediaLinkedEntityTypeLabel(
  linkedEntityType: AdminMediaArtifactData["linkedEntityType"],
  locale: AdminDashboardLocale,
): string {
  switch (linkedEntityType) {
    case "user":
      return text(locale, "User", "사용자");
    case "pet":
      return text(locale, "Pet", "반려동물");
    case "record":
      return text(locale, "Record", "기록");
    case "receipt":
      return text(locale, "Receipt", "영수증");
    case "file":
      return text(locale, "File", "파일");
    case "community_post":
      return text(locale, "Community post", "커뮤니티 게시물");
    case "unknown":
      return text(locale, "Unknown", "미확인");
    case undefined:
      return "-";
  }
}

type MediaArchiveStateKey =
  | "original_ready"
  | "preview_only"
  | "retention_expired"
  | "archive_removed"
  | "policy_blocked"
  | "binary_unavailable";

function pickMediaArchiveStateKey(
  artifact: Pick<
    AdminMediaArtifactData,
    "previewAvailability" | "downloadAvailability" | "storageStatus" | "fallbackReason"
  >,
): MediaArchiveStateKey {
  if (artifact.downloadAvailability === "available") {
    return "original_ready";
  }

  if (artifact.previewAvailability === "available") {
    return "preview_only";
  }

  if (
    artifact.previewAvailability === "retention_expired" ||
    artifact.downloadAvailability === "retention_expired" ||
    artifact.storageStatus === "retention_expired" ||
    artifact.fallbackReason === "retention_expired"
  ) {
    return "retention_expired";
  }

  if (
    artifact.previewAvailability === "deleted" ||
    artifact.downloadAvailability === "deleted" ||
    artifact.storageStatus === "deleted" ||
    artifact.storageStatus === "soft_deleted" ||
    artifact.storageStatus === "cleanup_scheduled"
  ) {
    return "archive_removed";
  }

  if (
    artifact.previewAvailability === "access_denied" ||
    artifact.downloadAvailability === "access_denied" ||
    artifact.fallbackReason === "access_denied"
  ) {
    return "policy_blocked";
  }

  return "binary_unavailable";
}

function formatMediaArchiveStateLabel(
  archiveStateKey: MediaArchiveStateKey,
  locale: AdminDashboardLocale,
): string {
  switch (archiveStateKey) {
    case "original_ready":
      return text(locale, "Original archive ready", "원본 archive 준비됨");
    case "preview_only":
      return text(locale, "Preview only archive", "미리보기 전용 archive");
    case "retention_expired":
      return text(locale, "Archive retention expired", "archive 보관 기간 만료");
    case "archive_removed":
      return text(locale, "Archive removed", "archive 제거됨");
    case "policy_blocked":
      return text(locale, "Archive blocked by policy", "정책으로 archive 차단");
    case "binary_unavailable":
      return text(locale, "Archive binary unavailable", "archive binary 사용 불가");
  }
}

function pickMediaArchiveStateHint(
  archiveStateKey: MediaArchiveStateKey,
  locale: AdminDashboardLocale,
): string {
  switch (archiveStateKey) {
    case "original_ready":
      return text(
        locale,
        "The original binary is still retained. Keep this artifact pinned when you compare the same trace or incident workspace.",
        "원본 binary가 아직 유지됩니다. 같은 추적이나 incident 작업면과 비교할 때 이 artifact를 계속 고정하세요.",
      );
    case "preview_only":
      return text(
        locale,
        "Preview is available but original export is not ready. Use this row as review evidence first, then escalate through trace before promising binary handoff.",
        "미리보기는 가능하지만 원본 export는 아직 준비되지 않았습니다. 먼저 이 row를 검토 증거로 사용하고, binary handoff를 약속하기 전에 추적으로 올려 확인하세요.",
      );
    case "retention_expired":
      return text(
        locale,
        "The original archive aged out of retention. Preserve the trace and fallback context before treating this as user-data loss.",
        "원본 archive가 보관 기간을 지나 만료됐습니다. 이를 사용자 데이터 손실로 보기 전에 추적과 fallback 문맥을 먼저 보존하세요.",
      );
    case "archive_removed":
      return text(
        locale,
        "The original archive has already been removed or cleanup-scheduled. Compare the lifecycle trace before treating the incident as unexplained.",
        "원본 archive가 이미 제거됐거나 cleanup 예정입니다. incident를 미설명 상태로 두기 전에 라이프사이클 추적을 먼저 비교하세요.",
      );
    case "policy_blocked":
      return text(
        locale,
        "Policy or role boundaries block archive delivery. Confirm the operator boundary before retrying preview or download.",
        "정책 또는 역할 경계가 archive 전달을 차단하고 있습니다. 미리보기나 다운로드를 재시도하기 전에 운영자 경계를 확인하세요.",
      );
    case "binary_unavailable":
      return text(
        locale,
        "No original binary can be issued from the current path. Read the fallback and trace before assuming the artifact is gone.",
        "현재 경로에서는 원본 binary를 발급할 수 없습니다. artifact가 사라졌다고 단정하기 전에 fallback과 추적을 먼저 읽으세요.",
      );
  }
}

type MediaIncidentAttachmentStateKey =
  | "attached_same_device"
  | "contextual_same_device"
  | "trace_attached"
  | "context_trace_only"
  | "not_attached";

function pickMediaIncidentAttachmentStateKey(
  artifact: Pick<AdminMediaArtifactData, "relationType" | "sameDeviceContextAvailable" | "latestTraceKey">,
): MediaIncidentAttachmentStateKey {
  if (artifact.sameDeviceContextAvailable && artifact.relationType === "direct") {
    return "attached_same_device";
  }

  if (artifact.sameDeviceContextAvailable) {
    return "contextual_same_device";
  }

  if ((artifact.latestTraceKey?.requestId || artifact.latestTraceKey?.correlationId) && artifact.relationType === "direct") {
    return "trace_attached";
  }

  if (artifact.latestTraceKey?.requestId || artifact.latestTraceKey?.correlationId) {
    return "context_trace_only";
  }

  return "not_attached";
}

function formatMediaIncidentAttachmentStateLabel(
  incidentAttachmentStateKey: MediaIncidentAttachmentStateKey,
  locale: AdminDashboardLocale,
): string {
  switch (incidentAttachmentStateKey) {
    case "attached_same_device":
      return text(locale, "Attached to same-device incident", "동일 기기 incident에 직접 연결");
    case "contextual_same_device":
      return text(locale, "Contextual same-device candidate", "문맥 기반 동일 기기 후보");
    case "trace_attached":
      return text(locale, "Trace-attached incident evidence", "추적으로 연결된 incident 증거");
    case "context_trace_only":
      return text(locale, "Context-only trace attachment", "문맥 기반 추적 연결");
    case "not_attached":
      return text(locale, "No incident attachment yet", "아직 incident 연결 없음");
  }
}

function pickMediaIncidentAttachmentHint(
  incidentAttachmentStateKey: MediaIncidentAttachmentStateKey,
  locale: AdminDashboardLocale,
): string {
  switch (incidentAttachmentStateKey) {
    case "attached_same_device":
      return text(
        locale,
        "This artifact is already aligned with same-device incident context. Compare it against the inline log excerpt before leaving this route.",
        "이 artifact는 이미 same-device incident 문맥과 정렬되어 있습니다. 이 화면을 떠나기 전에 inline 로그 발췌와 먼저 비교하세요.",
      );
    case "contextual_same_device":
      return text(
        locale,
        "A same-device incident can still be compared, but the attachment is contextual rather than direct. Keep the relation label visible when handing this off.",
        "same-device incident와 비교는 가능하지만 직접 연결이 아니라 문맥 연결입니다. handoff할 때 관계 라벨을 그대로 유지하세요.",
      );
    case "trace_attached":
      return text(
        locale,
        "This artifact stays incident-attached through request or correlation trace even before same-device evidence is pinned.",
        "same-device 증거를 아직 고정하지 않았더라도 이 artifact는 요청 또는 상관관계 추적으로 incident에 연결됩니다.",
      );
    case "context_trace_only":
      return text(
        locale,
        "Only contextual trace linkage is available. Compare the representative row before treating this artifact as the primary incident anchor.",
        "현재는 문맥 기반 추적 연결만 가능합니다. 이 artifact를 incident의 주 기준점으로 쓰기 전에 대표 row와 먼저 비교하세요.",
      );
    case "not_attached":
      return text(
        locale,
        "No incident attachment is visible yet. Start from the source trace or lifecycle path before promising binary replay.",
        "아직 보이는 incident 연결이 없습니다. binary replay를 약속하기 전에 source 추적 또는 라이프사이클 경로부터 시작하세요.",
      );
  }
}

function formatIncidentLogCaptureTriggerLabel(
  captureTrigger: AdminIncidentLogArtifactData["captureTrigger"],
  locale: AdminDashboardLocale,
): string {
  switch (captureTrigger) {
    case "failed_request":
      return text(locale, "Failed request", "실패 요청");
    case "fatal_boundary":
      return text(locale, "Fatal boundary", "치명 경계");
    case "operator_resend":
      return text(locale, "Operator resend", "운영자 재전송");
    case "upload_recovery":
      return text(locale, "Upload recovery", "업로드 복구");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogCaptureWindowLabel(
  captureWindow: AdminIncidentLogArtifactData["captureWindow"],
  locale: AdminDashboardLocale,
): string {
  switch (captureWindow) {
    case "pre_30s_post_30s":
      return text(locale, "Pre 30s / Post 30s", "이전 30초 / 이후 30초");
    case "pre_2m_post_2m":
      return text(locale, "Pre 2m / Post 2m", "이전 2분 / 이후 2분");
    case "session_excerpt":
      return text(locale, "Session excerpt", "세션 발췌");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogCapturedAtBucketLabel(
  capturedAtBucket: AdminIncidentLogArtifactData["capturedAtBucket"],
  locale: AdminDashboardLocale,
): string {
  switch (capturedAtBucket) {
    case "lt_15m":
      return text(locale, "Under 15m", "15분 미만");
    case "15m_to_1h":
      return text(locale, "15m to 1h", "15분~1시간");
    case "1h_to_24h":
      return text(locale, "1h to 24h", "1시간~24시간");
    case "24h_plus":
      return text(locale, "24h+", "24시간 이상");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogSupportabilityStateLabel(
  supportabilityState: AdminIncidentLogArtifactData["supportabilityState"],
  locale: AdminDashboardLocale,
): string {
  switch (supportabilityState) {
    case "supported":
      return text(locale, "Supported", "지원됨");
    case "build_not_supported":
      return text(locale, "Build not supported", "빌드 미지원");
    case "capture_disabled":
      return text(locale, "Capture disabled", "수집 비활성화");
    case "capture_pending":
      return text(locale, "Capture pending", "수집 대기");
    case "capture_pending_upload":
      return text(locale, "Capture pending upload", "수집 후 업로드 대기");
    case "upload_in_progress":
      return text(locale, "Upload in progress", "업로드 진행 중");
    case "upload_failed":
      return text(locale, "Upload failed", "업로드 실패");
    case "superseded":
      return text(locale, "Superseded", "대체됨");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "manual_resend_required":
      return text(locale, "Manual resend required", "수동 재전송 필요");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogPreviewReadinessLabel(
  previewReadiness: AdminIncidentLogArtifactData["previewReadiness"],
  locale: AdminDashboardLocale,
): string {
  switch (previewReadiness) {
    case "ready":
      return text(locale, "Ready", "준비");
    case "processing":
      return text(locale, "Processing", "처리 중");
    case "unsupported":
      return text(locale, "Unsupported", "미지원");
    case "empty_excerpt":
      return text(locale, "Empty excerpt", "발췌 없음");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogDownloadReadinessLabel(
  downloadReadiness: AdminIncidentLogArtifactData["downloadReadiness"],
  locale: AdminDashboardLocale,
): string {
  switch (downloadReadiness) {
    case "ready":
      return text(locale, "Ready", "준비");
    case "processing":
      return text(locale, "Processing", "처리 중");
    case "missing_manifest":
      return text(locale, "Missing manifest", "manifest 누락");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogHandoffReadinessLabel(
  handoffReadiness: AdminIncidentLogArtifactData["handoffReadiness"],
  locale: AdminDashboardLocale,
): string {
  switch (handoffReadiness) {
    case "ready":
      return text(locale, "Ready", "준비");
    case "needs_preview_only":
      return text(locale, "Preview only", "미리보기만 가능");
    case "needs_resend":
      return text(locale, "Needs resend", "재전송 필요");
    case "blocked_by_policy":
      return text(locale, "Blocked by policy", "정책 차단");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogExcerptLineCountBucketLabel(
  excerptLineCountBucket: AdminIncidentLogArtifactData["excerptLineCountBucket"],
  locale: AdminDashboardLocale,
): string {
  switch (excerptLineCountBucket) {
    case "0":
      return text(locale, "0 lines", "0줄");
    case "1_to_20":
      return text(locale, "1 to 20 lines", "1~20줄");
    case "21_to_100":
      return text(locale, "21 to 100 lines", "21~100줄");
    case "101_plus":
      return text(locale, "101+ lines", "101줄 이상");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogBundleHashPresenceLabel(
  bundleHashPresence: AdminIncidentLogArtifactData["bundleHashPresence"],
  locale: AdminDashboardLocale,
): string {
  switch (bundleHashPresence) {
    case "present":
      return text(locale, "Present", "있음");
    case "missing":
      return text(locale, "Missing", "누락");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function pickIncidentLogInvestigationHint(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  if (artifact.supportabilityState === "superseded") {
    return text(
      locale,
      "This same-device artifact was superseded. Reopen the newest capture first so excerpt, bundle, and trace review all stay on the current artifact identity.",
      "이 same-device artifact는 이미 대체되었습니다. excerpt, bundle, trace 검토가 모두 최신 artifact identity를 가리키도록 먼저 최신 수집본을 다시 여세요.",
    );
  }

  if (
    artifact.supportabilityState === "capture_pending" ||
    artifact.supportabilityState === "capture_pending_upload" ||
    artifact.supportabilityState === "upload_in_progress" ||
    artifact.downloadReadiness === "processing"
  ) {
    return text(
      locale,
      "Producer/upload is still in flight. Wait for the same artifact to finish upload before treating the excerpt or bundle path as canonical evidence.",
      "producer/upload가 아직 진행 중입니다. excerpt나 bundle 경로를 canonical evidence로 보기 전에 같은 artifact의 업로드 완료를 먼저 기다리세요.",
    );
  }

  if (
    artifact.supportabilityState === "upload_failed" ||
    artifact.supportabilityState === "manual_resend_required" ||
    artifact.handoffReadiness === "needs_resend"
  ) {
    return text(
      locale,
      "The current artifact did not finish a durable upload path. Follow the resend path and confirm the replacement artifact keeps the same device header before review.",
      "현재 artifact는 durable upload 경로를 끝까지 완료하지 못했습니다. 재전송 경로를 따르고 교체 artifact가 같은 device header를 유지하는지 확인한 뒤 검토하세요.",
    );
  }

  if (artifact.previewReadiness === "ready" && artifact.downloadReadiness === "ready") {
    return text(
      locale,
      "Start with the inline excerpt, then download the same bundle artifact and compare it with the linked request trace.",
      "먼저 inline excerpt를 읽고, 같은 bundle artifact를 내려받아 연결된 요청 추적과 비교하세요.",
    );
  }

  if (artifact.downloadReadiness === "ready") {
    return text(
      locale,
      "Preview is not ready yet. Keep the same trace anchor, then move to the downloadable bundle first.",
      "아직 preview가 준비되지 않았습니다. 같은 추적 기준점을 유지한 채 다운로드 가능한 bundle부터 확인하세요.",
    );
  }

  return text(
    locale,
    "The same-device log artifact is not fully reviewable yet. Read the supportability and readiness states before falling back to request trace only.",
    "same-device log artifact를 아직 완전히 검토할 수 없습니다. request trace만 보조로 남기기 전에 지원 상태와 readiness를 먼저 읽으세요.",
  );
}

function pickIncidentLogPrimaryFailureSignal(
  artifact: AdminIncidentLogArtifactData,
): string | undefined {
  const previewLines = artifact.previewExcerpt?.lines ?? [];
  for (let index = previewLines.length - 1; index >= 0; index -= 1) {
    const line = previewLines[index];
    if (!line) {
      continue;
    }
    if (line.errorCode) {
      return line.errorCode;
    }
    if (line.level === "error" && line.messageKey) {
      return line.messageKey;
    }
  }

  return (
    artifact.latestTraceKey?.errorCode ??
    artifact.previewExcerpt?.topCategory ??
    artifact.previewExcerpt?.headline
  );
}

function pickIncidentLogDiagnosisConfidenceLabel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  const hasTraceAnchor = Boolean(
    artifact.latestTraceKey?.requestId ?? artifact.latestTraceKey?.correlationId,
  );
  const hasPrimarySignal = Boolean(pickIncidentLogPrimaryFailureSignal(artifact));

  if (artifact.previewReadiness === "ready" && artifact.downloadReadiness === "ready" && hasTraceAnchor && hasPrimarySignal) {
    return text(locale, "Excerpt + trace aligned", "excerpt + trace 정렬");
  }

  if (artifact.previewReadiness === "ready" && hasPrimarySignal) {
    return text(locale, "Excerpt anchored", "excerpt 기준 확보");
  }

  if (artifact.downloadReadiness === "ready") {
    return text(locale, "Bundle-first", "번들 우선");
  }

  return text(locale, "Supportability pending", "지원 상태 확인 필요");
}

function pickIncidentLogDiagnosisSummaryLabel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  const primarySignal = pickIncidentLogPrimaryFailureSignal(artifact);
  const topCategory = artifact.previewExcerpt?.topCategory;
  if (primarySignal && topCategory) {
    return text(
      locale,
      `Primary failing signal ${primarySignal} is grouped under ${topCategory}.`,
      `주 실패 신호 ${primarySignal}는 ${topCategory} 범주로 묶입니다.`,
    );
  }

  if (primarySignal) {
    return text(
      locale,
      `Primary failing signal is ${primarySignal}.`,
      `주 실패 신호는 ${primarySignal}입니다.`,
    );
  }

  return text(
    locale,
    "The current excerpt does not expose a deterministic failure token yet.",
    "현재 excerpt에는 확정 가능한 실패 토큰이 아직 드러나지 않습니다.",
  );
}

function pickIncidentLogRecommendedActionLabel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  const hasTraceAnchor = Boolean(
    artifact.latestTraceKey?.requestId ?? artifact.latestTraceKey?.correlationId,
  );

  if (artifact.supportabilityState === "superseded") {
    return text(
      locale,
      "Open the newest same-device artifact first. Do not hand off or compare a superseded bundle as the canonical incident record.",
      "최신 same-device artifact를 먼저 여세요. 대체된 bundle을 canonical incident 기록처럼 handoff하거나 비교하면 안 됩니다.",
    );
  }

  if (
    artifact.supportabilityState === "capture_pending" ||
    artifact.supportabilityState === "capture_pending_upload" ||
    artifact.supportabilityState === "upload_in_progress" ||
    artifact.downloadReadiness === "processing"
  ) {
    return text(
      locale,
      "Wait for producer/upload completion before using this artifact as storage-backed incident evidence.",
      "이 artifact를 storage-backed incident evidence로 쓰기 전에 producer/upload 완료를 먼저 기다리세요.",
    );
  }

  if (
    artifact.supportabilityState === "upload_failed" ||
    artifact.supportabilityState === "manual_resend_required" ||
    artifact.handoffReadiness === "needs_resend"
  ) {
    return text(
      locale,
      "Follow the resend path and verify the replacement artifact keeps the same device header before root-cause review.",
      "재전송 경로를 먼저 따르고, 교체 artifact가 같은 device header를 유지하는지 확인한 뒤 근본 원인을 검토하세요.",
    );
  }

  if (artifact.previewReadiness === "ready" && artifact.downloadReadiness === "ready" && hasTraceAnchor) {
    return text(
      locale,
      "Confirm the failing message in the excerpt, then download the matching bundle and reopen the linked failed flow.",
      "excerpt에서 실패 메시지를 확인한 뒤, 같은 bundle을 내려받고 연결된 오류 흐름을 다시 여세요.",
    );
  }

  if (artifact.downloadReadiness === "ready") {
    return text(
      locale,
      "Issue the bundle download first and compare manifest/header parity before judging root cause.",
      "근본 원인을 단정하기 전에 bundle 다운로드를 먼저 발급하고 manifest/header parity를 비교하세요.",
    );
  }

  return text(
    locale,
    "Read supportability and handoff readiness before falling back to trace-only investigation.",
    "trace-only 조사로 돌아가기 전에 supportability와 handoff readiness를 먼저 읽으세요.",
  );
}

function pickIncidentLogDeliveryPathStateLabel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  if (
    artifact.supportabilityState === "supported" &&
    artifact.downloadReadiness === "ready" &&
    artifact.handoffReadiness === "ready" &&
    artifact.bundleHashPresence === "present" &&
    artifact.manifestVersion
  ) {
    return text(locale, "Producer-to-storage ready", "producer-to-storage 준비 완료");
  }

  if (
    artifact.supportabilityState === "capture_pending" ||
    artifact.supportabilityState === "capture_pending_upload" ||
    artifact.supportabilityState === "upload_in_progress" ||
    artifact.downloadReadiness === "processing"
  ) {
    return text(locale, "Capture/upload pending", "수집/업로드 진행 중");
  }

  if (
    artifact.supportabilityState === "upload_failed" ||
    artifact.supportabilityState === "manual_resend_required" ||
    artifact.handoffReadiness === "needs_resend"
  ) {
    return text(locale, "Upload failed or resend required", "업로드 실패 또는 재전송 필요");
  }

  if (
    artifact.downloadReadiness === "missing_manifest" ||
    artifact.bundleHashPresence === "missing"
  ) {
    return text(locale, "Manifest recovery needed", "manifest 복구 필요");
  }

  if (
    artifact.supportabilityState === "retention_expired" ||
    artifact.downloadReadiness === "retention_expired" ||
    artifact.previewReadiness === "retention_expired"
  ) {
    return text(locale, "Retention expired", "보관 기간 만료");
  }

  if (
    artifact.supportabilityState === "access_denied" ||
    artifact.downloadReadiness === "access_denied" ||
    artifact.previewReadiness === "access_denied" ||
    artifact.handoffReadiness === "blocked_by_policy"
  ) {
    return text(locale, "Policy blocked", "정책 차단");
  }

  return text(locale, "Supportability review required", "지원 상태 추가 확인 필요");
}

function pickIncidentLogProducerPathNextActionLabel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  if (
    artifact.supportabilityState === "supported" &&
    artifact.downloadReadiness === "ready" &&
    artifact.handoffReadiness === "ready" &&
    artifact.bundleHashPresence === "present" &&
    artifact.manifestVersion
  ) {
    return text(
      locale,
      "Use the inline excerpt as the first check, then compare the downloaded bundle manifest against the same artifact identity.",
      "inline excerpt를 먼저 읽고, 내려받은 bundle manifest를 같은 artifact identity와 비교하세요.",
    );
  }

  if (
    artifact.supportabilityState === "capture_pending" ||
    artifact.supportabilityState === "capture_pending_upload" ||
    artifact.supportabilityState === "upload_in_progress" ||
    artifact.downloadReadiness === "processing"
  ) {
    return text(
      locale,
      "Wait for producer/upload completion before treating this artifact as storage-backed evidence.",
      "이 artifact를 storage-backed evidence로 보기 전에 producer/upload 완료를 먼저 기다리세요.",
    );
  }

  if (artifact.supportabilityState === "superseded") {
    return text(
      locale,
      "Open the newest same-device artifact before handoff. This bundle was superseded and should not be treated as canonical evidence.",
      "handoff 전에 최신 same-device artifact를 먼저 여세요. 이 bundle은 대체되어 canonical evidence로 간주하면 안 됩니다.",
    );
  }

  if (
    artifact.supportabilityState === "upload_failed" ||
    artifact.supportabilityState === "manual_resend_required" ||
    artifact.handoffReadiness === "needs_resend"
  ) {
    return text(
      locale,
      "Follow the resend path and confirm the next artifact keeps the same device header before handoff.",
      "재전송 경로를 먼저 따르고, 다음 artifact가 같은 device header를 유지하는지 확인한 뒤 handoff하세요.",
    );
  }

  if (
    artifact.downloadReadiness === "missing_manifest" ||
    artifact.bundleHashPresence === "missing"
  ) {
    return text(
      locale,
      "Recover the canonical manifest or bundle hash evidence before promising download parity.",
      "다운로드 parity를 약속하기 전에 canonical manifest 또는 bundle hash 근거를 먼저 복구하세요.",
    );
  }

  if (
    artifact.supportabilityState === "retention_expired" ||
    artifact.downloadReadiness === "retention_expired" ||
    artifact.previewReadiness === "retention_expired"
  ) {
    return text(
      locale,
      "Retention has expired. Reopen the failed flow and determine whether a fresh same-device capture is still possible.",
      "보관 기간이 만료되었습니다. 오류 흐름을 다시 열고 fresh same-device capture가 가능한지 먼저 판단하세요.",
    );
  }

  if (
    artifact.supportabilityState === "access_denied" ||
    artifact.downloadReadiness === "access_denied" ||
    artifact.previewReadiness === "access_denied" ||
    artifact.handoffReadiness === "blocked_by_policy"
  ) {
    return text(
      locale,
      "Resolve the policy boundary first before using this artifact as downloadable operator evidence.",
      "이 artifact를 다운로드 가능한 운영 증거로 쓰기 전에 정책 경계를 먼저 해결하세요.",
    );
  }

  return text(
    locale,
    "Read supportability, manifest, and handoff readiness together before escalating this artifact as canonical evidence.",
    "이 artifact를 canonical evidence로 승격하기 전에 supportability, manifest, handoff readiness를 함께 읽으세요.",
  );
}

function formatIncidentLogPreviewLineLevel(level: string): string {
  return level.toUpperCase();
}

function formatLatestTraceLabel(
  traceKey:
    | {
        requestId?: string;
        correlationId?: string;
      }
    | undefined,
): string {
  if (!traceKey) {
    return "-";
  }

  return `${toLabel(traceKey.requestId)} / ${toLabel(traceKey.correlationId)}`;
}

function formatAccountDeletionStatusSentence(
  status: AdminAccountDeletionStatus,
  locale: AdminDashboardLocale,
): string {
  return `${text(locale, "Status", "상태")} ${formatAdminAccountDeletionStatusLabel(status, locale)}`;
}

export type UserOperationsViewModel = {
  generatedAtLabel: string;
  requestId: string;
  freshness: AdminProjectionFreshness;
  internalUserId: string;
  userIdHash: string;
  createdAtLabel: string;
  accountIdentityLabel: string;
  providerIdentityLabel: string;
  linkedProviderCountLabel: string;
  latestDeviceInstallIdLabel: string;
  latestPlatformLabel: string;
  latestRuntimeSummaryLabel: string;
  latestRuntimeCoverageLabel: string | null;
  lastLoginAtLabel: string;
  termsConsentAtLabel: string;
  accountDeletionStatusLabel: string;
  latestReleaseLabel: string;
  latestAppVersionLabel: string;
  detailRoute: `/admin/users/${string}`;
  riskSummary: AdminUserDataRiskSummary;
  triageCards: Array<{
    key: "account" | "risk" | "latestFailure" | "latestSave" | "footprint";
    title: string;
    tone: "default" | "attention" | "warning" | "critical";
    headline: string;
    supporting: string;
    primaryActionLabel?: string;
    primaryActionTo?: string;
    secondaryActionLabel?: string;
    secondaryActionTo?: string;
  }>;
  scenarioTimeline: Array<{
    key:
      | "auth"
      | "pet"
      | "social"
      | "chat"
      | "community"
      | "daily"
      | "health"
      | "receipt"
      | "medication"
      | "ride"
      | "files"
      | "accountDeletion";
    title: string;
    tone: "default" | "attention" | "warning" | "critical";
    headline: string;
    latestActivityLabel: string;
    footprintLabel: string;
    riskLabel: string;
    summary: string;
    primaryActionLabel: string;
    primaryActionTo: string;
    secondaryActionLabel?: string;
    secondaryActionTo?: string;
    latestFailure?: {
      feature: string;
      useCase: string;
      errorCode: string;
      occurredAtLabel: string;
      requestId?: string;
      correlationId?: string;
      drilldownTo?: string;
    };
    latestSuccessfulSave?: {
      event: string;
      occurredAtLabel: string;
      signalKey?: AdminAppSignalKey;
      stage?: AdminAppSignalStage;
      requestId?: string;
      correlationId?: string;
      recordType?: AdminRecordType;
      recordId?: string;
      receiptId?: string;
    };
  }>;
  counts: Array<{
    key:
      | "pets"
      | "records"
      | "dailyRecords"
      | "healthRecords"
      | "receipts"
      | "files"
      | "aiJobs";
    label: string;
    valueLabel: string;
  }>;
  latestFailure?: {
    feature: string;
    useCase: string;
    errorCode: string;
    occurredAtLabel: string;
    requestId?: string;
    correlationId?: string;
    drilldownTo?: string;
  };
  latestSuccessfulSave?: {
    event: string;
    occurredAtLabel: string;
    signalKey?: AdminAppSignalKey;
    stage?: AdminAppSignalStage;
    requestId?: string;
    correlationId?: string;
    recordType?: AdminRecordType;
    recordId?: string;
    receiptId?: string;
  };
  recentFailures: Array<{
    id: string;
    feature: string;
    useCase: string;
    errorCode: string;
    occurredAtLabel: string;
    contextLabels: string[];
    requestId?: string;
    correlationId?: string;
    drilldownTo?: string;
  }>;
  recentSuccessfulSaves: Array<{
    id: string;
    event: string;
    occurredAtLabel: string;
    signalKey?: AdminAppSignalKey;
    stage?: AdminAppSignalStage;
    requestId?: string;
    correlationId?: string;
    recordType?: AdminRecordType;
    recordId?: string;
    receiptId?: string;
  }>;
};

export type UserDataDetailViewModel = {
  generatedAtLabel: string;
  requestId: string;
  freshness: AdminProjectionFreshness;
  internalUserId: string;
  userIdHash: string;
  accountIdentityLabel: string;
  providerIdentityLabel: string;
  linkedProviderCountLabel: string;
  latestReleaseLabel: string;
  latestAppVersionLabel: string;
  counts: Array<{
    key:
      | "pets"
      | "records"
      | "dailyRecords"
      | "healthRecords"
      | "receipts"
      | "files"
      | "aiJobs";
    label: string;
    valueLabel: string;
  }>;
  riskSummary: AdminUserDataRiskSummary;
  accountDeletionSummary: {
    severity: "none" | "attention" | "warning" | "critical";
    headline: string;
    summary: string;
    statusLabel: string;
    requestedAtLabel: string;
    completedAtLabel: string;
    latestFailure?: {
      feature: string;
      useCase: string;
      errorCode: string;
      occurredAtLabel: string;
      requestId?: string;
      correlationId?: string;
      drilldownTo?: string;
    };
    primaryActionLabel?: string;
    primaryActionTo?: string;
    jobsDrilldownTo: string;
    investigationHint: string;
  };
  dailyRecordTriage: {
    riskLevel: "none" | "attention" | "warning" | "critical";
    summary: string;
    dailyRecordCountLabel: string;
    recentFailureCountLabel: string;
    recentSuccessfulSaveCountLabel: string;
    latestFailure?: {
      feature: string;
      useCase: string;
      errorCode: string;
      occurredAtLabel: string;
      requestId?: string;
      correlationId?: string;
      drilldownTo?: string;
    };
    latestSuccessfulSave?: {
      event: string;
      occurredAtLabel: string;
      signalKey?: AdminAppSignalKey;
      stage?: AdminAppSignalStage;
      requestId?: string;
      correlationId?: string;
      recordType?: AdminRecordType;
      recordId?: string;
      receiptId?: string;
    };
  };
  uploadDiagnoses: Array<{
    scenarioKey: UploadDiagnosisScenarioKey;
    title: string;
    overallStatus: UploadDiagnosisStatusValue;
    summary: string;
    sourceAnchorLabel: string;
    recognitionSignalLabel: string;
    failedModuleLabel: string;
    latestTraceLabel: string;
    latestSaveLabel: string;
    operatorHint: string;
    steps: Array<{
      key: string;
      title: string;
      status: UploadDiagnosisStatusValue;
      summary: string;
      detail: string;
      metaRows: UploadDiagnosisEnrichmentRowViewModel[];
      fileIdLabel: string;
      recordIdLabel: string;
      receiptIdLabel: string;
      jobIdLabel: string;
      requestIdLabel: string;
      correlationIdLabel: string;
      errorCodeLabel: string;
      failedFlowDrilldownTo?: string;
      jobsDrilldownTo?: string;
    }>;
  }>;
  latestPetFailure?: {
    feature: string;
    useCase: string;
    errorCode: string;
    occurredAtLabel: string;
    requestId?: string;
    correlationId?: string;
    drilldownTo?: string;
  };
  latestFailure?: {
    feature: string;
    useCase: string;
    errorCode: string;
    occurredAtLabel: string;
    requestId?: string;
    correlationId?: string;
    drilldownTo?: string;
  };
  latestSuccessfulSave?: {
    event: string;
    occurredAtLabel: string;
    signalKey?: AdminAppSignalKey;
    stage?: AdminAppSignalStage;
    requestId?: string;
    correlationId?: string;
    recordType?: AdminRecordType;
    recordId?: string;
    receiptId?: string;
  };
  recentFailures: Array<{
    id: string;
    feature: string;
    useCase: string;
    errorCode: string;
    occurredAtLabel: string;
    contextLabels: string[];
    requestId?: string;
    correlationId?: string;
    drilldownTo?: string;
  }>;
  recentSuccessfulSaves: Array<{
    id: string;
    event: string;
    occurredAtLabel: string;
    signalKey?: AdminAppSignalKey;
    stage?: AdminAppSignalStage;
    requestId?: string;
    correlationId?: string;
    recordType?: AdminRecordType;
    recordId?: string;
    receiptId?: string;
  }>;
  pets: Array<{
    petId: string;
    petIdHash: string;
    createdAtLabel: string;
    speciesLabel: string;
    profileStatusLabel: string;
    photoFileLinkStatusLabel: string;
    recordCountLabel: string;
    deletedAtLabel: string;
  }>;
  records: Array<{
    recordId: string;
    typeLabel: string;
    titleLabel: string;
    recordedAtLabel: string;
    createdAtLabel: string;
    updatedAtLabel: string;
    deletedAtLabel: string;
    linkedFileCountLabel: string;
    latestAiJobStatusLabel: string;
    dataCompletenessLabel: string;
  }>;
  receipts: Array<{
    receiptId: string;
    purchasedAtLabel: string;
    amountBucketLabel: string;
    categoryLabel: string;
    createdAtLabel: string;
    updatedAtLabel: string;
    deletedAtLabel: string;
    extractionStatusLabel: string;
  }>;
  files: Array<{
    fileId: string;
    purposeLabel: string;
    statusLabel: string;
    contentTypeLabel: string;
    sizeBucketLabel: string;
    linkedEntityLabel: string;
    createdAtLabel: string;
    confirmedAtLabel: string;
    expiresAtLabel: string;
    deletedAtLabel: string;
    storageDeletedAtLabel: string;
    latestLifecycleRequestIdLabel: string;
    upstreamRequestIdLabel: string;
    cleanupScheduledAgeBucketLabel: string;
    cleanupAttemptCountBucketLabel: string;
    orphanClassificationLabel: string;
    storageDeleteOutcomeCategoryLabel: string;
    linkedEntityKindLabel: string;
    lifecycleContextSummaryLabel: string;
    lifecycleFailedFlowDrilldownTo?: string;
    upstreamFailedFlowDrilldownTo?: string;
    jobsDrilldownTo?: string;
    relatedMediaArtifactId?: string;
    relatedMediaScenarioKey?: Exclude<MediaScenarioKey, "all">;
    investigationHint: string;
  }>;
  representativePhoto?: {
    artifactId: string;
    sourceScenarioKey: Exclude<MediaScenarioKey, "all">;
    sourceScenarioLabel: string;
    linkedEntityTypeLabel: string;
    relationTypeKey: AdminMediaArtifactData["relationType"];
    relationTypeLabel: string;
    artifactRoleLabel: string;
    contentTypeBucketLabel: string;
    sizeBucketLabel: string;
    uploadedAtLabel: string;
    storageStatusLabel: string;
    previewAvailability: string;
    previewAvailabilityLabel: string;
    downloadAvailability: string;
    downloadAvailabilityLabel: string;
    binaryReviewPathLabel: string;
    binaryReviewHint: string;
    latestRequestId?: string;
    latestRequestIdLabel: string;
    latestCorrelationId?: string;
    latestCorrelationIdLabel: string;
    latestTraceLabel: string;
    requestTraceDrilldownTo?: string;
    fallbackReasonLabel: string;
    archiveStateLabel: string;
    archiveStateHint: string;
    incidentAttachmentStateLabel: string;
    incidentAttachmentHint: string;
  };
  mediaArtifacts: Array<{
    artifactId: string;
    sourceScenarioKey: Exclude<MediaScenarioKey, "all">;
    sourceScenarioLabel: string;
    linkedEntityTypeLabel: string;
    linkedEntityLabel: string;
    relationTypeKey: AdminMediaArtifactData["relationType"];
    relationTypeLabel: string;
    artifactRoleLabel: string;
    mediaKindLabel: string;
    contentTypeBucketLabel: string;
    sizeBucketLabel: string;
    uploadedAtLabel: string;
    confirmedAtLabel: string;
    storageStatusLabel: string;
    previewAvailability: string;
    previewAvailabilityLabel: string;
    downloadAvailability: string;
    downloadAvailabilityLabel: string;
    binaryReviewPathLabel: string;
    binaryReviewHint: string;
    latestRequestId?: string;
    latestRequestIdLabel: string;
    latestCorrelationId?: string;
    latestCorrelationIdLabel: string;
    latestTraceLabel: string;
    fallbackReasonLabel: string;
    auditScopeLabel: string;
    sameDeviceContextLabel: string;
    scenarioArtifactCountLabel: string;
    scenarioSourceConnectionLabel: string;
    scenarioCoverageStateLabel: string;
    scenarioDownloadOnlyCountLabel: string;
    scenarioBlockedCountLabel: string;
    scenarioFallbackSummaryLabel: string;
    scenarioLatestRequestIdLabel: string;
    scenarioLatestTraceLabel: string;
    scenarioRepresentativeArtifactIdLabel: string;
    scenarioLatestUploadAtLabel: string;
    matchesScenarioRepresentative: boolean;
    matchesScenarioLatestUpload: boolean;
    scenarioCompareHint: string;
    archiveStateLabel: string;
    archiveStateHint: string;
    incidentAttachmentStateLabel: string;
    incidentAttachmentHint: string;
    requestTraceDrilldownTo?: string;
    linkedFailedFlowDrilldownTo?: string;
    linkedLifecycleDrilldownTo?: string;
  }>;
  mediaScenarioPivots: Array<{
    scenarioKey: MediaScenarioKey;
    label: string;
    artifactCount: number;
    artifactCountLabel: string;
    representativeArtifactIdLabel: string;
    latestUploadAtLabel: string;
    downloadOnlyCountLabel: string;
    archiveReadyCountLabel: string;
    incidentAttachedCountLabel: string;
    blockedCountLabel: string;
    sourceConnectionLabel: string;
    coverageStateLabel: string;
    fallbackSummaryLabel: string;
    latestRequestIdLabel: string;
    latestTraceLabel: string;
    representativeIncluded: boolean;
    investigationHint: string;
  }>;
  mediaTimelineRows: Array<{
    artifactId: string;
    sourceScenarioKey: Exclude<MediaScenarioKey, "all">;
    sourceScenarioLabel: string;
    relationTypeLabel: string;
    artifactRoleLabel: string;
    uploadedAtLabel: string;
    previewAvailabilityLabel: string;
    downloadAvailabilityLabel: string;
    binaryReviewPathLabel: string;
    latestTraceLabel: string;
    isRepresentativeEntry: boolean;
    isLatestForScenario: boolean;
    requestTraceDrilldownTo?: string;
  }>;
  mediaCoverageLedger: Array<{
    scenarioKey: Exclude<MediaScenarioKey, "all">;
    label: string;
    artifactCount: number;
    artifactCountLabel: string;
    sourceConnectionKey: "connected" | "inventory_missing" | "rollout_gap";
    sourceConnectionLabel: string;
    coverageStateKey: "preview_available" | "download_only" | "metadata_only" | "missing";
    coverageStateLabel: string;
    previewReadyCountLabel: string;
    downloadReadyCountLabel: string;
    blockedCountLabel: string;
    fallbackSummaryLabel: string;
    latestRequestIdLabel: string;
    latestTraceLabel: string;
    representativeIncluded: boolean;
    investigationHint: string;
  }>;
  missingPhotoSourceDisclosures: Array<{
    scenarioKey: Exclude<MediaScenarioKey, "all">;
    label: string;
    sourceConnectionLabel: string;
    statusLabel: string;
    reasonLabel: string;
    nextActionHint: string;
  }>;
  photoSourceSurfaceMatrix: Array<{
    scenarioKey: Exclude<MediaScenarioKey, "all">;
    label: string;
    targetLinkedEntityLabel: string;
    sourceConnectionLabel: string;
    baselineStatusLabel: string;
    baselineSummaryLabel: string;
    currentCoverageStateLabel: string;
    previewReadyCountLabel: string;
    downloadReadyCountLabel: string;
    blockedCountLabel: string;
    binaryReviewPathLabel: string;
    operatorHint: string;
  }>;
  incidentLogArtifacts: Array<{
    artifactId: string;
    deviceInstallId: string;
    releaseLabel: string;
    appVersionLabel: string;
    platformLabel: string;
    runtimeAppBuildNumberLabel: string;
    runtimeLocaleLabel: string;
    runtimeTimezoneOffsetBucketLabel: string;
    runtimeTimezoneNameBucketLabel: string;
    runtimeDeviceModelBucketLabel: string;
    runtimeDeviceModelNameBucketLabel: string;
    runtimeOsMajorBucketLabel: string;
    runtimeOsVersionMinorBucketLabel: string;
    runtimeNetworkTypeBucketLabel: string;
    runtimePhysicalDeviceBucketLabel: string;
    runtimeSummaryLabel: string;
    captureTriggerLabel: string;
    captureWindowLabel: string;
    capturedAtBucketLabel: string;
    capturedAtOperatorValueLabel: string;
    supportabilityStateLabel: string;
    excerptLineCountBucketLabel: string;
    bundleHashPresenceLabel: string;
    manifestVersionLabel: string;
    redactionVersionLabel: string;
    downloadReadiness: string;
    previewReadinessLabel: string;
    downloadReadinessLabel: string;
    handoffReadinessLabel: string;
    previewHeadlineLabel: string;
    previewTopCategoryLabel: string;
    primaryFailureSignalLabel: string;
    diagnosisSummaryLabel: string;
    diagnosisConfidenceLabel: string;
    recommendedActionLabel: string;
    deliveryPathStateLabel: string;
    producerPathNextActionLabel: string;
    latestRequestId?: string;
    latestRequestIdLabel: string;
    latestCorrelationId?: string;
    latestCorrelationIdLabel: string;
    latestTraceLabel: string;
    latestErrorCodeLabel: string;
    previewLines: Array<{
      timestampLabel: string;
      levelLabel: string;
      categoryLabel: string;
      messageKeyLabel: string;
      redactedContextLabel: string;
      requestIdLabel: string;
      correlationIdLabel: string;
      errorCodeLabel: string;
    }>;
    requestTraceDrilldownTo?: string;
    investigationHint: string;
  }>;
  aiJobs: Array<{
    jobId: string;
    jobTypeLabel: string;
    statusLabel: string;
    attemptCountLabel: string;
    lastErrorCodeLabel: string;
    providerLabel: string;
    queuedAtLabel: string;
    startedAtLabel: string;
    finishedAtLabel: string;
    durationLabel: string;
    linkedFileIdLabel: string;
    linkedRecordIdLabel: string;
  }>;
  account: Array<{
    key:
      | "accountEmail"
      | "displayName"
      | "emailVerified"
      | "providerIdentity"
      | "providerLinkedAt"
      | "linkedProviderCount"
      | "termsConsentAt"
      | "lastLoginAt"
      | "logoutLastSeenAt"
      | "deletionRequestedAt"
      | "deletionCompletedAt"
      | "deletionJobStatus";
    label: string;
    value: string;
  }>;
};

function toFreshness(meta: AdminQueryMeta): AdminProjectionFreshness {
  return Object.values(meta.sourceFreshness).includes("stale")
    ? "stale"
    : Object.values(meta.sourceFreshness).includes("partial") ||
        Object.values(meta.sourceFreshness).includes("unavailable")
      ? "partial"
      : "fresh";
}

function toLabel(value: string | null | undefined): string {
  return value ?? "-";
}

function toPetViewModel(pet: AdminUserDataDetail["pets"][number]) {
  return {
    petId: pet.petId,
    petIdHash: pet.petIdHash,
    createdAtLabel: formatAdminDate(pet.createdAt),
    speciesLabel: toLabel(pet.species),
    profileStatusLabel: pet.profileStatus,
    photoFileLinkStatusLabel: pet.photoFileLinkStatus,
    recordCountLabel: formatAdminCount(pet.recordCount),
    deletedAtLabel: formatAdminDate(pet.deletedAt),
  };
}

function toRecordViewModel(record: AdminRecordData) {
  return {
    recordId: record.recordId,
    typeLabel: record.type,
    titleLabel: toLabel(record.title),
    recordedAtLabel: formatAdminDate(record.recordedAt),
    createdAtLabel: formatAdminDate(record.createdAt),
    updatedAtLabel: formatAdminDate(record.updatedAt),
    deletedAtLabel: formatAdminDate(record.deletedAt),
    linkedFileCountLabel: formatAdminCount(record.linkedFileCount),
    latestAiJobStatusLabel: toLabel(record.latestAiJobStatus),
    dataCompletenessLabel: record.dataCompleteness,
  };
}

function toReceiptViewModel(receipt: AdminReceiptData) {
  return {
    receiptId: receipt.receiptId,
    purchasedAtLabel: formatAdminDate(receipt.purchasedAt),
    amountBucketLabel: receipt.amountBucket,
    categoryLabel: toLabel(receipt.categoryLabel),
    createdAtLabel: formatAdminDate(receipt.createdAt),
    updatedAtLabel: formatAdminDate(receipt.updatedAt),
    deletedAtLabel: formatAdminDate(receipt.deletedAt),
    extractionStatusLabel: toLabel(receipt.extractionStatus),
  };
}

function toAccountRows(
  account: AdminUserDataDetail["account"],
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["account"] {
  return [
    {
      key: "accountEmail",
      label: text(locale, "Account email", "계정 이메일"),
      value: toLabel(account.accountEmail),
    },
    {
      key: "displayName",
      label: text(locale, "Display name", "표시 이름"),
      value: toLabel(account.displayName),
    },
    {
      key: "emailVerified",
      label: text(locale, "Email verification", "이메일 인증"),
      value:
        account.emailVerified === true
          ? text(locale, "Verified", "인증됨")
          : account.emailVerified === false
            ? text(locale, "Unverified", "미인증")
            : "-",
    },
    {
      key: "providerIdentity",
      label: text(locale, "Primary provider", "기본 provider"),
      value: formatAdminProviderIdentityLabel(account, locale),
    },
    {
      key: "providerLinkedAt",
      label: text(locale, "Provider linked", "provider 연결"),
      value: formatAdminDate(account.providerLinkedAt),
    },
    {
      key: "linkedProviderCount",
      label: text(locale, "Linked providers", "연결된 provider 수"),
      value:
        account.linkedProviderCount == null
          ? "-"
          : text(locale, `${formatAdminCount(account.linkedProviderCount)} linked`, `${formatAdminCount(account.linkedProviderCount)}개 연결`),
    },
    {
      key: "termsConsentAt",
      label: text(locale, "Terms", "약관"),
      value: formatAdminDate(account.termsConsentAt),
    },
    {
      key: "lastLoginAt",
      label: text(locale, "Last login", "최근 로그인"),
      value: formatAdminDate(account.lastLoginAt),
    },
    {
      key: "logoutLastSeenAt",
      label: text(locale, "Logout last seen", "로그아웃 마지막 확인"),
      value: formatAdminDate(account.logoutLastSeenAt),
    },
    {
      key: "deletionRequestedAt",
      label: text(locale, "Deletion requested", "삭제 요청"),
      value: formatAdminDate(account.deletionRequestedAt),
    },
    {
      key: "deletionCompletedAt",
      label: text(locale, "Deletion completed", "삭제 완료"),
      value: formatAdminDate(account.deletionCompletedAt),
    },
    {
      key: "deletionJobStatus",
      label: text(locale, "Deletion job", "삭제 작업"),
      value: toLabel(account.deletionJobStatus),
    },
  ];
}

function toFileViewModel(
  file: AdminFileData,
  failureRequestIds: ReadonlySet<string>,
  relatedMediaArtifact:
    | {
        artifactId: string;
        sourceScenario: Exclude<MediaScenarioKey, "all">;
      }
    | undefined,
  locale: AdminDashboardLocale,
) {
  const latestLifecycleRequestIdLabel = toLabel(file.latestLifecycleRequestId);
  const upstreamRequestIdLabel = toLabel(file.upstreamRequestId);
  const lifecycleFailedFlowDrilldownTo =
    file.latestLifecycleRequestId && failureRequestIds.has(file.latestLifecycleRequestId)
    ? `/admin/failed-flows?${new URLSearchParams({ requestId: file.latestLifecycleRequestId }).toString()}`
    : undefined;
  const upstreamFailedFlowDrilldownTo =
    file.upstreamRequestId && failureRequestIds.has(file.upstreamRequestId)
    ? `/admin/failed-flows?${new URLSearchParams({ requestId: file.upstreamRequestId }).toString()}`
    : undefined;
  const jobsParams = new URLSearchParams();
  if (file.purpose === "health_source" || file.purpose === "health_record_source") {
    jobsParams.set("linkedFileId", file.fileId);
    if (file.linkedEntityType === "record" && file.linkedEntityId) {
      jobsParams.set("linkedRecordId", file.linkedEntityId);
    }
  }

  const cleanupScheduledAgeBucketLabel = formatFileCleanupScheduledAgeBucketLabel(
    file.cleanupScheduledAgeBucket,
    locale,
  );
  const cleanupAttemptCountBucketLabel = formatFileCleanupAttemptCountBucketLabel(
    file.cleanupAttemptCountBucket,
    locale,
  );
  const orphanClassificationLabel = formatFileOrphanClassificationLabel(
    file.orphanClassification,
    locale,
  );
  const storageDeleteOutcomeCategoryLabel = formatFileStorageDeleteOutcomeCategoryLabel(
    file.storageDeleteOutcomeCategory,
    locale,
  );
  const linkedEntityKindLabel = formatFileLinkedEntityKindLabel(file.linkedEntityKind, locale);
  const lifecycleContextSummaryLabel = [
    `${text(locale, "Scheduled", "예약")}: ${cleanupScheduledAgeBucketLabel}`,
    `${text(locale, "Attempts", "시도")}: ${cleanupAttemptCountBucketLabel}`,
    `${text(locale, "Orphan", "분류")}: ${orphanClassificationLabel}`,
    `${text(locale, "Delete result", "삭제 결과")}: ${storageDeleteOutcomeCategoryLabel}`,
    `${text(locale, "Entity kind", "엔터티 종류")}: ${linkedEntityKindLabel}`,
  ].join(" / ");

  return {
    fileId: file.fileId,
    purposeLabel: formatFilePurposeLabel(file.purpose, locale),
    statusLabel: formatFileStatusLabel(file.status, locale),
    contentTypeLabel: toLabel(file.contentType),
    sizeBucketLabel: file.sizeBucket,
    linkedEntityLabel:
      file.linkedEntityType && file.linkedEntityId
        ? `${file.linkedEntityType}:${file.linkedEntityId}`
        : "-",
    createdAtLabel: formatAdminDate(file.createdAt),
    confirmedAtLabel: formatAdminDate(file.confirmedAt),
    expiresAtLabel: formatAdminDate(file.expiresAt),
    deletedAtLabel: formatAdminDate(file.deletedAt),
    storageDeletedAtLabel: formatAdminDate(file.storageDeletedAt),
    latestLifecycleRequestIdLabel,
    upstreamRequestIdLabel,
    cleanupScheduledAgeBucketLabel,
    cleanupAttemptCountBucketLabel,
    orphanClassificationLabel,
    storageDeleteOutcomeCategoryLabel,
    linkedEntityKindLabel,
    lifecycleContextSummaryLabel,
    lifecycleFailedFlowDrilldownTo,
    upstreamFailedFlowDrilldownTo,
    jobsDrilldownTo: jobsParams.size > 0 ? `/admin/jobs?${jobsParams.toString()}` : undefined,
    relatedMediaArtifactId: relatedMediaArtifact?.artifactId,
    relatedMediaScenarioKey: relatedMediaArtifact?.sourceScenario,
    investigationHint:
      file.status === "deleted" || file.status === "cleanup_scheduled" || file.status === "cleanup_failed"
        ? text(locale, "Open lifecycle trace first, then compare storageDeletedAt and cleanup route stage.", "먼저 수명주기 조사를 열고 스토리지 삭제 시각과 정리 경로 단계를 비교하세요.")
        : file.status === "temp" || file.status === "expired"
          ? text(locale, "Check upload or confirm path first, then verify whether temp cleanup evidence exists.", "먼저 업로드 또는 확정 경로를 확인한 뒤 임시 정리 근거가 있는지 점검하세요.")
          : file.upstreamRequestId
            ? text(locale, "Use upstream trace to connect this file to the original user-facing flow.", "상위 요청 추적을 사용해 이 파일을 원래 사용자 흐름과 연결하세요.")
            : text(locale, "No deterministic upstream trace is available. Fall back to the linked entity and recent failure or save context.", "확정된 상위 요청 추적이 없습니다. 연결된 대상과 최근 실패 또는 저장 문맥을 기준으로 보조 조사하세요."),
  };
}

function toMediaArtifactViewModel(
  artifact: AdminMediaArtifactData,
  locale: AdminDashboardLocale,
  scenarioArtifacts: readonly AdminMediaArtifactData[],
  linkedLifecycleDrilldownTo?: string,
) {
  const requestTraceDrilldownTo = artifact.latestTraceKey?.requestId
    ? `/admin/failed-flows?${new URLSearchParams({ requestId: artifact.latestTraceKey.requestId }).toString()}`
    : artifact.latestTraceKey?.correlationId
      ? `/admin/failed-flows?${new URLSearchParams({ correlationId: artifact.latestTraceKey.correlationId }).toString()}`
      : undefined;
  const scenarioRepresentativeArtifact = findScenarioRepresentativeArtifact(scenarioArtifacts);
  const latestUploadedArtifact = findLatestUploadedArtifact(scenarioArtifacts);
  const matchesScenarioRepresentative = scenarioRepresentativeArtifact?.artifactId === artifact.artifactId;
  const matchesScenarioLatestUpload = latestUploadedArtifact?.artifactId === artifact.artifactId;
  const scenarioSourceConnectionKey = pickSourceConnectionKey(
    pickPhotoSourceEmptyStateConnectionKey(artifact.sourceScenario, locale),
    scenarioArtifacts,
  );
  const scenarioCoverageStateKey = pickCoverageStateKey(scenarioArtifacts);
  const scenarioDownloadOnlyCount = countDownloadOnlyArtifacts(scenarioArtifacts);
  const archiveStateKey = pickMediaArchiveStateKey(artifact);
  const incidentAttachmentStateKey = pickMediaIncidentAttachmentStateKey(artifact);
  const scenarioBlockedCount = scenarioArtifacts.filter(
    (candidate) => !isPreviewReady(candidate) && !isDownloadReady(candidate),
  ).length;

  return {
    artifactId: artifact.artifactId,
    sourceScenarioKey: artifact.sourceScenario,
    sourceScenarioLabel: formatMediaScenarioLabel(artifact.sourceScenario, locale),
    linkedEntityTypeLabel: formatMediaLinkedEntityTypeLabel(artifact.linkedEntityType, locale),
    linkedEntityLabel: toLabel(artifact.linkedEntityId),
    relationTypeKey: artifact.relationType,
    relationTypeLabel: formatMediaRelationTypeLabel(artifact.relationType, locale),
    artifactRoleLabel: formatMediaArtifactRoleLabel(artifact.artifactRole, locale),
    mediaKindLabel: formatMediaKindLabel(artifact.mediaKind, locale),
    contentTypeBucketLabel: artifact.contentTypeBucket,
    sizeBucketLabel: artifact.sizeBucket,
    uploadedAtLabel: formatAdminDate(artifact.uploadedAt),
    confirmedAtLabel: formatAdminDate(artifact.confirmedAt),
    storageStatusLabel: formatMediaStorageStatusLabel(artifact.storageStatus, locale),
    previewAvailability: artifact.previewAvailability,
    previewAvailabilityLabel: formatMediaAvailabilityLabel(artifact.previewAvailability, locale),
    downloadAvailability: artifact.downloadAvailability,
    downloadAvailabilityLabel: formatMediaAvailabilityLabel(artifact.downloadAvailability, locale),
    binaryReviewPathLabel: pickArtifactBinaryReviewPathLabel(artifact, locale),
    binaryReviewHint: pickArtifactBinaryReviewHint(artifact, locale),
    latestRequestId: artifact.latestTraceKey?.requestId,
    latestRequestIdLabel: toLabel(artifact.latestTraceKey?.requestId),
    latestCorrelationId: artifact.latestTraceKey?.correlationId,
    latestCorrelationIdLabel: toLabel(artifact.latestTraceKey?.correlationId),
    latestTraceLabel: formatLatestTraceLabel(artifact.latestTraceKey),
    fallbackReasonLabel: formatMediaFallbackReasonLabel(artifact.fallbackReason, locale),
    auditScopeLabel: formatMediaAuditScopeLabel(artifact.auditScope, locale),
    sameDeviceContextLabel: formatSameDeviceContextLabel(artifact.sameDeviceContextAvailable, locale),
    scenarioArtifactCountLabel: formatScenarioCount(
      scenarioArtifacts.length,
      locale,
      "artifacts",
      "artifact",
    ),
    scenarioSourceConnectionLabel: pickSourceConnectionLabel(scenarioSourceConnectionKey, locale),
    scenarioCoverageStateLabel: pickCoverageStateLabel(scenarioCoverageStateKey, locale),
    scenarioDownloadOnlyCountLabel: formatScenarioCount(
      scenarioDownloadOnlyCount,
      locale,
      "download-only",
      "다운로드 전용",
    ),
    scenarioBlockedCountLabel: formatScenarioCount(
      scenarioBlockedCount,
      locale,
      "blocked",
      "차단",
    ),
    scenarioFallbackSummaryLabel: pickFallbackSummaryLabel(scenarioArtifacts, locale),
    scenarioLatestRequestIdLabel: toLabel(latestUploadedArtifact?.latestTraceKey?.requestId),
    scenarioLatestTraceLabel: formatLatestTraceLabel(latestUploadedArtifact?.latestTraceKey),
    scenarioRepresentativeArtifactIdLabel: toLabel(scenarioRepresentativeArtifact?.artifactId),
    scenarioLatestUploadAtLabel: formatAdminDate(latestUploadedArtifact?.uploadedAt),
    matchesScenarioRepresentative,
    matchesScenarioLatestUpload,
    scenarioCompareHint: pickScenarioCompareHint(
      artifact,
      scenarioRepresentativeArtifact,
      latestUploadedArtifact,
      locale,
    ),
    archiveStateLabel: formatMediaArchiveStateLabel(archiveStateKey, locale),
    archiveStateHint: pickMediaArchiveStateHint(archiveStateKey, locale),
    incidentAttachmentStateLabel: formatMediaIncidentAttachmentStateLabel(
      incidentAttachmentStateKey,
      locale,
    ),
    incidentAttachmentHint: pickMediaIncidentAttachmentHint(
      incidentAttachmentStateKey,
      locale,
    ),
    requestTraceDrilldownTo,
    linkedFailedFlowDrilldownTo: requestTraceDrilldownTo,
    linkedLifecycleDrilldownTo,
  };
}

function inferMediaScenarioFromFilePurpose(
  purpose: AdminFileData["purpose"],
): Exclude<MediaScenarioKey, "all"> | undefined {
  switch (purpose) {
    case "user_avatar":
      return "avatar";
    case "pet_profile_photo":
      return "pet_profile";
    case "daily_record_photo":
    case "daily_record_video":
      return "daily_record";
    case "community_post_image":
      return "community_post";
    case "receipt_source":
      return "receipt";
    case "health_record_source":
    case "health_source":
      return "health_record";
    default:
      return undefined;
  }
}

function resolveRelatedMediaArtifactForFile(
  file: AdminFileData,
  artifacts: readonly AdminMediaArtifactData[],
):
  | {
      artifactId: string;
      sourceScenario: Exclude<MediaScenarioKey, "all">;
    }
  | undefined {
  const exactArtifact = artifacts.find((artifact) => artifact.artifactId === file.fileId);
  if (exactArtifact && exactArtifact.sourceScenario !== "unknown") {
    return {
      artifactId: exactArtifact.artifactId,
      sourceScenario: exactArtifact.sourceScenario,
    };
  }

  const explicitFileArtifact = artifacts.find(
    (artifact) =>
      artifact.linkedEntityType === "file" &&
      artifact.linkedEntityId === file.fileId &&
      artifact.sourceScenario !== "unknown",
  );
  if (explicitFileArtifact) {
    return {
      artifactId: explicitFileArtifact.artifactId,
      sourceScenario: explicitFileArtifact.sourceScenario,
    };
  }

  const expectedScenario = inferMediaScenarioFromFilePurpose(file.purpose);
  const contextualArtifact = artifacts.find(
    (artifact) =>
      artifact.sourceScenario !== "unknown" &&
      artifact.linkedEntityType === file.linkedEntityType &&
      artifact.linkedEntityId === file.linkedEntityId &&
      (expectedScenario === undefined || artifact.sourceScenario === expectedScenario),
  );
  if (contextualArtifact) {
    return {
      artifactId: contextualArtifact.artifactId,
      sourceScenario: contextualArtifact.sourceScenario,
    };
  }

  return undefined;
}

function buildArtifactLifecycleDrilldownMap(
  files: readonly AdminFileData[],
  artifacts: readonly AdminMediaArtifactData[],
  failureRequestIds: ReadonlySet<string>,
  locale: AdminDashboardLocale,
): Map<string, string> {
  const map = new Map<string, string>();

  for (const file of files) {
    const relatedArtifact = resolveRelatedMediaArtifactForFile(file, artifacts);
    if (!relatedArtifact || map.has(relatedArtifact.artifactId)) {
      continue;
    }

    const fileViewModel = toFileViewModel(file, failureRequestIds, relatedArtifact, locale);
    if (fileViewModel.lifecycleFailedFlowDrilldownTo) {
      map.set(relatedArtifact.artifactId, fileViewModel.lifecycleFailedFlowDrilldownTo);
    }
  }

  return map;
}

function toMediaScenarioPivots(
  artifacts: AdminMediaArtifactData[],
  representativeArtifactId: string | undefined,
  locale: AdminDashboardLocale,
) {
  const scenarioOrder = [
    "avatar",
    "pet_profile",
    "daily_record",
    "receipt",
    "health_record",
    "community_post",
    "unknown",
  ] as const;

  const pivots = scenarioOrder
    .map((scenarioKey) => {
      const scenarioArtifacts = artifacts.filter((artifact) => artifact.sourceScenario === scenarioKey);
      if (scenarioArtifacts.length === 0) {
        return undefined;
      }

      const latestUploadedArtifact = findLatestUploadedArtifact(scenarioArtifacts);
      const representativeArtifact = findScenarioRepresentativeArtifact(scenarioArtifacts);
      const downloadOnlyCount = countDownloadOnlyArtifacts(scenarioArtifacts);
      const archiveReadyCount = countArchiveReadyArtifacts(scenarioArtifacts);
      const incidentAttachedCount = countIncidentAttachedArtifacts(scenarioArtifacts);
      const blockedCount = scenarioArtifacts.filter(
        (artifact) => !isPreviewReady(artifact) && !isDownloadReady(artifact),
      ).length;

      return {
        scenarioKey,
        label: formatMediaScenarioLabel(scenarioKey, locale),
        artifactCount: scenarioArtifacts.length,
        artifactCountLabel: formatScenarioCount(scenarioArtifacts.length, locale, "artifacts", "artifact"),
        representativeArtifactIdLabel: toLabel(representativeArtifact?.artifactId),
        latestUploadAtLabel: formatAdminDate(latestUploadedArtifact?.uploadedAt),
        downloadOnlyCountLabel: formatScenarioCount(
          downloadOnlyCount,
          locale,
          "download-only",
          "다운로드 전용",
        ),
        archiveReadyCountLabel: formatScenarioCount(
          archiveReadyCount,
          locale,
          "archive-ready",
          "archive 준비",
        ),
        incidentAttachedCountLabel: formatScenarioCount(
          incidentAttachedCount,
          locale,
          "incident-attached",
          "incident 연결",
        ),
        blockedCountLabel: formatScenarioCount(blockedCount, locale, "blocked", "차단"),
        fallbackSummaryLabel: pickFallbackSummaryLabel(scenarioArtifacts, locale),
        sourceConnectionLabel: pickSourceConnectionLabel(
          pickSourceConnectionKey(
            pickPhotoSourceEmptyStateConnectionKey(scenarioKey, locale),
            scenarioArtifacts,
          ),
          locale,
        ),
        coverageStateLabel: pickCoverageStateLabel(pickCoverageStateKey(scenarioArtifacts), locale),
        latestRequestIdLabel: toLabel(latestUploadedArtifact?.latestTraceKey?.requestId),
        latestTraceLabel: formatLatestTraceLabel(latestUploadedArtifact?.latestTraceKey),
        representativeIncluded: representativeArtifactId
          ? scenarioArtifacts.some((artifact) => artifact.artifactId === representativeArtifactId)
          : false,
        investigationHint: pickMediaCoverageInvestigationHint(
          scenarioKey,
          pickCoverageStateKey(scenarioArtifacts),
          locale,
        ),
      };
    })
    .filter((pivot): pivot is NonNullable<typeof pivot> => pivot !== undefined);

  return [
    {
      scenarioKey: "all" as const,
      label: text(locale, "All scenarios", "전체 시나리오"),
      artifactCount: artifacts.length,
      artifactCountLabel: formatScenarioCount(artifacts.length, locale, "artifacts", "artifact"),
      representativeArtifactIdLabel: toLabel(
        findScenarioRepresentativeArtifact(artifacts)?.artifactId ?? representativeArtifactId,
      ),
      latestUploadAtLabel: formatAdminDate(findLatestUploadedArtifact(artifacts)?.uploadedAt),
      downloadOnlyCountLabel: formatScenarioCount(
        countDownloadOnlyArtifacts(artifacts),
        locale,
        "download-only",
        "다운로드 전용",
      ),
      archiveReadyCountLabel: formatScenarioCount(
        countArchiveReadyArtifacts(artifacts),
        locale,
        "archive-ready",
        "archive 준비",
      ),
      incidentAttachedCountLabel: formatScenarioCount(
        countIncidentAttachedArtifacts(artifacts),
        locale,
        "incident-attached",
        "incident 연결",
      ),
      blockedCountLabel: formatScenarioCount(
        artifacts.filter((artifact) => !isPreviewReady(artifact) && !isDownloadReady(artifact)).length,
        locale,
        "blocked",
        "차단",
      ),
      fallbackSummaryLabel: pickFallbackSummaryLabel(artifacts, locale),
      sourceConnectionLabel: pickSourceConnectionLabel(
        artifacts.length > 0 ? "connected" : "inventory_missing",
        locale,
      ),
      coverageStateLabel: pickCoverageStateLabel(pickCoverageStateKey(artifacts), locale),
      latestRequestIdLabel: toLabel(findLatestUploadedArtifact(artifacts)?.latestTraceKey?.requestId),
      latestTraceLabel: formatLatestTraceLabel(findLatestUploadedArtifact(artifacts)?.latestTraceKey),
      representativeIncluded: representativeArtifactId
        ? artifacts.some((artifact) => artifact.artifactId === representativeArtifactId)
        : false,
      investigationHint:
        artifacts.length > 0
          ? text(
              locale,
              "Read the scenario card first, then narrow into one representative or selected artifact before issuing preview or download.",
              "시나리오 카드를 먼저 읽고, 대표 또는 선택 artifact 하나로 좁힌 뒤 미리보기나 다운로드를 발급하세요.",
            )
          : text(
              locale,
              "No uploaded corpus is currently available. Confirm producer coverage before treating the user as photo-free.",
              "현재 업로드 코퍼스가 없습니다. 사용자가 사진이 없다고 단정하기 전에 생산자 coverage를 먼저 확인하세요.",
            ),
    },
    ...pivots,
  ];
}

function toMediaTimelineRows(
  artifacts: readonly AdminMediaArtifactData[],
  representativeArtifactId: string | undefined,
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["mediaTimelineRows"] {
  return [...artifacts]
    .sort((left, right) => {
      const uploadedAtOrder = right.uploadedAt.localeCompare(left.uploadedAt);
      if (uploadedAtOrder !== 0) {
        return uploadedAtOrder;
      }

      const scenarioOrder = formatMediaScenarioLabel(left.sourceScenario, locale).localeCompare(
        formatMediaScenarioLabel(right.sourceScenario, locale),
      );
      if (scenarioOrder !== 0) {
        return scenarioOrder;
      }

      return left.artifactId.localeCompare(right.artifactId);
    })
    .map((artifact) => {
      const scenarioArtifacts = artifacts.filter((candidate) => candidate.sourceScenario === artifact.sourceScenario);
      const latestUploadedArtifact = findLatestUploadedArtifact(scenarioArtifacts);

      return {
        artifactId: artifact.artifactId,
        sourceScenarioKey: artifact.sourceScenario,
        sourceScenarioLabel: formatMediaScenarioLabel(artifact.sourceScenario, locale),
        relationTypeLabel: formatMediaRelationTypeLabel(artifact.relationType, locale),
        artifactRoleLabel: formatMediaArtifactRoleLabel(artifact.artifactRole, locale),
        uploadedAtLabel: formatAdminDate(artifact.uploadedAt),
        previewAvailabilityLabel: formatMediaAvailabilityLabel(artifact.previewAvailability, locale),
        downloadAvailabilityLabel: formatMediaAvailabilityLabel(artifact.downloadAvailability, locale),
        binaryReviewPathLabel: pickArtifactBinaryReviewPathLabel(artifact, locale),
        latestTraceLabel: artifact.latestTraceKey
          ? `${toLabel(artifact.latestTraceKey.requestId)} / ${toLabel(artifact.latestTraceKey.correlationId)}`
          : "-",
        isRepresentativeEntry: representativeArtifactId === artifact.artifactId,
        isLatestForScenario: latestUploadedArtifact?.artifactId === artifact.artifactId,
        requestTraceDrilldownTo: artifact.latestTraceKey?.requestId
          ? `/admin/failed-flows?${new URLSearchParams({ requestId: artifact.latestTraceKey.requestId }).toString()}`
          : artifact.latestTraceKey?.correlationId
            ? `/admin/failed-flows?${new URLSearchParams({ correlationId: artifact.latestTraceKey.correlationId }).toString()}`
            : undefined,
      };
    });
}

function isPreviewReady(
  artifact: Pick<AdminMediaArtifactData, "previewAvailability">,
): boolean {
  return artifact.previewAvailability === "available";
}

function isDownloadReady(
  artifact: Pick<AdminMediaArtifactData, "downloadAvailability">,
): boolean {
  return artifact.downloadAvailability === "available";
}

type PhotoSourceSurfaceBaseline = {
  scenarioKey: Exclude<MediaScenarioKey, "all">;
  label: string;
  targetLinkedEntityLabel: string;
  baselineStatusLabel: string;
  baselineSummaryLabel: string;
  operatorHint: string;
  emptyStateConnectionKey: "inventory_missing" | "rollout_gap";
};

function buildPhotoSourceSurfaceBaselines(
  locale: AdminDashboardLocale,
): PhotoSourceSurfaceBaseline[] {
  return [
    {
      scenarioKey: "avatar",
      label: formatMediaScenarioLabel("avatar", locale),
      targetLinkedEntityLabel: text(locale, "user", "user"),
      baselineStatusLabel: text(locale, "Connected baseline", "기본 연결 완료"),
      baselineSummaryLabel: text(
        locale,
        "Representative photo direct source. Current admin inventory is expected to surface user-avatar uploads here.",
        "대표 사진 직접 source입니다. 현재 관리자 inventory는 사용자 아바타 업로드를 여기서 보여줘야 합니다.",
      ),
      operatorHint: text(
        locale,
        "If this stays empty, compare user-profile traces before assuming a rollout gap.",
        "여기가 비어 있으면 rollout gap으로 보기 전에 사용자 프로필 추적을 먼저 비교하세요.",
      ),
      emptyStateConnectionKey: "inventory_missing",
    },
    {
      scenarioKey: "pet_profile",
      label: formatMediaScenarioLabel("pet_profile", locale),
      targetLinkedEntityLabel: text(locale, "pet", "pet"),
      baselineStatusLabel: text(locale, "Connected baseline", "기본 연결 완료"),
      baselineSummaryLabel: text(
        locale,
        "Pet onboarding/profile direct source. Representative photo and selected artifact rows should already align here.",
        "펫 온보딩/프로필 직접 source입니다. 대표 사진과 선택 artifact row가 여기서 이미 맞물려야 합니다.",
      ),
      operatorHint: text(
        locale,
        "Check pet save or photo confirm traces first when the inventory looks inconsistent.",
        "inventory가 어긋나 보이면 펫 저장 또는 사진 확정 추적부터 확인하세요.",
      ),
      emptyStateConnectionKey: "inventory_missing",
    },
    {
      scenarioKey: "daily_record",
      label: formatMediaScenarioLabel("daily_record", locale),
      targetLinkedEntityLabel: text(locale, "record", "record"),
      baselineStatusLabel: text(locale, "Connected baseline", "기본 연결 완료"),
      baselineSummaryLabel: text(
        locale,
        "Daily record photo or video source. Preview and download capability may diverge by media kind, but the source itself is in scope now.",
        "일상 기록 사진/동영상 source입니다. 미디어 종류에 따라 미리보기와 다운로드 capability는 갈릴 수 있지만 source 자체는 현재 범위에 포함됩니다.",
      ),
      operatorHint: text(
        locale,
        "Use latestTraceKey and media kind together before classifying a daily artifact as missing.",
        "일상 artifact를 누락으로 분류하기 전에 latestTraceKey와 media kind를 함께 확인하세요.",
      ),
      emptyStateConnectionKey: "inventory_missing",
    },
    {
      scenarioKey: "receipt",
      label: formatMediaScenarioLabel("receipt", locale),
      targetLinkedEntityLabel: text(locale, "receipt", "receipt"),
      baselineStatusLabel: text(locale, "Projection follow-up", "projection 보강 필요"),
      baselineSummaryLabel: text(
        locale,
        "Receipt-linked media belongs in scope, but current coverage can still depend on linkage projection rather than a dedicated file purpose.",
        "영수증 연결 미디어는 범위에 포함되지만, 현재 coverage는 전용 file purpose보다 linkage projection에 더 의존할 수 있습니다.",
      ),
      operatorHint: text(
        locale,
        "Check receipt linkage projection before treating sparse inventory as product loss.",
        "희박한 inventory를 제품 손실로 보기 전에 receipt linkage projection부터 확인하세요.",
      ),
      emptyStateConnectionKey: "inventory_missing",
    },
    {
      scenarioKey: "health_record",
      label: formatMediaScenarioLabel("health_record", locale),
      targetLinkedEntityLabel: text(locale, "record", "record"),
      baselineStatusLabel: text(locale, "Connected baseline", "기본 연결 완료"),
      baselineSummaryLabel: text(
        locale,
        "Health record source is in scope now. Non-image artifacts may remain download-focused, but the source contract is already active.",
        "건강 기록 source는 현재 범위에 들어와 있습니다. 이미지가 아닌 artifact는 다운로드 중심일 수 있지만 source 계약 자체는 이미 활성화됐습니다.",
      ),
      operatorHint: text(
        locale,
        "Differentiate unsupported preview from missing inventory before escalating a health source incident.",
        "건강 기록 source incident를 올리기 전에 미리보기 미지원과 inventory 누락을 먼저 구분하세요.",
      ),
      emptyStateConnectionKey: "inventory_missing",
    },
    {
      scenarioKey: "community_post",
      label: formatMediaScenarioLabel("community_post", locale),
      targetLinkedEntityLabel: text(locale, "community post", "커뮤니티 게시물"),
      baselineStatusLabel: text(locale, "Connected baseline", "기본 연결 완료"),
      baselineSummaryLabel: text(
        locale,
        "Community posting photo corpus is in scope now. The admin inventory should surface community post image artifacts on the same baseline as other photo sources.",
        "커뮤니티 게시물 사진 corpus는 이제 현재 범위에 포함됩니다. 관리자 inventory는 다른 사진 source와 같은 baseline에서 커뮤니티 게시물 이미지 artifact를 보여줘야 합니다.",
      ),
      operatorHint: text(
        locale,
        "If this stays empty, compare community upload trace, file confirm state, and post linkage before treating it as product loss.",
        "여기가 비어 있으면 제품 손실로 보기 전에 커뮤니티 업로드 추적, 파일 확정 상태, 게시물 연결을 먼저 비교하세요.",
      ),
      emptyStateConnectionKey: "inventory_missing",
    },
  ];
}

function pickPhotoSourceEmptyStateConnectionKey(
  scenarioKey: MediaScenarioKey,
  locale: AdminDashboardLocale,
): "inventory_missing" | "rollout_gap" {
  if (scenarioKey === "unknown") {
    return "inventory_missing";
  }

  return (
    buildPhotoSourceSurfaceBaselines(locale).find((entry) => entry.scenarioKey === scenarioKey)?.emptyStateConnectionKey ??
    "inventory_missing"
  );
}

function pickCoverageStateKey(
  artifacts: readonly AdminMediaArtifactData[],
): "preview_available" | "download_only" | "metadata_only" | "missing" {
  if (artifacts.length === 0) {
    return "missing";
  }

  if (artifacts.some(isPreviewReady)) {
    return "preview_available";
  }

  if (artifacts.some(isDownloadReady)) {
    return "download_only";
  }

  return "metadata_only";
}

function pickCoverageStateLabel(
  coverageStateKey: "preview_available" | "download_only" | "metadata_only" | "missing",
  locale: AdminDashboardLocale,
): string {
  switch (coverageStateKey) {
    case "preview_available":
      return text(locale, "Preview available", "미리보기 가능");
    case "download_only":
      return text(locale, "Download only", "다운로드 전용");
    case "metadata_only":
      return text(locale, "Metadata only", "메타데이터만 가능");
    case "missing":
      return text(locale, "Missing", "누락");
  }
}

function pickMediaCoverageInvestigationHint(
  _scenarioKey: Exclude<MediaScenarioKey, "all">,
  coverageStateKey: "preview_available" | "download_only" | "metadata_only" | "missing",
  locale: AdminDashboardLocale,
): string {
  switch (coverageStateKey) {
    case "preview_available":
      return text(
        locale,
        "Start with the representative or selected artifact preview, then compare the same request trace before issuing the original download.",
        "대표 또는 선택 artifact 미리보기부터 열고, 같은 요청 추적을 비교한 뒤 원본 다운로드를 발급하세요.",
      );
    case "download_only":
      return text(
        locale,
        "Preview is unavailable or policy-gated. Read the fallback first, then use the original download and linked trace together.",
        "미리보기가 불가하거나 정책 확인이 필요합니다. fallback을 먼저 읽고 원본 다운로드와 연결된 추적을 함께 확인하세요.",
      );
    case "metadata_only":
      return text(
        locale,
        "No artifact is immediately previewable or downloadable. Use the fallback reason and latest trace to decide whether this is lifecycle, policy, or producer coverage.",
        "즉시 미리보기나 다운로드 가능한 artifact가 없습니다. fallback 이유와 최근 추적을 보고 수명주기, 정책, 생산자 coverage 중 어디 문제인지 구분하세요.",
      );
    case "missing":
      return text(
        locale,
        "No artifact inventory is currently linked to this scenario. Inspect the upstream request trace or producer coverage before treating it as a user-visible deletion.",
        "현재 이 시나리오에 연결된 artifact inventory가 없습니다. 사용자 가시 삭제로 단정하기 전에 상위 요청 추적과 생산자 coverage를 먼저 확인하세요.",
      );
  }
}

function pickSourceConnectionKey(
  emptyStateConnectionKey: "inventory_missing" | "rollout_gap",
  artifacts: readonly AdminMediaArtifactData[],
): "connected" | "inventory_missing" | "rollout_gap" {
  if (artifacts.length > 0) {
    return "connected";
  }

  return emptyStateConnectionKey;
}

function pickSourceConnectionLabel(
  sourceConnectionKey: "connected" | "inventory_missing" | "rollout_gap",
  locale: AdminDashboardLocale,
): string {
  switch (sourceConnectionKey) {
    case "connected":
      return text(locale, "Connected", "연결됨");
    case "inventory_missing":
      return text(locale, "Inventory missing", "inventory 누락");
    case "rollout_gap":
      return text(locale, "Rollout gap", "rollout 공백");
  }
}

function pickMissingSourceDisclosureReason(
  _scenarioKey: Exclude<MediaScenarioKey, "all">,
  locale: AdminDashboardLocale,
): string {
  return text(
    locale,
    "No inventory rows are currently connected for this source scenario.",
    "현재 이 source 시나리오에 연결된 inventory row가 없습니다.",
  );
}

function pickMissingSourceNextActionHint(
  _scenarioKey: Exclude<MediaScenarioKey, "all">,
  locale: AdminDashboardLocale,
): string {
  return text(
    locale,
    "Check producer coverage and upstream trace evidence before classifying this as a user-visible missing upload.",
    "사용자 가시 누락 업로드로 분류하기 전에 producer coverage와 상위 추적 근거를 먼저 확인하세요.",
  );
}

function pickFallbackSummaryLabel(
  artifacts: readonly AdminMediaArtifactData[],
  locale: AdminDashboardLocale,
): string {
  if (artifacts.length === 0) {
    return text(locale, "Missing", "누락");
  }

  const counts = new Map<
    AdminMediaArtifactData["fallbackReason"] | "available",
    number
  >();
  for (const artifact of artifacts) {
    const key = artifact.fallbackReason ?? "available";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const [topReason] =
    [...counts.entries()].sort((left, right) => right[1] - left[1])[0] ?? [];

  return topReason
    ? topReason === "available"
      ? formatMediaAvailabilityLabel("available", locale)
      : formatMediaFallbackReasonLabel(topReason, locale)
    : "-";
}

function findLatestUploadedArtifact(
  artifacts: readonly AdminMediaArtifactData[],
): AdminMediaArtifactData | undefined {
  return [...artifacts].sort((left, right) => right.uploadedAt.localeCompare(left.uploadedAt))[0];
}

function findScenarioRepresentativeArtifact(
  artifacts: readonly AdminMediaArtifactData[],
): AdminMediaArtifactData | undefined {
  return [...artifacts].sort((left, right) => {
    if (left.artifactRole === "representative" && right.artifactRole !== "representative") {
      return -1;
    }
    if (left.artifactRole !== "representative" && right.artifactRole === "representative") {
      return 1;
    }
    return right.uploadedAt.localeCompare(left.uploadedAt);
  })[0];
}

function countDownloadOnlyArtifacts(
  artifacts: readonly AdminMediaArtifactData[],
): number {
  return artifacts.filter((artifact) => !isPreviewReady(artifact) && isDownloadReady(artifact)).length;
}

function countArchiveReadyArtifacts(
  artifacts: readonly AdminMediaArtifactData[],
): number {
  return artifacts.filter((artifact) => pickMediaArchiveStateKey(artifact) === "original_ready").length;
}

function countIncidentAttachedArtifacts(
  artifacts: readonly AdminMediaArtifactData[],
): number {
  return artifacts.filter(
    (artifact) => pickMediaIncidentAttachmentStateKey(artifact) !== "not_attached",
  ).length;
}

function pickScenarioCompareHint(
  artifact: AdminMediaArtifactData,
  scenarioRepresentativeArtifact: AdminMediaArtifactData | undefined,
  latestUploadedArtifact: AdminMediaArtifactData | undefined,
  locale: AdminDashboardLocale,
): string {
  const isScenarioRepresentative = scenarioRepresentativeArtifact?.artifactId === artifact.artifactId;
  const isScenarioLatestUpload = latestUploadedArtifact?.artifactId === artifact.artifactId;

  if (isScenarioRepresentative && isScenarioLatestUpload) {
    return text(
      locale,
      "This selected artifact is both the scenario representative and the latest upload. Compare its trace first before moving to any other row.",
      "이 선택 artifact는 시나리오 대표이면서 최근 업로드이기도 합니다. 다른 row로 이동하기 전에 이 추적부터 먼저 비교하세요.",
    );
  }

  if (isScenarioRepresentative) {
    return text(
      locale,
      "This selected artifact is the scenario representative. Use it as the first compare anchor, then decide whether a newer contextual upload also matters.",
      "이 선택 artifact는 시나리오 대표입니다. 이를 첫 비교 기준점으로 사용한 뒤 더 최근의 문맥 artifact도 중요한지 판단하세요.",
    );
  }

  if (isScenarioLatestUpload) {
    return text(
      locale,
      "This selected artifact is the latest upload for the scenario, but not the representative entry. Compare it against the scenario representative before issuing the original download.",
      "이 선택 artifact는 해당 시나리오의 최근 업로드이지만 대표 항목은 아닙니다. 원본 다운로드 전에 시나리오 대표와 먼저 비교하세요.",
    );
  }

  return text(
    locale,
    "This selected artifact is neither the scenario representative nor the latest upload. Keep it pinned only when its trace or fallback explains the incident better than the scenario anchor rows.",
    "이 선택 artifact는 시나리오 대표도 최근 업로드도 아닙니다. 추적이나 fallback이 시나리오 기준 행보다 incident를 더 잘 설명할 때만 고정해 두세요.",
  );
}

function toMediaCoverageLedger(
  artifacts: readonly AdminMediaArtifactData[],
  representativeArtifactId: string | undefined,
  locale: AdminDashboardLocale,
) {
  const baselines = buildPhotoSourceSurfaceBaselines(locale);
  const scenarioOrder = baselines.map((entry) => entry.scenarioKey);

  const optionalUnknownArtifacts = artifacts.filter((artifact) => artifact.sourceScenario === "unknown");
  const orderedScenarios = optionalUnknownArtifacts.length > 0
    ? [...scenarioOrder, "unknown" as const]
    : scenarioOrder;

  return orderedScenarios.map((scenarioKey) => {
    const scenarioArtifacts = artifacts.filter((artifact) => artifact.sourceScenario === scenarioKey);
    const previewReadyCount = scenarioArtifacts.filter(isPreviewReady).length;
    const downloadReadyCount = scenarioArtifacts.filter(isDownloadReady).length;
    const blockedCount = scenarioArtifacts.filter(
      (artifact) => !isPreviewReady(artifact) && !isDownloadReady(artifact),
    ).length;
    const baseline = scenarioKey === "unknown"
      ? undefined
      : baselines.find((entry) => entry.scenarioKey === scenarioKey);
    const sourceConnectionKey = pickSourceConnectionKey(
      baseline?.emptyStateConnectionKey ?? "inventory_missing",
      scenarioArtifacts,
    );
    const coverageStateKey = pickCoverageStateKey(scenarioArtifacts);

    return {
      scenarioKey,
      label: formatMediaScenarioLabel(scenarioKey, locale),
      artifactCount: scenarioArtifacts.length,
      artifactCountLabel: formatScenarioCount(
        scenarioArtifacts.length,
        locale,
        "artifacts",
        "artifact",
      ),
      sourceConnectionKey,
      sourceConnectionLabel: pickSourceConnectionLabel(sourceConnectionKey, locale),
      coverageStateKey,
      coverageStateLabel: pickCoverageStateLabel(coverageStateKey, locale),
      previewReadyCountLabel: formatScenarioCount(previewReadyCount, locale, "preview ready", "미리보기 가능"),
      downloadReadyCountLabel: formatScenarioCount(downloadReadyCount, locale, "download ready", "다운로드 가능"),
      blockedCountLabel: formatScenarioCount(blockedCount, locale, "blocked", "차단"),
      fallbackSummaryLabel: pickFallbackSummaryLabel(scenarioArtifacts, locale),
      latestRequestIdLabel: toLabel(findLatestUploadedArtifact(scenarioArtifacts)?.latestTraceKey?.requestId),
      latestTraceLabel: formatLatestTraceLabel(findLatestUploadedArtifact(scenarioArtifacts)?.latestTraceKey),
      representativeIncluded: representativeArtifactId
        ? scenarioArtifacts.some((artifact) => artifact.artifactId === representativeArtifactId)
        : false,
      investigationHint: pickMediaCoverageInvestigationHint(scenarioKey, coverageStateKey, locale),
    };
  });
}

function toMissingPhotoSourceDisclosures(
  artifacts: readonly AdminMediaArtifactData[],
  locale: AdminDashboardLocale,
) {
  return buildPhotoSourceSurfaceBaselines(locale).flatMap((baseline) => {
    const scenarioArtifacts = artifacts.filter((artifact) => artifact.sourceScenario === baseline.scenarioKey);
    const sourceConnectionKey = pickSourceConnectionKey(
      baseline.emptyStateConnectionKey,
      scenarioArtifacts,
    );
    if (sourceConnectionKey !== "rollout_gap") {
      return [];
    }

    return [
      {
        scenarioKey: baseline.scenarioKey,
        label: baseline.label,
        sourceConnectionLabel: pickSourceConnectionLabel(sourceConnectionKey, locale),
        statusLabel: text(locale, "Not yet connected", "아직 미연결"),
        reasonLabel: pickMissingSourceDisclosureReason(baseline.scenarioKey, locale),
        nextActionHint: pickMissingSourceNextActionHint(baseline.scenarioKey, locale),
      },
    ];
  });
}

function pickBinaryReviewPathLabel(
  scenarioKey: Exclude<MediaScenarioKey, "all">,
  sourceConnectionKey: "connected" | "inventory_missing" | "rollout_gap",
  coverageStateKey: "preview_available" | "download_only" | "metadata_only" | "missing",
  locale: AdminDashboardLocale,
): string {
  if (sourceConnectionKey === "rollout_gap") {
    return text(locale, "Await rollout connection", "rollout 연결 대기");
  }

  if (scenarioKey === "receipt" && sourceConnectionKey === "connected" && coverageStateKey !== "preview_available") {
    return text(locale, "Projection follow-up first", "projection 보강 우선");
  }

  switch (coverageStateKey) {
    case "preview_available":
      return text(locale, "Preview first", "미리보기 우선");
    case "download_only":
      return text(locale, "Download first", "다운로드 우선");
    case "metadata_only":
      return text(locale, "Trace and fallback first", "추적 및 fallback 우선");
    case "missing":
      return text(locale, "Source trace first", "source 추적 우선");
  }
}

function pickArtifactBinaryReviewPathLabel(
  artifact: Pick<AdminMediaArtifactData, "previewAvailability" | "downloadAvailability" | "fallbackReason">,
  locale: AdminDashboardLocale,
): string {
  if (isPreviewReady(artifact)) {
    return text(locale, "Preview first", "미리보기 우선");
  }

  if (isDownloadReady(artifact)) {
    return text(locale, "Download first", "다운로드 우선");
  }

  if (artifact.fallbackReason === "missing") {
    return text(locale, "Source trace first", "source 추적 우선");
  }

  return text(locale, "Trace and fallback first", "추적 및 fallback 우선");
}

function pickArtifactBinaryReviewHint(
  artifact: Pick<AdminMediaArtifactData, "previewAvailability" | "downloadAvailability" | "fallbackReason">,
  locale: AdminDashboardLocale,
): string {
  if (isPreviewReady(artifact)) {
    return text(
      locale,
      "Open the preview first, then keep the same artifact pinned while you compare request trace and original download.",
      "먼저 미리보기를 열고, 같은 artifact를 고정한 채 요청 추적과 원본 다운로드를 함께 비교하세요.",
    );
  }

  if (isDownloadReady(artifact)) {
    return text(
      locale,
      "Preview is unavailable for this artifact. Issue the original download first, then compare the same artifact against its latest trace.",
      "이 artifact는 미리보기를 바로 열 수 없습니다. 먼저 원본 다운로드를 발급한 뒤 같은 artifact를 최신 추적과 비교하세요.",
    );
  }

  if (artifact.fallbackReason === "missing") {
    return text(
      locale,
      "Binary delivery is missing for this artifact. Go back to source trace and scenario coverage before treating it as product loss.",
      "이 artifact는 binary 전달 자체가 누락됐습니다. 제품 손실로 보기 전에 source 추적과 시나리오 coverage를 먼저 다시 확인하세요.",
    );
  }

  return text(
    locale,
    "Binary delivery is blocked for now. Read the fallback reason and latest trace before retrying preview or download.",
    "현재는 binary 전달이 막혀 있습니다. 미리보기나 다운로드를 다시 시도하기 전에 fallback 사유와 최신 추적을 먼저 읽으세요.",
  );
}

function toPhotoSourceSurfaceMatrix(
  artifacts: readonly AdminMediaArtifactData[],
  locale: AdminDashboardLocale,
) {
  return buildPhotoSourceSurfaceBaselines(locale).map((entry) => {
    const scenarioArtifacts = artifacts.filter((artifact) => artifact.sourceScenario === entry.scenarioKey);
    const sourceConnectionKey = pickSourceConnectionKey(
      entry.emptyStateConnectionKey,
      scenarioArtifacts,
    );
    const coverageStateKey = pickCoverageStateKey(scenarioArtifacts);
    const previewReadyCount = scenarioArtifacts.filter(isPreviewReady).length;
    const downloadReadyCount = scenarioArtifacts.filter(isDownloadReady).length;
    const blockedCount = scenarioArtifacts.filter(
      (artifact) => !isPreviewReady(artifact) && !isDownloadReady(artifact),
    ).length;

    return {
      ...entry,
      sourceConnectionLabel: pickSourceConnectionLabel(sourceConnectionKey, locale),
      currentCoverageStateLabel: pickCoverageStateLabel(coverageStateKey, locale),
      previewReadyCountLabel: formatScenarioCount(previewReadyCount, locale, "preview ready", "미리보기 가능"),
      downloadReadyCountLabel: formatScenarioCount(downloadReadyCount, locale, "download ready", "다운로드 가능"),
      blockedCountLabel: formatScenarioCount(blockedCount, locale, "blocked", "차단"),
      binaryReviewPathLabel: pickBinaryReviewPathLabel(
        entry.scenarioKey,
        sourceConnectionKey,
        coverageStateKey,
        locale,
      ),
    };
  });
}

function toIncidentLogArtifactViewModel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
) {
  const requestTraceDrilldownTo = artifact.latestTraceKey?.requestId
    ? `/admin/failed-flows?${new URLSearchParams({ requestId: artifact.latestTraceKey.requestId }).toString()}`
    : artifact.latestTraceKey?.correlationId
      ? `/admin/failed-flows?${new URLSearchParams({ correlationId: artifact.latestTraceKey.correlationId }).toString()}`
      : undefined;

  return {
    artifactId: artifact.artifactId,
    deviceInstallId: artifact.deviceInstallId,
    releaseLabel: artifact.release,
    appVersionLabel: artifact.appVersion,
    platformLabel: artifact.platform,
    runtimeAppBuildNumberLabel: formatAdminTelemetryAppBuildNumberLabel(artifact.runtimeAppBuildNumber, locale),
    runtimeLocaleLabel: formatAdminTelemetryLocaleLabel(artifact.runtimeLocale, locale),
    runtimeTimezoneOffsetBucketLabel: formatAdminTelemetryTimezoneOffsetBucketLabel(
      artifact.runtimeTimezoneOffsetBucket,
      locale,
    ),
    runtimeTimezoneNameBucketLabel: formatAdminTelemetryTimezoneNameBucketLabel(
      artifact.runtimeTimezoneNameBucket,
      locale,
    ),
    runtimeDeviceModelBucketLabel: formatAdminTelemetryDeviceModelBucketLabel(artifact.runtimeDeviceModelBucket, locale),
    runtimeDeviceModelNameBucketLabel: formatAdminTelemetryDeviceModelNameBucketLabel(
      artifact.runtimeDeviceModelNameBucket,
      locale,
    ),
    runtimeOsMajorBucketLabel: formatAdminTelemetryOsMajorBucketLabel(artifact.runtimeOsMajorBucket, locale),
    runtimeOsVersionMinorBucketLabel: formatAdminTelemetryOsVersionMinorBucketLabel(
      artifact.runtimeOsVersionMinorBucket,
      locale,
    ),
    runtimeNetworkTypeBucketLabel: formatAdminTelemetryNetworkTypeBucketLabel(
      artifact.runtimeNetworkTypeBucket,
      locale,
    ),
    runtimePhysicalDeviceBucketLabel: formatAdminTelemetryPhysicalDeviceBucketLabel(
      artifact.runtimePhysicalDeviceBucket,
      locale,
    ),
    runtimeSummaryLabel: [
      formatAdminTelemetryAppBuildNumberLabel(artifact.runtimeAppBuildNumber, locale),
      formatAdminTelemetryLocaleLabel(artifact.runtimeLocale, locale),
      formatAdminTelemetryTimezoneNameBucketLabel(artifact.runtimeTimezoneNameBucket, locale),
      formatAdminTelemetryDeviceModelNameBucketLabel(artifact.runtimeDeviceModelNameBucket, locale),
      formatAdminTelemetryOsVersionMinorBucketLabel(artifact.runtimeOsVersionMinorBucket, locale),
      formatAdminTelemetryNetworkTypeBucketLabel(artifact.runtimeNetworkTypeBucket, locale),
      formatAdminTelemetryPhysicalDeviceBucketLabel(artifact.runtimePhysicalDeviceBucket, locale),
    ].join(" / "),
    captureTriggerLabel: formatIncidentLogCaptureTriggerLabel(artifact.captureTrigger, locale),
    captureWindowLabel: formatIncidentLogCaptureWindowLabel(artifact.captureWindow, locale),
    capturedAtBucketLabel: formatIncidentLogCapturedAtBucketLabel(artifact.capturedAtBucket, locale),
    capturedAtOperatorValueLabel: formatAdminDate(artifact.capturedAtOperatorValue),
    supportabilityStateLabel: formatIncidentLogSupportabilityStateLabel(artifact.supportabilityState, locale),
    excerptLineCountBucketLabel: formatIncidentLogExcerptLineCountBucketLabel(artifact.excerptLineCountBucket, locale),
    bundleHashPresenceLabel: formatIncidentLogBundleHashPresenceLabel(artifact.bundleHashPresence, locale),
    manifestVersionLabel: toLabel(artifact.manifestVersion),
    redactionVersionLabel: toLabel(artifact.redactionVersion),
    downloadReadiness: artifact.downloadReadiness,
    previewReadinessLabel: formatIncidentLogPreviewReadinessLabel(artifact.previewReadiness, locale),
    downloadReadinessLabel: formatIncidentLogDownloadReadinessLabel(artifact.downloadReadiness, locale),
    handoffReadinessLabel: formatIncidentLogHandoffReadinessLabel(artifact.handoffReadiness, locale),
    previewHeadlineLabel: toLabel(artifact.previewExcerpt?.headline),
    previewTopCategoryLabel: toLabel(artifact.previewExcerpt?.topCategory),
    primaryFailureSignalLabel: toLabel(pickIncidentLogPrimaryFailureSignal(artifact)),
    diagnosisSummaryLabel: pickIncidentLogDiagnosisSummaryLabel(artifact, locale),
    diagnosisConfidenceLabel: pickIncidentLogDiagnosisConfidenceLabel(artifact, locale),
    recommendedActionLabel: pickIncidentLogRecommendedActionLabel(artifact, locale),
    deliveryPathStateLabel: pickIncidentLogDeliveryPathStateLabel(artifact, locale),
    producerPathNextActionLabel: pickIncidentLogProducerPathNextActionLabel(artifact, locale),
    latestRequestId: artifact.latestTraceKey?.requestId,
    latestRequestIdLabel: toLabel(artifact.latestTraceKey?.requestId),
    latestCorrelationId: artifact.latestTraceKey?.correlationId,
    latestCorrelationIdLabel: toLabel(artifact.latestTraceKey?.correlationId),
    latestTraceLabel: formatLatestTraceLabel(artifact.latestTraceKey),
    latestErrorCodeLabel: toLabel(artifact.latestTraceKey?.errorCode),
    previewLines: (artifact.previewExcerpt?.lines ?? []).map((line) => ({
      timestampLabel: formatAdminDate(line.timestamp),
      levelLabel: formatIncidentLogPreviewLineLevel(line.level),
      categoryLabel: line.category,
      messageKeyLabel: line.messageKey,
      redactedContextLabel: line.redactedContext,
      requestIdLabel: toLabel(line.requestId),
      correlationIdLabel: toLabel(line.correlationId),
      errorCodeLabel: toLabel(line.errorCode),
    })),
    requestTraceDrilldownTo,
    investigationHint: pickIncidentLogInvestigationHint(artifact, locale),
  };
}

function toJobViewModel(job: AdminJobData, locale: AdminDashboardLocale) {
  return {
    jobId: job.jobId,
    jobTypeLabel: job.jobType,
    statusLabel: formatAdminJobStatusLabel(job.status as AdminJobStatus, locale),
    attemptCountLabel: formatAdminCount(job.attemptCount),
    lastErrorCodeLabel: toLabel(job.lastErrorCode),
    providerLabel: toLabel(job.provider),
    queuedAtLabel: formatAdminDate(job.queuedAt),
    startedAtLabel: formatAdminDate(job.startedAt),
    finishedAtLabel: formatAdminDate(job.finishedAt),
    durationLabel: formatAdminDuration(job.durationMs),
    linkedFileIdLabel: toLabel(job.linkedFileId),
    linkedRecordIdLabel: toLabel(job.linkedRecordId),
  };
}

function buildFailedFlowDrilldownTo(summary: AdminUserSummary): string | undefined {
  if (summary.latestFailure?.correlationId) {
    const params = new URLSearchParams();
    params.set("correlationId", summary.latestFailure.correlationId);
    return `/admin/failed-flows?${params.toString()}`;
  }

  if (summary.latestFailure?.requestId) {
    const params = new URLSearchParams();
    params.set("requestId", summary.latestFailure.requestId);
    return `/admin/failed-flows?${params.toString()}`;
  }

  return undefined;
}

export function buildFailureDrilldownTo(failure: AdminLatestFailure): string | undefined {
  if (failure.correlationId) {
    const params = new URLSearchParams();
    params.set("correlationId", failure.correlationId);
    return `/admin/failed-flows?${params.toString()}`;
  }

  if (failure.requestId) {
    const params = new URLSearchParams();
    params.set("requestId", failure.requestId);
    return `/admin/failed-flows?${params.toString()}`;
  }

  return undefined;
}

export function buildRecentFailureContextLabels(
  failure: AdminLatestFailure,
  locale: AdminDashboardLocale,
): string[] {
  const labels: string[] = [];

  if (failure.providerKind) {
    labels.push(
      `${text(locale, "Auth provider", "인증 제공자")}: ${formatAdminAuthProviderKindLabel(
        failure.providerKind,
        locale,
      )}`,
    );
  }

  if (failure.reauthMethodBucket) {
    labels.push(
      `${text(locale, "Reauth method", "재인증 방식")}: ${formatAdminTelemetryReauthMethodBucketLabel(
        failure.reauthMethodBucket,
        locale,
      )}`,
    );
  }

  if (failure.recentAuthAgeBucket) {
    labels.push(
      `${text(locale, "Recent auth", "최근 인증")}: ${formatAdminTelemetryRecentAuthAgeBucketLabel(
        failure.recentAuthAgeBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestProfileCompletionStep) {
    labels.push(
      `${text(locale, "Profile step", "프로필 단계")}: ${formatAdminPetProfileCompletionStepLabel(
        failure.requestProfileCompletionStep,
        locale,
      )}`,
    );
  }

  if (failure.requestAttemptCountBucket) {
    labels.push(
      `${text(locale, "Attempt bucket", "재시도 버킷")}: ${formatAdminPetAttemptCountBucketLabel(
        failure.requestAttemptCountBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestSpeciesBucket) {
    labels.push(
      `${text(locale, "Species", "종")}: ${formatAdminPetSpeciesBucketLabel(
        failure.requestSpeciesBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestBreedSelectionBucket) {
    labels.push(
      `${text(locale, "Breed selection", "품종 선택")}: ${formatAdminPetBreedSelectionBucketLabel(
        failure.requestBreedSelectionBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestTargetUserLookupKind) {
    labels.push(
      `${text(locale, "Target lookup", "대상 조회 경로")}: ${formatAdminSocialTargetUserLookupKindLabel(
        failure.requestTargetUserLookupKind,
        locale,
      )}`,
    );
  }

  if (failure.requestPublicPetCountBucket) {
    labels.push(
      `${text(locale, "Public pet count", "공개 펫 수")}: ${formatAdminSocialPublicPetCountBucketLabel(
        failure.requestPublicPetCountBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestProfileMutationFieldSetBucket) {
    labels.push(
      `${text(locale, "Profile mutation", "프로필 수정 범위")}: ${formatAdminSocialProfileMutationFieldSetBucketLabel(
        failure.requestProfileMutationFieldSetBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestRoomKind) {
    labels.push(
      `${text(locale, "Room kind", "방 종류")}: ${formatAdminChatRoomKindLabel(
        failure.requestRoomKind,
        locale,
      )}`,
    );
  }

  if (failure.requestMessageLengthBucket) {
    labels.push(
      `${text(locale, "Message length", "메시지 길이")}: ${formatAdminChatMessageLengthBucketLabel(
        failure.requestMessageLengthBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestMembershipCountBucket) {
    labels.push(
      `${text(locale, "Membership size", "참여 인원 수")}: ${formatAdminChatMembershipCountBucketLabel(
        failure.requestMembershipCountBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestScheduleRecurrenceBucket) {
    labels.push(
      `${text(locale, "Schedule recurrence", "반복 일정")}: ${formatAdminMedicationScheduleRecurrenceBucketLabel(
        failure.requestScheduleRecurrenceBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestDosePresence) {
    labels.push(
      `${text(locale, "Dose presence", "복용량 입력")}: ${formatAdminMedicationDosePresenceLabel(
        failure.requestDosePresence,
        locale,
      )}`,
    );
  }

  if (failure.requestOccurrenceActionKind) {
    labels.push(
      `${text(locale, "Occurrence action", "복용 액션")}: ${formatAdminMedicationOccurrenceActionKindLabel(
        failure.requestOccurrenceActionKind,
        locale,
      )}`,
    );
  }

  if (failure.requestSnoozeWindowBucket) {
    labels.push(
      `${text(locale, "Snooze window", "미루기 시간")}: ${formatAdminMedicationSnoozeWindowBucketLabel(
        failure.requestSnoozeWindowBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestRideVisitLeadTimeBucket) {
    labels.push(
      `${text(locale, "Visit window", "방문 시점")}: ${formatAdminRideVisitLeadTimeBucketLabel(
        failure.requestRideVisitLeadTimeBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestRideLongestAddressLengthBucket) {
    labels.push(
      `${text(locale, "Address length", "주소 길이")}: ${formatAdminRideLongestAddressLengthBucketLabel(
        failure.requestRideLongestAddressLengthBucket,
        locale,
      )}`,
    );
  }

  if (typeof failure.requestRideMemoProvided === "boolean") {
    labels.push(
      `${text(locale, "Memo provided", "메모 포함")}: ${formatAdminBooleanLabel(
        failure.requestRideMemoProvided,
        locale,
      )}`,
    );
  }

  if (failure.requestCommunityUploadSourceBucket) {
    labels.push(
      `${text(locale, "Upload source", "업로드 경로")}: ${formatAdminCommunityUploadSourceBucketLabel(
        failure.requestCommunityUploadSourceBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestCommunityContentLengthBucket) {
    labels.push(
      `${text(locale, "Post length", "게시글 길이")}: ${formatAdminCommunityContentLengthBucketLabel(
        failure.requestCommunityContentLengthBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestCommunityCommentLengthBucket) {
    labels.push(
      `${text(locale, "Comment length", "댓글 길이")}: ${formatAdminCommunityContentLengthBucketLabel(
        failure.requestCommunityCommentLengthBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestCommunityImageCountBucket) {
    labels.push(
      `${text(locale, "Image count", "이미지 수")}: ${formatAdminCommunityImageCountBucketLabel(
        failure.requestCommunityImageCountBucket,
        locale,
      )}`,
    );
  }

  if (typeof failure.requestCommunityPetContextAttached === "boolean") {
    labels.push(
      `${text(locale, "Pet context", "펫 문맥")}: ${formatAdminBooleanLabel(
        failure.requestCommunityPetContextAttached,
        locale,
      )}`,
    );
  }

  if (failure.requestCommunityTopicTag) {
    labels.push(
      `${text(locale, "Topic", "주제")}: ${formatAdminCommunityTopicTagLabel(
        failure.requestCommunityTopicTag,
        locale,
      )}`,
    );
  }

  if (failure.requestCommunityRetryCountBucket) {
    labels.push(
      `${text(locale, "Retry bucket", "재시도 버킷")}: ${formatAdminPetAttemptCountBucketLabel(
        failure.requestCommunityRetryCountBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestReceiptExtractSourceBucket) {
    labels.push(
      `${text(locale, "Extract source", "추출 경로")}: ${formatAdminReceiptExtractSourceBucketLabel(
        failure.requestReceiptExtractSourceBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestReceiptImageByteSizeBucket) {
    labels.push(
      `${text(locale, "Image size", "이미지 크기")}: ${formatAdminReceiptImageByteSizeBucketLabel(
        failure.requestReceiptImageByteSizeBucket,
        locale,
      )}`,
    );
  }

  if (failure.requestReceiptOcrConfidenceBucket) {
    labels.push(
      `${text(locale, "OCR confidence", "OCR 확신도")}: ${formatAdminReceiptOcrConfidenceBucketLabel(
        failure.requestReceiptOcrConfidenceBucket,
        locale,
      )}`,
    );
  }

  if (typeof failure.requestReceiptManualOverride === "boolean") {
    labels.push(
      `${text(locale, "Manual override", "수동 수정")}: ${formatAdminBooleanLabel(
        failure.requestReceiptManualOverride,
        locale,
      )}`,
    );
  }

  if (failure.requestReceiptCategoryMutationType) {
    labels.push(
      `${text(locale, "Category mutation", "카테고리 변경")}: ${formatAdminReceiptCategoryMutationTypeLabel(
        failure.requestReceiptCategoryMutationType,
        locale,
      )}`,
    );
  }

  return labels;
}

function toRecentFailureViewModel(
  failure: AdminLatestFailure,
  index: number,
  locale: AdminDashboardLocale,
) {
  return {
    id: failure.requestId ?? failure.correlationId ?? `${failure.errorCode}:${failure.occurredAt}:${index}`,
    feature: failure.feature,
    useCase: failure.useCase,
    errorCode: failure.errorCode,
    occurredAtLabel: formatAdminDate(failure.occurredAt),
    contextLabels: buildRecentFailureContextLabels(failure, locale),
    requestId: failure.requestId,
    correlationId: failure.correlationId,
    drilldownTo: buildFailureDrilldownTo(failure),
  };
}

function toFailureViewModel(failure: AdminLatestFailure) {
  return {
    feature: failure.feature,
    useCase: failure.useCase,
    errorCode: failure.errorCode,
    occurredAtLabel: formatAdminDate(failure.occurredAt),
    requestId: failure.requestId,
    correlationId: failure.correlationId,
    drilldownTo: buildFailureDrilldownTo(failure),
  };
}

function toTriageTone(
  level: "none" | "attention" | "warning" | "critical",
): "default" | "attention" | "warning" | "critical" {
  if (level === "critical") {
    return "critical";
  }

  if (level === "warning") {
    return "warning";
  }

  if (level === "attention") {
    return "attention";
  }

  return "default";
}

function toAccountDeletionTone(
  status: string | null | undefined,
): "default" | "attention" | "warning" | "critical" {
  if (status === "failed") {
    return "critical";
  }

  if (status === "processing" || status === "queued") {
    return "warning";
  }

  if (status === "succeeded") {
    return "default";
  }

  return "default";
}

function toAccountDeletionHeadline(
  status: string | null | undefined,
  locale: AdminDashboardLocale,
): string {
  if (status === "failed") {
    return text(locale, "Deletion failed", "삭제 실패");
  }

  if (status === "processing") {
    return text(locale, "Deletion in progress", "삭제 진행 중");
  }

  if (status === "queued") {
    return text(locale, "Deletion queued", "삭제 대기");
  }

  if (status === "succeeded") {
    return text(locale, "Deletion completed", "삭제 완료");
  }

  return text(locale, "No deletion backlog", "삭제 대기 없음");
}

function buildUserOperationsTriageCards(
  summary: AdminUserSummary,
  detailRoute: `/admin/users/${string}`,
  latestFailure: UserOperationsViewModel["latestFailure"],
  latestSuccessfulSave: UserOperationsViewModel["latestSuccessfulSave"],
  hasTraceLookup: boolean,
  locale: AdminDashboardLocale,
): UserOperationsViewModel["triageCards"] {
  const totalDataFootprint =
    summary.counts.pets +
    summary.counts.records +
    summary.counts.receipts +
    summary.counts.files +
    summary.counts.aiJobs;
  const footprintSupporting = [
    `${text(locale, "pets", "펫")} ${formatAdminCount(summary.counts.pets)}`,
    `${text(locale, "records", "기록")} ${formatAdminCount(summary.counts.records)}`,
    `${text(locale, "receipts", "영수증")} ${formatAdminCount(summary.counts.receipts)}`,
    `${text(locale, "files", "파일")} ${formatAdminCount(summary.counts.files)}`,
    `${text(locale, "jobs", "작업")} ${formatAdminCount(summary.counts.aiJobs)}`,
  ].join(" / ");

  return [
    {
      key: "account",
      title: text(locale, "Account", "계정"),
      tone: toAccountDeletionTone(summary.accountDeletionStatus),
      headline: toAccountDeletionHeadline(summary.accountDeletionStatus, locale),
      supporting: `${text(locale, "release", "릴리스")} ${summary.latestRelease ?? "-"} / ${text(locale, "app", "앱")} ${summary.latestAppVersion ?? "-"}`,
      primaryActionLabel: text(locale, "Open detail", "상세 열기"),
      primaryActionTo: detailRoute,
      secondaryActionLabel: latestFailure?.drilldownTo ? text(locale, "Open latest failed flow", "최근 오류 흐름 열기") : undefined,
      secondaryActionTo: latestFailure?.drilldownTo,
    },
    {
      key: "risk",
      title: text(locale, "Risk", "위험"),
      tone: toTriageTone(summary.riskSummary.highestLevel),
      headline:
        summary.riskSummary.highestLevel === "none"
          ? text(locale, "No immediate risk", "즉시 대응할 위험 없음")
          : `${formatAdminRiskLevelLabel(summary.riskSummary.highestLevel, locale)} ${text(locale, "risk", "위험")}`,
      supporting:
        summary.riskSummary.items.length > 0
          ? locale === "ko"
            ? `${formatAdminCount(summary.riskSummary.items.length)}개 신호를 검토해야 합니다`
            : `${formatAdminCount(summary.riskSummary.items.length)} signals require review`
          : text(locale, "No current canonical risk signal", "현재 정규 위험 신호가 없습니다"),
      primaryActionLabel: text(locale, "Open detail", "상세 열기"),
      primaryActionTo: detailRoute,
    },
    {
      key: "latestFailure",
      title: text(locale, "Latest failure", "최근 실패"),
      tone: latestFailure ? "critical" : "default",
      headline: latestFailure ? latestFailure.errorCode : text(locale, "No recent failure", "최근 실패 없음"),
      supporting: latestFailure
        ? `${latestFailure.feature} / ${latestFailure.useCase} / ${latestFailure.occurredAtLabel}`
        : hasTraceLookup
          ? text(
              locale,
              "No failed request is linked to the requested trace",
              "요청한 추적에 연결된 실패 요청이 없습니다",
            )
          : text(
              locale,
              "No failed request is currently linked to this user summary",
              "현재 이 사용자 요약에 연결된 실패 요청이 없습니다",
            ),
      primaryActionLabel: latestFailure?.drilldownTo ? text(locale, "Open failed flow", "오류 흐름 열기") : text(locale, "Open detail", "상세 열기"),
      primaryActionTo: latestFailure?.drilldownTo ?? detailRoute,
      secondaryActionLabel: latestFailure?.drilldownTo ? text(locale, "Open detail", "상세 열기") : undefined,
      secondaryActionTo: latestFailure?.drilldownTo ? detailRoute : undefined,
    },
    {
      key: "latestSave",
      title: text(locale, "Latest save", "최근 저장"),
      tone: latestSuccessfulSave ? "default" : "attention",
      headline: latestSuccessfulSave ? latestSuccessfulSave.event : text(locale, "No recent save", "최근 저장 없음"),
      supporting: latestSuccessfulSave
        ? `${latestSuccessfulSave.occurredAtLabel} / ${text(locale, "record", "기록")} ${latestSuccessfulSave.recordId ?? "-"} / ${text(locale, "receipt", "영수증")} ${latestSuccessfulSave.receiptId ?? "-"}`
        : hasTraceLookup
          ? text(
              locale,
              "No successful save is linked to the requested trace",
              "요청한 추적에 연결된 저장 성공이 없습니다",
            )
          : text(
              locale,
              "Recent successful save evidence is not available in the current summary",
              "현재 요약에는 최근 저장 성공 근거가 없습니다",
            ),
      primaryActionLabel: text(locale, "Open detail", "상세 열기"),
      primaryActionTo: detailRoute,
    },
    {
      key: "footprint",
      title: text(locale, "Data footprint", "데이터 현황"),
      tone: totalDataFootprint > 0 ? "default" : "attention",
      headline: `${formatAdminCount(totalDataFootprint)} ${text(locale, "linked rows", "개 연결 행")}`,
      supporting: footprintSupporting,
      primaryActionLabel: text(locale, "Open detail", "상세 열기"),
      primaryActionTo: detailRoute,
    },
  ];
}

function findLatestPetFailure(detail: AdminUserDataDetail): AdminLatestFailure | undefined {
  const candidates = [
    detail.user.latestFailure,
    ...detail.user.recentFailures,
  ].filter((failure): failure is AdminLatestFailure => Boolean(failure));

  return candidates.find((failure) => failure.signalKey === "pet_profile");
}

function findLatestAccountDeletionFailure(
  detail: AdminUserDataDetail,
): AdminLatestFailure | undefined {
  const candidates = dedupeFailures(
    [detail.user.latestFailure, ...detail.user.recentFailures]
      .filter(isPresent)
      .filter((failure) => failure.signalKey === "account_deletion"),
  ).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));

  return candidates[0];
}

function findLatestAccountDeletionFailureWithDrilldown(
  detail: AdminUserDataDetail,
): AdminLatestFailure | undefined {
  const candidates = dedupeFailures(
    [detail.user.latestFailure, ...detail.user.recentFailures]
      .filter(isPresent)
      .filter((failure) => failure.signalKey === "account_deletion")
      .filter((failure) => Boolean(buildFailureDrilldownTo(failure))),
  ).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));

  return candidates[0];
}

function toAccountDeletionSummary(
  detail: AdminUserDataDetail,
  riskSummary: AdminUserDataRiskSummary,
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["accountDeletionSummary"] {
  const latestFailure = findLatestAccountDeletionFailure(detail);
  const latestTraceableFailure = findLatestAccountDeletionFailureWithDrilldown(detail);
  const status = detail.account.deletionJobStatus ?? detail.user.accountDeletionStatus ?? "none";
  const jobsDrilldownTo = `/admin/jobs?${new URLSearchParams({
    jobType: "account_deletion",
    userIdHash: detail.user.userIdHash,
  }).toString()}`;
  const hasCanonicalDeletionRisk = riskSummary.items.some(
    (item) => item.code === "ACCOUNT_DELETION_FAILED",
  );
  const latestFailureDrilldownTo = latestTraceableFailure
    ? buildFailureDrilldownTo(latestTraceableFailure)
    : undefined;
  const severity: UserDataDetailViewModel["accountDeletionSummary"]["severity"] =
    status === "failed" || hasCanonicalDeletionRisk || latestFailure
      ? "critical"
      : status === "processing" || status === "queued"
        ? "warning"
        : status === "succeeded"
          ? "none"
          : "none";

  const primaryActionTo = latestFailureDrilldownTo ?? jobsDrilldownTo;
  const primaryActionLabel = latestFailureDrilldownTo
    ? text(locale, "Open deletion failed flow", "삭제 오류 흐름 열기")
    : status === "none" && !hasCanonicalDeletionRisk
      ? undefined
      : text(locale, "Open deletion jobs", "삭제 작업 열기");

  const headline =
    status === "failed" || hasCanonicalDeletionRisk || latestFailure
      ? text(locale, "Deletion failed", "삭제 실패")
      : status === "processing"
        ? text(locale, "Deletion in progress", "삭제 진행 중")
        : status === "queued"
          ? text(locale, "Deletion queued", "삭제 대기")
          : status === "succeeded"
            ? text(locale, "Deletion completed", "삭제 완료")
            : text(locale, "No deletion workflow", "삭제 흐름 없음");

  const summary = latestFailure
    ? latestFailure.errorCode === "ACCOUNT_DELETION_REAUTH_REQUIRED"
      ? text(locale, "Reauth failed during deletion request. Reopen the failed flow first, then confirm whether a durable deletion job was created.", "삭제 요청 중 재인증에 실패했습니다. 먼저 오류 흐름을 다시 열고 영속 삭제 작업이 생성됐는지 확인하세요.")
      : latestFailure.stage === "account_deletion_process"
        ? text(locale, "Deletion worker or terminal cleanup failed. Start from the failed flow, then compare it with the durable deletion job state.", "삭제 작업자 또는 최종 정리 단계가 실패했습니다. 오류 흐름에서 시작해 영속 삭제 작업 상태와 비교하세요.")
        : text(locale, "Deletion request failed before completion. Check the failed flow first, then confirm whether the durable deletion job advanced.", "삭제 요청이 완료 전에 실패했습니다. 먼저 오류 흐름을 확인하고 영속 삭제 작업이 진행됐는지 점검하세요.")
    : status === "failed"
      ? text(locale, "Durable deletion job is failed. No request trace is pinned here, so start from the deletion job timeline and compare it with recent request outcomes.", "영속 삭제 작업이 실패 상태입니다. 여기에 고정된 요청 추적 정보가 없으므로 삭제 작업 타임라인에서 시작해 최근 요청 결과와 비교하세요.")
      : status === "processing"
        ? text(locale, "Deletion is actively processing. Verify the durable job timeline before reviewing raw account tables.", "삭제가 진행 중입니다. 원본 계정 테이블을 보기 전에 영속 작업 타임라인을 확인하세요.")
        : status === "queued"
          ? text(locale, "Deletion is queued and waiting for processing. Open the deletion job queue first.", "삭제가 대기열에 있으며 처리를 기다리고 있습니다. 먼저 삭제 작업 대기열을 여세요.")
          : status === "succeeded"
            ? text(locale, "Deletion workflow reached completed state in the current projection window.", "현재 프로젝션 구간에서 삭제 흐름이 완료 상태에 도달했습니다.")
            : text(locale, "No account deletion workflow is visible for this user in the current projection window.", "현재 프로젝션 구간에는 이 사용자의 계정 삭제 흐름이 보이지 않습니다.");

  const investigationHint = latestFailure
    ? latestFailure.errorCode === "ACCOUNT_DELETION_REAUTH_REQUIRED"
      ? text(locale, "Start with request reauth evidence, then confirm whether a durable deletion job was created after the retry.", "먼저 요청 재인증 근거를 확인하고 재시도 뒤 영속 삭제 작업이 생성됐는지 점검하세요.")
      : latestFailure.stage === "account_deletion_process"
        ? text(locale, "Check worker request logs and terminal cleanup evidence first. Local cleanup and Firebase credential deletion belong to the same investigation stage.", "먼저 작업자 요청 로그와 최종 정리 근거를 확인하세요. 로컬 정리와 Firebase 자격 증명 삭제는 같은 조사 단계입니다.")
        : text(locale, "Check deletion request validation and owner-user scope first, then compare with durable job creation.", "먼저 삭제 요청 검증과 소유 사용자 범위를 확인한 뒤 영속 작업 생성과 비교하세요.")
    : status === "failed"
      ? text(locale, "Open deletion jobs first, then compare durable failure state with recent request outcomes and cleanup evidence.", "먼저 삭제 작업 화면을 열고 영속 실패 상태를 최근 요청 결과, 정리 근거와 비교하세요.")
      : status === "processing" || status === "queued"
        ? text(locale, "Use the deletion jobs view as the first anchor, then inspect any matching recent failure rows if processing stalls.", "삭제 작업 화면을 첫 기준점으로 사용하고 처리가 멈추면 일치하는 최근 실패 행을 확인하세요.")
        : text(locale, "Use account metadata as baseline and move to recent failures only if deletion activity appears later.", "계정 메타데이터를 기준선으로 보고 이후 삭제 활동이 나타나면 최근 실패로 이동하세요.");

  return {
    severity,
    headline,
    summary,
    statusLabel: formatAdminAccountDeletionStatusLabel(status, locale),
    requestedAtLabel: formatAdminDate(detail.account.deletionRequestedAt),
    completedAtLabel: formatAdminDate(detail.account.deletionCompletedAt),
    latestFailure: latestFailure ? toFailureViewModel(latestFailure) : undefined,
    primaryActionLabel,
    primaryActionTo: primaryActionLabel ? primaryActionTo : undefined,
    jobsDrilldownTo,
    investigationHint,
  };
}

function toRecentSuccessfulSaveViewModel(save: AdminLatestSuccessfulSave, index: number) {
  return {
    id:
      save.requestId ??
      save.recordId ??
      save.receiptId ??
      `${save.signalKey ?? "unknown"}:${save.event}:${save.occurredAt}:${index}`,
    event: save.event,
    occurredAtLabel: formatAdminDate(save.occurredAt),
    signalKey: save.signalKey,
    stage: save.stage,
    requestId: save.requestId,
    correlationId: save.correlationId,
    recordType: save.recordType,
    recordId: save.recordId,
    receiptId: save.receiptId,
  };
}

type UserScenarioTimelineKey =
  | "auth"
  | "pet"
  | "social"
  | "chat"
  | "community"
  | "daily"
  | "health"
  | "receipt"
  | "medication"
  | "ride"
  | "files"
  | "accountDeletion";

type UserScenarioTimelineItem = UserOperationsViewModel["scenarioTimeline"][number];

const USER_SCENARIO_FALLBACK_ORDER: UserScenarioTimelineKey[] = [
  "auth",
  "pet",
  "social",
  "chat",
  "community",
  "daily",
  "health",
  "receipt",
  "medication",
  "ride",
  "files",
  "accountDeletion",
];

function hasSignalKey(
  value: Pick<AdminLatestFailure, "signalKey"> | Pick<AdminLatestSuccessfulSave, "signalKey">,
  signalKey: AdminAppSignalKey,
): boolean {
  return value.signalKey === signalKey;
}

function isAuthFailure(failure: AdminLatestFailure): boolean {
  return hasSignalKey(failure, "auth_session");
}

function isDailyFailure(failure: AdminLatestFailure): boolean {
  return failure.signalKey === "daily_record" || failure.signalKey === "daily_media";
}

function isSocialFailure(failure: AdminLatestFailure): boolean {
  return hasSignalKey(failure, "social_profile");
}

function isChatFailure(failure: AdminLatestFailure): boolean {
  return hasSignalKey(failure, "chat_room");
}

function isCommunityFailure(failure: AdminLatestFailure): boolean {
  return hasSignalKey(failure, "community_post");
}

function isMedicationFailure(failure: AdminLatestFailure): boolean {
  return hasSignalKey(failure, "medication_schedule");
}

function isRideFailure(failure: AdminLatestFailure): boolean {
  return hasSignalKey(failure, "ride_request");
}

function isDailySuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return save.recordType === "daily" || hasSignalKey(save, "daily_record") || hasSignalKey(save, "daily_media");
}

function isAuthSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "auth_session");
}

function isPetFailure(failure: AdminLatestFailure): boolean {
  return failure.signalKey === "pet_profile";
}

function localizeRiskSummary(
  riskSummary: AdminUserDataRiskSummary,
  locale: AdminDashboardLocale,
  latestPetFailure?: AdminLatestFailure,
): AdminUserDataRiskSummary {
  return {
    ...riskSummary,
    items: localizeAdminUserRiskItems(riskSummary.items, locale, latestPetFailure),
  };
}

function isHealthFailure(failure: AdminLatestFailure): boolean {
  return failure.signalKey === "health_record";
}

function isReceiptFailure(failure: AdminLatestFailure): boolean {
  return failure.signalKey === "receipt_record";
}

function isFileFailure(failure: AdminLatestFailure): boolean {
  return failure.signalKey === "file_lifecycle";
}

function isAccountDeletionFailure(failure: AdminLatestFailure): boolean {
  return failure.signalKey === "account_deletion";
}

function isHealthSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return save.recordType === "health" || hasSignalKey(save, "health_record");
}

function isReceiptSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return save.recordType === "receipt" || Boolean(save.receiptId) || hasSignalKey(save, "receipt_record");
}

function isPetSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "pet_profile") || save.event.startsWith("pet_");
}

function isFileSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "file_lifecycle") || save.event.startsWith("file_");
}

function isAccountDeletionSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "account_deletion") || save.event.startsWith("account_deletion");
}

function isSocialSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "social_profile");
}

function isChatSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "chat_room");
}

function isCommunitySuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "community_post");
}

function isMedicationSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "medication_schedule");
}

function isRideSuccessfulSave(save: AdminLatestSuccessfulSave): boolean {
  return hasSignalKey(save, "ride_request");
}

function pickLatestByDate<T>(
  items: readonly T[],
  getDate: (item: T) => string | undefined,
): T | undefined {
  return [...items]
    .filter((item) => Boolean(getDate(item)))
    .sort((left, right) => {
      const leftDate = getDate(left) ?? "";
      const rightDate = getDate(right) ?? "";
      return rightDate.localeCompare(leftDate);
    })[0];
}

function isLatestFailureCurrent(
  failure: AdminLatestFailure | undefined,
  save: AdminLatestSuccessfulSave | undefined,
): failure is AdminLatestFailure {
  if (!failure) {
    return false;
  }

  if (!save) {
    return true;
  }

  return failure.occurredAt >= save.occurredAt;
}

function buildJobDrilldownTo(job: AdminJobData): string {
  const params = new URLSearchParams();
  params.set("jobType", job.jobType);
  if (job.linkedFileId) {
    params.set("linkedFileId", job.linkedFileId);
  }
  if (job.linkedRecordId) {
    params.set("linkedRecordId", job.linkedRecordId);
  }
  if (job.ownerUserIdHash) {
    params.set("userIdHash", job.ownerUserIdHash);
  }
  return `/admin/jobs?${params.toString()}`;
}

function toUploadDiagnosisStatusFromJob(
  status: AdminJobStatus | undefined,
): UploadDiagnosisStatusValue {
  if (status === "failed") {
    return "failed";
  }
  if (status === "processing") {
    return "processing";
  }
  if (status === "queued") {
    return "queued";
  }
  if (status === "succeeded") {
    return "succeeded";
  }
  return "unavailable";
}

function toUploadDiagnosisFailureModuleLabel(
  failure: AdminLatestFailure | undefined,
  locale: AdminDashboardLocale,
): string {
  if (!failure) {
    return text(locale, "No blocking module", "현재 막힌 모듈 없음");
  }

  return [failure.stage ?? failure.useCase, failure.errorCode].filter(Boolean).join(" / ");
}

function toUploadDiagnosisLatestSaveLabel(
  save: AdminLatestSuccessfulSave | undefined,
  locale: AdminDashboardLocale,
): string {
  if (!save) {
    return text(locale, "No confirmed save", "확정 저장 없음");
  }

  const target = save.recordId ?? save.receiptId ?? "-";
  return `${save.event} / ${target} / ${formatAdminDate(save.occurredAt)}`;
}

function buildUploadDiagnosisStep(input: {
  key: string;
  title: string;
  status: UploadDiagnosisStatusValue;
  summary: string;
  detail: string;
  metaRows?: UploadDiagnosisEnrichmentRowViewModel[];
  fileId?: string;
  recordId?: string;
  receiptId?: string;
  jobId?: string;
  requestId?: string;
  correlationId?: string;
  errorCode?: string;
  failedFlowDrilldownTo?: string;
  jobsDrilldownTo?: string;
}): UserDataDetailViewModel["uploadDiagnoses"][number]["steps"][number] {
  return {
    key: input.key,
    title: input.title,
    status: input.status,
    summary: input.summary,
    detail: input.detail,
    metaRows: input.metaRows ?? [],
    fileIdLabel: toLabel(input.fileId),
    recordIdLabel: toLabel(input.recordId),
    receiptIdLabel: toLabel(input.receiptId),
    jobIdLabel: toLabel(input.jobId),
    requestIdLabel: toLabel(input.requestId),
    correlationIdLabel: toLabel(input.correlationId),
    errorCodeLabel: toLabel(input.errorCode),
    failedFlowDrilldownTo: input.failedFlowDrilldownTo,
    jobsDrilldownTo: input.jobsDrilldownTo,
  };
}

function pushUploadDiagnosisEnrichmentRow(
  rows: UploadDiagnosisEnrichmentRowViewModel[],
  key: string,
  label: string,
  value: string | undefined,
): void {
  if (!value || value.trim().length === 0) {
    return;
  }

  rows.push({
    key,
    label,
    value: value.trim(),
  });
}

function buildUploadDiagnosisEnrichmentRows(
  input: AdminUploadDiagnosisEnrichment | undefined,
  locale: AdminDashboardLocale,
): UploadDiagnosisEnrichmentRowViewModel[] {
  if (!input) {
    return [];
  }

  const rows: UploadDiagnosisEnrichmentRowViewModel[] = [];
  pushUploadDiagnosisEnrichmentRow(rows, "ocrProviderName", text(locale, "OCR provider", "OCR 공급자"), input.ocrProviderName);
  pushUploadDiagnosisEnrichmentRow(rows, "ocrStatus", text(locale, "OCR status", "OCR 상태"), input.ocrStatus);
  pushUploadDiagnosisEnrichmentRow(rows, "ocrConfidenceSummary", text(locale, "OCR confidence", "OCR 신뢰도"), input.ocrConfidenceSummary);
  pushUploadDiagnosisEnrichmentRow(rows, "aiStructuringModel", text(locale, "AI model", "AI 모델"), input.aiStructuringModel);
  pushUploadDiagnosisEnrichmentRow(rows, "aiStructuringStatus", text(locale, "AI status", "AI 상태"), input.aiStructuringStatus);
  pushUploadDiagnosisEnrichmentRow(rows, "manualReviewReason", text(locale, "Manual review", "수동 검토"), input.manualReviewReason);
  pushUploadDiagnosisEnrichmentRow(rows, "elapsedTimeBucket", text(locale, "Elapsed time", "처리 시간"), input.elapsedTimeBucket);
  return rows;
}

function buildHealthUploadDiagnosis(
  detail: AdminUserDataDetail,
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["uploadDiagnoses"][number] {
  const latestFailure = toLatestFailureForScenario(detail.user, isHealthFailure);
  const latestSave = toLatestSuccessfulSaveForScenario(detail.user, isHealthSuccessfulSave);
  const currentFailure = isLatestFailureCurrent(latestFailure, latestSave) ? latestFailure : undefined;
  const sourceFile = pickLatestByDate(
    detail.files.filter((file) => file.purpose === "health_source" || file.purpose === "health_record_source"),
    (file) => file.confirmedAt ?? file.createdAt,
  );
  const latestRecord = pickLatestByDate(
    detail.records.filter((record) => record.type === "health"),
    (record) => record.updatedAt ?? record.createdAt,
  );
  const analysisJob = pickLatestByDate(
    detail.aiJobs.filter(
      (job) =>
        job.jobType === "health_file_analysis" ||
        job.signalKey === "health_record" ||
        (sourceFile ? job.linkedFileId === sourceFile.fileId : false) ||
        (latestRecord ? job.linkedRecordId === latestRecord.recordId : false),
    ),
    (job) => job.finishedAt ?? job.startedAt ?? job.queuedAt,
  );

  const overallStatus: UploadDiagnosisStatusValue = currentFailure
    ? "failed"
    : analysisJob?.status === "processing"
      ? "processing"
      : analysisJob?.status === "queued"
        ? "queued"
        : latestSave
          ? "succeeded"
          : sourceFile
            ? "attention"
            : "unavailable";

  return {
    scenarioKey: "health",
    title: text(locale, "Health upload diagnosis", "건강 기록 업로드 진단"),
    overallStatus,
    summary: currentFailure
      ? text(locale, "The latest health upload flow is blocked in analysis or save. Open the failed flow before trusting the current record state.", "최근 건강 업로드 흐름이 분석 또는 저장 단계에서 막혔습니다. 현재 기록 상태를 신뢰하기 전에 오류 흐름을 먼저 여세요.")
      : analysisJob?.status === "processing" || analysisJob?.status === "queued"
        ? text(locale, "The health source file is linked, but the analysis job is still in flight.", "건강 원본 파일은 연결됐지만 분석 작업이 아직 진행 중입니다.")
        : latestSave
          ? text(locale, "A confirmed health save is visible. Compare it with the latest source file and any newer job failure.", "확정된 건강 저장이 보입니다. 최신 원본 파일과 더 최근의 작업 실패가 있는지 함께 비교하세요.")
          : sourceFile
            ? text(locale, "A health source file is visible, but no confirmed save is visible yet.", "건강 원본 파일은 보이지만 아직 확정 저장은 보이지 않습니다.")
            : text(locale, "No health upload-linked evidence is visible in the current users/detail contract.", "현재 users/detail 계약에는 건강 업로드 연결 증거가 보이지 않습니다."),
    sourceAnchorLabel: sourceFile
      ? `${sourceFile.fileId} / ${formatFilePurposeLabel(sourceFile.purpose, locale)}`
      : latestRecord
        ? `${text(locale, "Record", "기록")} ${latestRecord.recordId}`
        : "-",
    recognitionSignalLabel: text(
      locale,
      "No health confidence field exists in the current users/detail contract.",
      "현재 users/detail 계약에는 건강 분석 신뢰도 필드가 없습니다.",
    ),
    failedModuleLabel: toUploadDiagnosisFailureModuleLabel(currentFailure, locale),
    latestTraceLabel: formatLatestTraceLabel({
      requestId: currentFailure?.requestId ?? latestSave?.requestId ?? sourceFile?.upstreamRequestId ?? sourceFile?.latestLifecycleRequestId,
      correlationId: currentFailure?.correlationId,
    }),
    latestSaveLabel: toUploadDiagnosisLatestSaveLabel(latestSave, locale),
    operatorHint: currentFailure
      ? text(locale, "Use the failed flow and linked health analysis job together. The current users/detail contract does not carry health OCR raw text or provider payload.", "오류 흐름과 연결된 health analysis 작업을 함께 보세요. 현재 users/detail 계약은 건강 OCR 원문이나 provider payload를 싣지 않습니다.")
      : text(locale, "Use the source file, linked job, and final save together. If quality metrics are needed, add a new source owner first instead of inferring them here.", "원본 파일, 연결된 작업, 최종 저장을 함께 보세요. 품질 지표가 필요하면 여기서 추정하지 말고 먼저 새 source owner를 정의해야 합니다."),
    steps: [
      buildUploadDiagnosisStep({
        key: "source",
        title: text(locale, "Source upload", "원본 업로드"),
        status: sourceFile ? "succeeded" : "unavailable",
        summary: sourceFile
          ? text(locale, "Health source file is linked to the current user detail.", "건강 원본 파일이 현재 사용자 상세에 연결되어 있습니다.")
          : text(locale, "No linked health source file is visible.", "연결된 건강 원본 파일이 보이지 않습니다."),
        detail: sourceFile
          ? `${sourceFile.fileId} / ${formatFileStatusLabel(sourceFile.status, locale)} / ${formatAdminDate(sourceFile.confirmedAt ?? sourceFile.createdAt)}`
          : text(locale, "Current contract has no health upload anchor for this user.", "현재 계약에는 이 사용자의 건강 업로드 anchor가 없습니다."),
        fileId: sourceFile?.fileId,
        recordId: latestRecord?.recordId,
        requestId: sourceFile?.upstreamRequestId ?? sourceFile?.latestLifecycleRequestId,
        failedFlowDrilldownTo: sourceFile?.upstreamRequestId
          ? `/admin/failed-flows?${new URLSearchParams({ requestId: sourceFile.upstreamRequestId }).toString()}`
          : undefined,
      }),
      buildUploadDiagnosisStep({
        key: "analysis",
        title: text(locale, "OCR and analysis", "OCR 및 분석"),
        status: currentFailure && currentFailure.stage === "health_analysis_process"
          ? "failed"
          : analysisJob
            ? toUploadDiagnosisStatusFromJob(analysisJob.status)
            : latestRecord?.latestAiJobStatus === "succeeded"
              ? "succeeded"
              : latestRecord?.latestAiJobStatus === "queued"
                ? "queued"
                : "attention",
        summary: currentFailure && currentFailure.stage === "health_analysis_process"
          ? text(locale, "The latest health analysis step failed.", "최근 건강 분석 단계가 실패했습니다.")
          : analysisJob
            ? text(locale, "A durable health analysis job is linked to this upload.", "이 업로드에 연결된 durable health analysis 작업이 있습니다.")
            : latestRecord?.latestAiJobStatus
              ? text(locale, "Record metadata shows AI status, but no durable job row is linked in the current detail payload.", "기록 메타데이터에는 AI 상태가 있지만 현재 detail payload에는 durable job row가 연결되지 않습니다.")
              : text(locale, "No health analysis job is visible.", "보이는 health analysis 작업이 없습니다."),
        detail: analysisJob
          ? `${analysisJob.jobType} / ${formatAdminJobStatusLabel(analysisJob.status, locale)} / ${toLabel(analysisJob.provider)}`
          : latestRecord?.latestAiJobStatus && latestRecord.latestAiJobStatus !== "none"
            ? `${text(locale, "Record AI state", "기록 AI 상태")} / ${formatAdminJobStatusLabel(latestRecord.latestAiJobStatus, locale)}`
            : text(locale, "Current users/detail contract has no health OCR quality or provider-model detail.", "현재 users/detail 계약에는 건강 OCR 품질이나 provider-model 상세가 없습니다."),
        metaRows: buildUploadDiagnosisEnrichmentRows(detail.uploadDiagnosis?.health?.analysis, locale),
        fileId: sourceFile?.fileId,
        recordId: latestRecord?.recordId,
        jobId: analysisJob?.jobId,
        requestId: currentFailure?.requestId,
        correlationId: currentFailure?.correlationId,
        errorCode: currentFailure?.errorCode ?? analysisJob?.lastErrorCode,
        failedFlowDrilldownTo:
          currentFailure && currentFailure.stage === "health_analysis_process"
            ? buildFailureDrilldownTo(currentFailure)
            : undefined,
        jobsDrilldownTo: analysisJob ? buildJobDrilldownTo(analysisJob) : undefined,
      }),
      buildUploadDiagnosisStep({
        key: "save",
        title: text(locale, "Final save", "최종 저장"),
        status: latestSave
          ? "succeeded"
          : currentFailure && currentFailure.stage === "health_record_save"
            ? "failed"
            : sourceFile || analysisJob
              ? "attention"
              : "unavailable",
        summary: latestSave
          ? text(locale, "A health record save anchor is visible.", "건강 기록 저장 anchor가 보입니다.")
          : currentFailure && currentFailure.stage === "health_record_save"
            ? text(locale, "The latest health save step failed.", "최근 건강 저장 단계가 실패했습니다.")
            : text(locale, "No confirmed health save is visible yet.", "아직 확정된 건강 저장이 보이지 않습니다."),
        detail: latestSave
          ? `${latestSave.event} / ${toLabel(latestSave.recordId)} / ${formatAdminDate(latestSave.occurredAt)}`
          : currentFailure && currentFailure.stage === "health_record_save"
            ? `${currentFailure.errorCode} / ${formatAdminDate(currentFailure.occurredAt)}`
            : text(locale, "Stay on the source file and analysis steps until a confirmed save or a newer failed flow appears.", "확정 저장이나 더 새로운 실패 흐름이 보일 때까지 원본 파일과 분석 단계를 기준으로 조사하세요."),
        fileId: sourceFile?.fileId,
        recordId: latestSave?.recordId ?? latestRecord?.recordId,
        requestId: latestSave?.requestId ?? currentFailure?.requestId,
        correlationId: latestSave?.correlationId ?? currentFailure?.correlationId,
        errorCode: currentFailure?.stage === "health_record_save" ? currentFailure.errorCode : undefined,
        failedFlowDrilldownTo:
          currentFailure && currentFailure.stage === "health_record_save"
            ? buildFailureDrilldownTo(currentFailure)
            : undefined,
      }),
    ],
  };
}

function buildReceiptRecognitionSignalLabel(
  failure: AdminLatestFailure | undefined,
  locale: AdminDashboardLocale,
): string {
  if (!failure) {
    return text(locale, "No OCR confidence source in the latest users/detail receipt state.", "최신 users/detail 영수증 상태에는 OCR 신뢰도 source가 없습니다.");
  }

  if (
    !failure.requestReceiptOcrConfidenceBucket &&
    !failure.requestReceiptExtractSourceBucket &&
    failure.requestReceiptManualOverride === undefined
  ) {
    return text(locale, "No OCR confidence source in the latest users/detail receipt failure.", "최신 users/detail 영수증 실패에는 OCR 신뢰도 source가 없습니다.");
  }

  return [
    `${text(locale, "OCR", "OCR")} ${formatAdminReceiptOcrConfidenceBucketLabel(failure.requestReceiptOcrConfidenceBucket, locale)}`,
    `${text(locale, "Source", "입력 경로")} ${formatAdminReceiptExtractSourceBucketLabel(failure.requestReceiptExtractSourceBucket, locale)}`,
    `${text(locale, "Manual override", "수동 수정")} ${formatAdminBooleanLabel(failure.requestReceiptManualOverride, locale)}`,
  ].join(" / ");
}

function buildReceiptUploadDiagnosis(
  detail: AdminUserDataDetail,
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["uploadDiagnoses"][number] {
  const latestFailure = toLatestFailureForScenario(detail.user, isReceiptFailure);
  const latestSave = toLatestSuccessfulSaveForScenario(detail.user, isReceiptSuccessfulSave);
  const currentFailure = isLatestFailureCurrent(latestFailure, latestSave) ? latestFailure : undefined;
  const latestReceipt = pickLatestByDate(detail.receipts, (receipt) => receipt.updatedAt ?? receipt.createdAt);
  const sourceArtifact = pickLatestByDate(
    (detail.mediaArtifacts ?? []).filter((artifact) => artifact.sourceScenario === "receipt"),
    (artifact) => artifact.confirmedAt ?? artifact.uploadedAt,
  );
  const legacySourceFile = pickLatestByDate(
    detail.files.filter((file) => file.purpose === "receipt_source"),
    (file) => file.confirmedAt ?? file.createdAt,
  );

  const overallStatus: UploadDiagnosisStatusValue = currentFailure
    ? "failed"
    : latestSave
      ? "succeeded"
      : latestReceipt?.extractionStatus === "draft_created" || latestReceipt?.extractionStatus === "fallback"
        ? "attention"
        : sourceArtifact || legacySourceFile || latestReceipt
          ? "attention"
          : "unavailable";

  return {
    scenarioKey: "receipt",
    title: text(locale, "Receipt upload diagnosis", "영수증 업로드 진단"),
    overallStatus,
    summary: currentFailure
      ? text(locale, "The latest receipt extraction or save flow is blocked. Open the failed flow before trusting the saved receipt state.", "최근 영수증 추출 또는 저장 흐름이 막혔습니다. 저장된 영수증 상태를 신뢰하기 전에 오류 흐름을 먼저 여세요.")
      : latestSave
        ? text(locale, "A confirmed receipt save is visible. Compare it with the latest extraction cue before editing categories again.", "확정된 영수증 저장이 보입니다. 카테고리를 다시 수정하기 전에 최신 추출 단서와 함께 비교하세요.")
        : latestReceipt?.extractionStatus === "draft_created"
          ? text(locale, "Receipt extraction created a draft, but no final save is visible yet.", "영수증 추출이 초안을 만들었지만 아직 최종 저장은 보이지 않습니다.")
          : sourceArtifact || legacySourceFile || latestReceipt
            ? text(locale, "Receipt-linked evidence exists, but the current users/detail contract does not prove a final save yet.", "영수증 연결 증거는 있지만 현재 users/detail 계약은 아직 최종 저장을 증명하지 않습니다.")
            : text(locale, "No receipt upload-linked evidence is visible in the current users/detail contract.", "현재 users/detail 계약에는 영수증 업로드 연결 증거가 보이지 않습니다."),
    sourceAnchorLabel: sourceArtifact
      ? `${sourceArtifact.artifactId} / ${sourceArtifact.sourceScenario}`
      : legacySourceFile
        ? `${legacySourceFile.fileId} / ${formatFilePurposeLabel(legacySourceFile.purpose, locale)}`
      : latestReceipt
        ? `${text(locale, "Receipt", "영수증")} ${latestReceipt.receiptId}`
        : "-",
    recognitionSignalLabel: buildReceiptRecognitionSignalLabel(currentFailure ?? latestFailure, locale),
    failedModuleLabel: toUploadDiagnosisFailureModuleLabel(currentFailure, locale),
    latestTraceLabel: formatLatestTraceLabel({
      requestId:
        currentFailure?.requestId ??
        latestSave?.requestId ??
        sourceArtifact?.latestTraceKey?.requestId ??
        legacySourceFile?.upstreamRequestId,
      correlationId:
        currentFailure?.correlationId ??
        latestSave?.correlationId ??
        sourceArtifact?.latestTraceKey?.correlationId,
    }),
    latestSaveLabel: toUploadDiagnosisLatestSaveLabel(latestSave, locale),
    operatorHint: currentFailure
      ? text(locale, "Receipt diagnosis can show OCR confidence and mutation cues, but not raw OCR text or final provider payload. Use the failed flow as the ground truth.", "영수증 진단은 OCR 신뢰도와 mutation 단서는 보여주지만 raw OCR text나 최종 provider payload는 보여주지 않습니다. 오류 흐름을 정본으로 사용하세요.")
      : text(locale, "Use OCR confidence, extract source, and final save together. If raw recognized text is needed, add a new source owner instead of expanding users/detail ad hoc.", "OCR 신뢰도, 입력 경로, 최종 저장을 함께 보세요. raw 인식 text가 필요하면 users/detail을 ad hoc으로 넓히지 말고 새 source owner를 먼저 정의하세요."),
    steps: [
      buildUploadDiagnosisStep({
        key: "source",
        title: text(locale, "Source upload", "원본 업로드"),
        status: sourceArtifact || legacySourceFile || latestReceipt ? "succeeded" : "unavailable",
        summary: sourceArtifact
          ? text(locale, "Receipt-linked source artifact is visible.", "영수증 연결 원본 아티팩트가 보입니다.")
          : legacySourceFile
            ? text(locale, "Legacy receipt source evidence is still linked.", "구형 영수증 원본 증거가 아직 연결되어 있습니다.")
          : latestReceipt
            ? text(locale, "Receipt row exists, but the linked source artifact is not visible yet.", "영수증 행은 있지만 연결된 원본 아티팩트는 아직 보이지 않습니다.")
            : text(locale, "No receipt source anchor is visible.", "영수증 원본 anchor가 보이지 않습니다."),
        detail: sourceArtifact
          ? `${sourceArtifact.artifactId} / ${formatMediaStorageStatusLabel(sourceArtifact.storageStatus, locale)}`
          : legacySourceFile
            ? `${legacySourceFile.fileId} / ${formatFileStatusLabel(legacySourceFile.status, locale)}`
          : latestReceipt
            ? `${latestReceipt.receiptId} / ${formatAdminDate(latestReceipt.createdAt)}`
            : text(locale, "Current contract does not expose a receipt upload anchor for this user.", "현재 계약은 이 사용자의 영수증 업로드 anchor를 노출하지 않습니다."),
        fileId: legacySourceFile?.fileId,
        receiptId: latestReceipt?.receiptId,
        requestId: sourceArtifact?.latestTraceKey?.requestId ?? legacySourceFile?.upstreamRequestId,
        correlationId: sourceArtifact?.latestTraceKey?.correlationId,
        failedFlowDrilldownTo:
          sourceArtifact?.latestTraceKey?.requestId
            ? `/admin/failed-flows?${new URLSearchParams({ requestId: sourceArtifact.latestTraceKey.requestId }).toString()}`
            : legacySourceFile?.upstreamRequestId
              ? `/admin/failed-flows?${new URLSearchParams({ requestId: legacySourceFile.upstreamRequestId }).toString()}`
          : undefined,
      }),
      buildUploadDiagnosisStep({
        key: "extract",
        title: text(locale, "OCR and extract", "OCR 및 추출"),
        status: currentFailure && currentFailure.stage === "receipt_extract"
          ? "failed"
          : latestReceipt?.extractionStatus === "draft_created"
            ? "succeeded"
            : latestReceipt?.extractionStatus === "fallback"
              ? "attention"
              : latestReceipt?.extractionStatus === "failed"
                ? "failed"
                : latestReceipt
                  ? "attention"
                  : "unavailable",
        summary: currentFailure && currentFailure.stage === "receipt_extract"
          ? text(locale, "The latest receipt OCR/extract step failed.", "최근 영수증 OCR/추출 단계가 실패했습니다.")
          : latestReceipt?.extractionStatus === "draft_created"
            ? text(locale, "Receipt extraction produced a draft.", "영수증 추출이 초안을 생성했습니다.")
            : latestReceipt?.extractionStatus === "fallback"
              ? text(locale, "Receipt extraction fell back before final save.", "영수증 추출이 최종 저장 전에 fallback 경로로 내려갔습니다.")
              : text(locale, "No confirmed OCR/extract evidence is visible.", "확정된 OCR/추출 증거가 보이지 않습니다."),
        detail: currentFailure
          ? [
              formatAdminReceiptOcrConfidenceBucketLabel(currentFailure.requestReceiptOcrConfidenceBucket, locale),
              formatAdminReceiptImageByteSizeBucketLabel(currentFailure.requestReceiptImageByteSizeBucket, locale),
              formatAdminReceiptCategoryMutationTypeLabel(currentFailure.requestReceiptCategoryMutationType, locale),
            ].join(" / ")
          : latestReceipt
            ? `${text(locale, "Extraction state", "추출 상태")} / ${toLabel(latestReceipt.extractionStatus)}`
            : text(locale, "Current users/detail payload has no raw OCR or final Gemini payload fields.", "현재 users/detail payload에는 raw OCR 또는 최종 Gemini payload 필드가 없습니다."),
        metaRows: buildUploadDiagnosisEnrichmentRows(detail.uploadDiagnosis?.receipt?.extract, locale),
        receiptId: latestReceipt?.receiptId,
        requestId: currentFailure?.requestId,
        correlationId: currentFailure?.correlationId,
        errorCode: currentFailure?.stage === "receipt_extract" ? currentFailure.errorCode : undefined,
        failedFlowDrilldownTo:
          currentFailure && currentFailure.stage === "receipt_extract"
            ? buildFailureDrilldownTo(currentFailure)
            : undefined,
      }),
      buildUploadDiagnosisStep({
        key: "save",
        title: text(locale, "Finalize and save", "최종 정리 및 저장"),
        status: latestSave
          ? "succeeded"
          : currentFailure &&
              (currentFailure.stage === "receipt_save" ||
                currentFailure.stage === "receipt_category_create" ||
                currentFailure.stage === "receipt_category_update" ||
                currentFailure.stage === "receipt_category_delete")
            ? "failed"
            : latestReceipt
              ? "attention"
              : "unavailable",
        summary: latestSave
          ? text(locale, "A final receipt save anchor is visible.", "최종 영수증 저장 anchor가 보입니다.")
          : currentFailure
            ? text(locale, "The latest receipt finalize/save step failed.", "최근 영수증 최종 정리/저장 단계가 실패했습니다.")
            : text(locale, "No final receipt save is visible yet.", "아직 최종 영수증 저장이 보이지 않습니다."),
        detail: latestSave
          ? `${latestSave.event} / ${toLabel(latestSave.receiptId ?? latestReceipt?.receiptId)} / ${formatAdminDate(latestSave.occurredAt)}`
          : currentFailure
            ? `${currentFailure.errorCode} / ${formatAdminDate(currentFailure.occurredAt)}`
            : text(locale, "Stay on extract diagnostics until a confirmed save or a newer failed flow appears.", "확정 저장이나 더 새로운 실패 흐름이 보일 때까지 추출 진단을 기준으로 보세요."),
        metaRows: buildUploadDiagnosisEnrichmentRows(detail.uploadDiagnosis?.receipt?.save, locale),
        receiptId: latestSave?.receiptId ?? latestReceipt?.receiptId,
        requestId: latestSave?.requestId ?? currentFailure?.requestId,
        correlationId: latestSave?.correlationId ?? currentFailure?.correlationId,
        errorCode:
          currentFailure &&
          (currentFailure.stage === "receipt_save" ||
            currentFailure.stage === "receipt_category_create" ||
            currentFailure.stage === "receipt_category_update" ||
            currentFailure.stage === "receipt_category_delete")
            ? currentFailure.errorCode
            : undefined,
        failedFlowDrilldownTo: currentFailure ? buildFailureDrilldownTo(currentFailure) : undefined,
      }),
    ],
  };
}

function buildDailyUploadDiagnosis(
  detail: AdminUserDataDetail,
  mediaArtifacts: readonly AdminMediaArtifactData[],
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["uploadDiagnoses"][number] {
  const latestFailure = toLatestFailureForScenario(detail.user, isDailyFailure);
  const latestSave = toLatestSuccessfulSaveForScenario(detail.user, isDailySuccessfulSave);
  const currentFailure = isLatestFailureCurrent(latestFailure, latestSave) ? latestFailure : undefined;
  const latestRecord = pickLatestByDate(
    detail.records.filter((record) => record.type === "daily"),
    (record) => record.updatedAt ?? record.createdAt,
  );
  const sourceFile = pickLatestByDate(
    detail.files.filter(
      (file) => file.purpose === "daily_record_photo" || file.purpose === "daily_record_video",
    ),
    (file) => file.confirmedAt ?? file.createdAt,
  );
  const sourceArtifact = pickLatestByDate(
    mediaArtifacts.filter((artifact) => artifact.sourceScenario === "daily_record"),
    (artifact) => artifact.confirmedAt ?? artifact.uploadedAt,
  );
  const analysisJob = pickLatestByDate(
    detail.aiJobs.filter(
      (job) =>
        job.jobType === "daily_analysis" ||
        job.signalKey === "daily_record" ||
        (latestRecord ? job.linkedRecordId === latestRecord.recordId : false),
    ),
    (job) => job.finishedAt ?? job.startedAt ?? job.queuedAt,
  );
  const hasUploadAnchor = Boolean(sourceFile || sourceArtifact);
  const uploadScopedAnalysisJob = hasUploadAnchor ? analysisJob : undefined;
  const uploadScopedFailure = hasUploadAnchor ? currentFailure : undefined;
  const uploadScopedSave = hasUploadAnchor ? latestSave : undefined;

  const overallStatus: UploadDiagnosisStatusValue = uploadScopedFailure
    ? "failed"
    : uploadScopedAnalysisJob?.status === "processing"
      ? "processing"
      : uploadScopedAnalysisJob?.status === "queued"
        ? "queued"
        : hasUploadAnchor
          ? uploadScopedSave
            ? "succeeded"
            : "attention"
          : "unavailable";

  return {
    scenarioKey: "daily",
    title: text(locale, "Daily upload diagnosis", "일상 기록 업로드 진단"),
    overallStatus,
    summary: uploadScopedFailure
      ? text(locale, "The latest daily upload-linked flow is blocked before final save.", "최근 일상 업로드 연결 흐름이 최종 저장 전에 막혔습니다.")
      : uploadScopedAnalysisJob?.status === "processing" || uploadScopedAnalysisJob?.status === "queued"
        ? text(locale, "A daily analysis job is still running or queued for the current upload-linked record.", "현재 업로드 연결 일상 기록에 대해 daily analysis 작업이 아직 진행 중이거나 대기 중입니다.")
        : hasUploadAnchor
          ? uploadScopedSave
            ? text(locale, "A daily upload-linked save is visible. Compare it with the source attachment and any newer analysis failure.", "업로드가 연결된 일상 저장이 보입니다. 원본 첨부와 더 최근의 분석 실패가 있는지 함께 비교하세요.")
            : text(locale, "Daily upload-linked evidence exists, but no confirmed final save is visible yet.", "일상 업로드 연결 증거는 있지만 아직 확정된 최종 저장은 보이지 않습니다.")
          : text(locale, "No upload-linked daily evidence is visible. Text-only daily flows stay outside this upload diagnosis workspace.", "업로드가 연결된 일상 증거가 보이지 않습니다. 텍스트 전용 일상 흐름은 이 upload diagnosis workspace 밖에 둡니다."),
    sourceAnchorLabel: sourceFile
      ? `${sourceFile.fileId} / ${formatFilePurposeLabel(sourceFile.purpose, locale)}`
      : sourceArtifact
        ? `${sourceArtifact.artifactId} / ${sourceArtifact.sourceScenario}`
        : "-",
    recognitionSignalLabel: text(
      locale,
      "No daily upload quality field exists in the current users/detail contract.",
      "현재 users/detail 계약에는 일상 업로드 품질 필드가 없습니다.",
    ),
    failedModuleLabel: toUploadDiagnosisFailureModuleLabel(uploadScopedFailure, locale),
    latestTraceLabel: formatLatestTraceLabel({
      requestId:
        uploadScopedFailure?.requestId ??
        uploadScopedSave?.requestId ??
        sourceArtifact?.latestTraceKey?.requestId ??
        sourceFile?.upstreamRequestId,
      correlationId:
        uploadScopedFailure?.correlationId ??
        uploadScopedSave?.correlationId ??
        sourceArtifact?.latestTraceKey?.correlationId,
    }),
    latestSaveLabel: toUploadDiagnosisLatestSaveLabel(uploadScopedSave, locale),
    operatorHint: uploadScopedFailure
      ? text(locale, "Daily upload diagnosis only covers flows with an uploaded attachment anchor. Use the failed flow and linked daily analysis job first.", "일상 업로드 진단은 업로드 첨부 anchor가 있는 흐름만 다룹니다. 오류 흐름과 연결된 daily analysis 작업을 먼저 보세요.")
      : text(locale, "Use the attachment anchor, analysis job, and final save together. If text-only daily diagnostics are needed, split them from upload diagnosis in a follow-up row.", "첨부 anchor, 분석 작업, 최종 저장을 함께 보세요. 텍스트 전용 일상 진단이 필요하면 후속 row에서 upload diagnosis와 분리해야 합니다."),
    steps: [
      buildUploadDiagnosisStep({
        key: "source",
        title: text(locale, "Source upload", "원본 업로드"),
        status: sourceFile || sourceArtifact ? "succeeded" : "unavailable",
        summary: sourceFile || sourceArtifact
          ? text(locale, "A daily attachment anchor is linked.", "일상 첨부 anchor가 연결되어 있습니다.")
          : text(locale, "No daily upload attachment anchor is visible.", "일상 업로드 첨부 anchor가 보이지 않습니다."),
        detail: sourceFile
          ? `${sourceFile.fileId} / ${formatFileStatusLabel(sourceFile.status, locale)}`
          : sourceArtifact
            ? `${sourceArtifact.artifactId} / ${formatMediaAvailabilityLabel(sourceArtifact.downloadAvailability, locale)}`
            : text(locale, "Current users/detail payload does not prove an uploaded daily attachment for this user.", "현재 users/detail payload는 이 사용자의 업로드된 일상 첨부를 증명하지 않습니다."),
        fileId: sourceFile?.fileId ?? sourceArtifact?.artifactId,
        recordId: latestRecord?.recordId,
        requestId: sourceArtifact?.latestTraceKey?.requestId ?? sourceFile?.upstreamRequestId,
        correlationId: sourceArtifact?.latestTraceKey?.correlationId,
        failedFlowDrilldownTo: sourceArtifact?.latestTraceKey?.requestId
          ? `/admin/failed-flows?${new URLSearchParams({ requestId: sourceArtifact.latestTraceKey.requestId }).toString()}`
          : sourceFile?.upstreamRequestId
            ? `/admin/failed-flows?${new URLSearchParams({ requestId: sourceFile.upstreamRequestId }).toString()}`
            : undefined,
      }),
      buildUploadDiagnosisStep({
        key: "analysis",
        title: text(locale, "Analysis draft", "분석 초안"),
        status: uploadScopedFailure &&
            (uploadScopedFailure.stage === "daily_draft_create" || uploadScopedFailure.stage === "daily_draft_update")
          ? "failed"
          : uploadScopedAnalysisJob
            ? toUploadDiagnosisStatusFromJob(uploadScopedAnalysisJob.status)
            : latestRecord?.latestAiJobStatus === "succeeded"
              ? "succeeded"
              : latestRecord?.latestAiJobStatus === "queued"
                ? "queued"
                : hasUploadAnchor
                  ? "attention"
                  : "unavailable",
        summary: uploadScopedFailure &&
            (uploadScopedFailure.stage === "daily_draft_create" || uploadScopedFailure.stage === "daily_draft_update")
          ? text(locale, "The latest daily analysis draft step failed.", "최근 일상 분석 초안 단계가 실패했습니다.")
          : uploadScopedAnalysisJob
            ? text(locale, "A durable daily analysis job is linked to this upload flow.", "이 업로드 흐름에 연결된 durable daily analysis 작업이 있습니다.")
            : latestRecord?.latestAiJobStatus
              ? text(locale, "Record metadata keeps an AI state, but no durable daily analysis job is linked in the current detail payload.", "기록 메타데이터에는 AI 상태가 있지만 현재 detail payload에는 durable daily analysis 작업이 연결되지 않습니다.")
              : text(locale, "No daily analysis job is visible.", "보이는 daily analysis 작업이 없습니다."),
        detail: uploadScopedAnalysisJob
          ? `${uploadScopedAnalysisJob.jobType} / ${formatAdminJobStatusLabel(uploadScopedAnalysisJob.status, locale)} / ${toLabel(uploadScopedAnalysisJob.provider)}`
          : latestRecord?.latestAiJobStatus && latestRecord.latestAiJobStatus !== "none"
            ? `${text(locale, "Record AI state", "기록 AI 상태")} / ${formatAdminJobStatusLabel(latestRecord.latestAiJobStatus, locale)}`
            : text(locale, "Current users/detail contract has no daily analysis confidence field.", "현재 users/detail 계약에는 일상 분석 신뢰도 필드가 없습니다."),
        metaRows: buildUploadDiagnosisEnrichmentRows(detail.uploadDiagnosis?.daily?.analysis, locale),
        fileId: sourceFile?.fileId ?? sourceArtifact?.artifactId,
        recordId: latestRecord?.recordId,
        jobId: uploadScopedAnalysisJob?.jobId,
        requestId: uploadScopedFailure?.requestId,
        correlationId: uploadScopedFailure?.correlationId,
        errorCode: uploadScopedFailure?.errorCode ?? uploadScopedAnalysisJob?.lastErrorCode,
        failedFlowDrilldownTo:
          uploadScopedFailure &&
          (uploadScopedFailure.stage === "daily_draft_create" || uploadScopedFailure.stage === "daily_draft_update")
            ? buildFailureDrilldownTo(uploadScopedFailure)
            : undefined,
        jobsDrilldownTo: uploadScopedAnalysisJob ? buildJobDrilldownTo(uploadScopedAnalysisJob) : undefined,
      }),
      buildUploadDiagnosisStep({
        key: "save",
        title: text(locale, "Final save", "최종 저장"),
        status: uploadScopedSave
          ? "succeeded"
          : uploadScopedFailure &&
              (uploadScopedFailure.stage === "daily_record_save" || uploadScopedFailure.stage === "record_update")
            ? "failed"
            : hasUploadAnchor
              ? "attention"
              : "unavailable",
        summary: uploadScopedSave
          ? text(locale, "A daily record save anchor is visible.", "일상 기록 저장 anchor가 보입니다.")
          : uploadScopedFailure
            ? text(locale, "The latest daily final save step failed.", "최근 일상 최종 저장 단계가 실패했습니다.")
            : text(locale, "No confirmed daily save is visible yet.", "아직 확정된 일상 저장이 보이지 않습니다."),
        detail: uploadScopedSave
          ? `${uploadScopedSave.event} / ${toLabel(uploadScopedSave.recordId)} / ${formatAdminDate(uploadScopedSave.occurredAt)}`
          : uploadScopedFailure
            ? `${uploadScopedFailure.errorCode} / ${formatAdminDate(uploadScopedFailure.occurredAt)}`
            : text(locale, "Stay on the source attachment and analysis steps until a confirmed save or a newer failed flow appears.", "확정 저장이나 더 새로운 실패 흐름이 보일 때까지 원본 첨부와 분석 단계를 기준으로 보세요."),
        fileId: sourceFile?.fileId ?? sourceArtifact?.artifactId,
        recordId: uploadScopedSave?.recordId ?? latestRecord?.recordId,
        requestId: uploadScopedSave?.requestId ?? uploadScopedFailure?.requestId,
        correlationId: uploadScopedSave?.correlationId ?? uploadScopedFailure?.correlationId,
        errorCode:
          uploadScopedFailure &&
          (uploadScopedFailure.stage === "daily_record_save" || uploadScopedFailure.stage === "record_update")
            ? uploadScopedFailure.errorCode
            : undefined,
        failedFlowDrilldownTo: uploadScopedFailure ? buildFailureDrilldownTo(uploadScopedFailure) : undefined,
      }),
    ],
  };
}

function buildUploadDiagnoses(
  detail: AdminUserDataDetail,
  mediaArtifacts: readonly AdminMediaArtifactData[],
  locale: AdminDashboardLocale,
): UserDataDetailViewModel["uploadDiagnoses"] {
  return [
    buildHealthUploadDiagnosis(detail, locale),
    buildReceiptUploadDiagnosis(detail, locale),
    buildDailyUploadDiagnosis(detail, mediaArtifacts, locale),
  ];
}

function dedupeFailures(failures: AdminLatestFailure[]): AdminLatestFailure[] {
  const seen = new Set<string>();
  return failures.filter((failure) => {
    const key =
      failure.requestId ??
      failure.correlationId ??
      `${failure.signalKey ?? "unknown"}:${failure.stage ?? "unknown"}:${failure.errorCode}:${failure.occurredAt}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function dedupeSuccessfulSaves(saves: AdminLatestSuccessfulSave[]): AdminLatestSuccessfulSave[] {
  const seen = new Set<string>();
  return saves.filter((save) => {
    const key =
      save.requestId ??
      save.recordId ??
      save.receiptId ??
      `${save.signalKey ?? save.recordType ?? "unknown"}:${save.stage ?? save.event}:${save.occurredAt}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function isPresent<T>(value: T | null | undefined): value is T {
  return value != null;
}

function toFailureCountLabel(count: number, locale: AdminDashboardLocale): string {
  return count === 0
    ? text(locale, "no recent failed flow", "최근 실패 흐름 없음")
    : `${formatAdminCount(count)} ${text(locale, "recent failure(s)", "최근 실패")}`;
}

function toScenarioRiskItems(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
) {
  return summary.riskSummary.items.filter((item) => {
    if (scenario === "pet") {
      return item.code.startsWith("PET_");
    }

    if (scenario === "receipt") {
      return item.code.startsWith("RECEIPT_");
    }

    if (scenario === "files") {
      return item.code.startsWith("FILE_");
    }

    if (scenario === "accountDeletion") {
      return item.code.startsWith("ACCOUNT_DELETION_");
    }

    return false;
  });
}

function toScenarioRiskLabel(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
  locale: AdminDashboardLocale,
): string {
  const items = toScenarioRiskItems(summary, scenario);
  if (items.length === 0) {
    return text(locale, "No scenario-specific canonical risk", "시나리오 전용 정규 위험 없음");
  }

  const highestLevel = items.some((item) => item.level === "critical")
    ? "critical"
    : items.some((item) => item.level === "warning")
      ? "warning"
      : "attention";

  return `${formatAdminRiskLevelLabel(highestLevel, locale)} ${text(locale, "risk", "위험")} / ${locale === "ko" ? `${formatAdminCount(items.length)}개 신호` : `${formatAdminCount(items.length)} signals`}`;
}

function toScenarioTone(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
  hasFootprint: boolean,
): UserScenarioTimelineItem["tone"] {
  if (latestFailure) {
    return "critical";
  }

  if (scenario === "accountDeletion") {
    if (summary.accountDeletionStatus === "processing" || summary.accountDeletionStatus === "queued") {
      return "warning";
    }

    if (summary.accountDeletionStatus === "failed") {
      return "critical";
    }
  }

  const riskItems = toScenarioRiskItems(summary, scenario);
  if (riskItems.some((item) => item.level === "critical")) {
    return "critical";
  }
  if (riskItems.some((item) => item.level === "warning")) {
    return "warning";
  }
  if (riskItems.some((item) => item.level === "attention")) {
    return "attention";
  }

  if (hasFootprint && !latestSuccessfulSave) {
    return "attention";
  }

  return "default";
}

function toScenarioTitle(scenario: UserScenarioTimelineKey, locale: AdminDashboardLocale): string {
  if (scenario === "auth") {
    return text(locale, "Auth and session", "인증 및 세션");
  }
  if (scenario === "pet") {
    return text(locale, "Pet onboarding/profile", "펫 등록/프로필");
  }
  if (scenario === "social") {
    return text(locale, "Social profile/follow", "소셜 프로필/팔로우");
  }
  if (scenario === "chat") {
    return text(locale, "Chat", "채팅");
  }
  if (scenario === "community") {
    return text(locale, "Community", "커뮤니티");
  }
  if (scenario === "daily") {
    return text(locale, "Daily records", "일상 기록");
  }
  if (scenario === "health") {
    return text(locale, "Health records", "건강 기록");
  }
  if (scenario === "receipt") {
    return text(locale, "Receipts", "영수증");
  }
  if (scenario === "medication") {
    return text(locale, "Medication", "복약");
  }
  if (scenario === "ride") {
    return text(locale, "Ride requests", "이동지원 요청");
  }
  if (scenario === "files") {
    return text(locale, "File lifecycle", "파일 라이프사이클");
  }
  return text(locale, "Account deletion", "계정 삭제");
}

function hasScenarioRequestActivity(
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
): boolean {
  return Boolean(latestFailure || latestSuccessfulSave);
}

function toScenarioFootprintLabel(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
  locale: AdminDashboardLocale,
): string {
  if (scenario === "auth") {
    if (summary.lastLoginAt || summary.termsConsentAt || summary.accountIdentity?.linkedProviderCount) {
      return text(locale, "Account identity and session metadata available", "계정 식별자 및 세션 메타데이터 있음");
    }
    return text(locale, "No account identity or session metadata", "계정 식별자 또는 세션 메타데이터 없음");
  }
  if (scenario === "pet") {
    return formatScenarioCount(summary.counts.pets, locale, "pet profiles", "펫 프로필");
  }
  if (scenario === "social") {
    return hasScenarioRequestActivity(latestFailure, latestSuccessfulSave)
      ? text(locale, "Recent social request trace", "최근 소셜 요청 추적")
      : text(locale, "No social request trace", "소셜 요청 추적 없음");
  }
  if (scenario === "chat") {
    return hasScenarioRequestActivity(latestFailure, latestSuccessfulSave)
      ? text(locale, "Recent chat request trace", "최근 채팅 요청 추적")
      : text(locale, "No chat request trace", "채팅 요청 추적 없음");
  }
  if (scenario === "community") {
    return hasScenarioRequestActivity(latestFailure, latestSuccessfulSave)
      ? text(locale, "Recent community request trace", "최근 커뮤니티 요청 추적")
      : text(locale, "No community request trace", "커뮤니티 요청 추적 없음");
  }
  if (scenario === "daily") {
    return formatScenarioCount(summary.counts.dailyRecords, locale, "daily records", "일상 기록");
  }
  if (scenario === "health") {
    return formatScenarioCount(summary.counts.healthRecords, locale, "health records", "건강 기록");
  }
  if (scenario === "receipt") {
    return formatScenarioCount(summary.counts.receipts, locale, "receipt rows", "영수증 행");
  }
  if (scenario === "medication") {
    return hasScenarioRequestActivity(latestFailure, latestSuccessfulSave)
      ? text(locale, "Recent medication request trace", "최근 복약 요청 추적")
      : text(locale, "No medication request trace", "복약 요청 추적 없음");
  }
  if (scenario === "ride") {
    return hasScenarioRequestActivity(latestFailure, latestSuccessfulSave)
      ? text(locale, "Recent ride request trace", "최근 이동지원 요청 추적")
      : text(locale, "No ride request trace", "이동지원 요청 추적 없음");
  }
  if (scenario === "files") {
    return formatScenarioCount(summary.counts.files, locale, "file rows", "파일 행");
  }

  return formatAccountDeletionStatusSentence(summary.accountDeletionStatus ?? "none", locale);
}

function toScenarioFootprintCount(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
): number {
  if (scenario === "auth") {
    return summary.lastLoginAt || summary.termsConsentAt || summary.accountIdentity ? 1 : 0;
  }
  if (scenario === "pet") {
    return summary.counts.pets;
  }
  if (scenario === "social" || scenario === "chat" || scenario === "community" || scenario === "medication" || scenario === "ride") {
    return hasScenarioRequestActivity(latestFailure, latestSuccessfulSave) ? 1 : 0;
  }
  if (scenario === "daily") {
    return summary.counts.dailyRecords;
  }
  if (scenario === "health") {
    return summary.counts.healthRecords;
  }
  if (scenario === "receipt") {
    return summary.counts.receipts;
  }
  if (scenario === "files") {
    return summary.counts.files;
  }

  return summary.accountDeletionStatus && summary.accountDeletionStatus !== "none" ? 1 : 0;
}

function toScenarioLatestActivityLabel(
  summary: AdminUserSummary,
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
  locale: AdminDashboardLocale,
): string {
  const latestFailureAt = latestFailure?.occurredAt;
  const latestSuccessfulSaveAt = latestSuccessfulSave?.occurredAt;

  if (latestFailureAt && (!latestSuccessfulSaveAt || latestFailureAt >= latestSuccessfulSaveAt)) {
    return `${text(locale, "Latest activity: failure at", "최근 활동: 실패 시각")} ${formatAdminDate(latestFailureAt)}`;
  }

  if (latestSuccessfulSaveAt) {
    return `${text(locale, "Latest activity: success at", "최근 활동: 성공 시각")} ${formatAdminDate(latestSuccessfulSaveAt)}`;
  }

  if (summary.accountDeletionStatus && summary.accountDeletionStatus !== "none") {
    return `${text(locale, "Latest activity: status", "최근 활동: 상태")} ${formatAdminAccountDeletionStatusLabel(summary.accountDeletionStatus, locale)}`;
  }

  return text(locale, "No recent scenario activity in the current summary window", "현재 요약 구간에 최근 시나리오 활동이 없습니다");
}

function toScenarioHeadline(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
  hasFootprint: boolean,
  locale: AdminDashboardLocale,
): string {
  if (latestFailure) {
    return latestFailure.errorCode;
  }

  if (latestSuccessfulSave) {
    return latestSuccessfulSave.event;
  }

  if (scenario === "accountDeletion") {
    return toAccountDeletionHeadline(summary.accountDeletionStatus, locale);
  }

  if (scenario === "auth") {
    return hasFootprint
      ? text(locale, "Account identity linked", "계정 식별자 연결됨")
      : text(locale, "No auth/session footprint", "인증/세션 이력 없음");
  }
  if (scenario === "pet") {
    return hasFootprint ? formatScenarioCount(summary.counts.pets, locale, "pet profiles linked", "펫 프로필 연결") : text(locale, "No pet profile footprint", "펫 프로필 이력 없음");
  }
  if (scenario === "social") {
    return hasFootprint ? text(locale, "Recent social request linked", "최근 소셜 요청 연결") : text(locale, "No social footprint", "소셜 이력 없음");
  }
  if (scenario === "chat") {
    return hasFootprint ? text(locale, "Recent chat request linked", "최근 채팅 요청 연결") : text(locale, "No chat footprint", "채팅 이력 없음");
  }
  if (scenario === "community") {
    return hasFootprint ? text(locale, "Recent community request linked", "최근 커뮤니티 요청 연결") : text(locale, "No community footprint", "커뮤니티 이력 없음");
  }
  if (scenario === "daily") {
    return hasFootprint
      ? formatScenarioCount(summary.counts.dailyRecords, locale, "daily records linked", "일상 기록 연결")
      : text(locale, "No daily record footprint", "일상 기록 이력 없음");
  }
  if (scenario === "health") {
    return hasFootprint
      ? formatScenarioCount(summary.counts.healthRecords, locale, "health records linked", "건강 기록 연결")
      : text(locale, "No health record footprint", "건강 기록 이력 없음");
  }
  if (scenario === "receipt") {
    return hasFootprint
      ? formatScenarioCount(summary.counts.receipts, locale, "receipt rows linked", "영수증 행 연결")
      : text(locale, "No receipt footprint", "영수증 이력 없음");
  }
  if (scenario === "medication") {
    return hasFootprint ? text(locale, "Recent medication request linked", "최근 복약 요청 연결") : text(locale, "No medication footprint", "복약 이력 없음");
  }
  if (scenario === "ride") {
    return hasFootprint ? text(locale, "Recent ride request linked", "최근 이동지원 요청 연결") : text(locale, "No ride footprint", "이동지원 요청 이력 없음");
  }

  return hasFootprint ? formatScenarioCount(summary.counts.files, locale, "file rows linked", "파일 행 연결") : text(locale, "No file footprint", "파일 이력 없음");
}

function toScenarioSummary(
  summary: AdminUserSummary,
  scenario: UserScenarioTimelineKey,
  latestFailure: AdminLatestFailure | undefined,
  latestSuccessfulSave: AdminLatestSuccessfulSave | undefined,
  failureCount: number,
  hasFootprint: boolean,
  locale: AdminDashboardLocale,
): string {
  if (latestFailure) {
    return `${toFailureCountLabel(failureCount, locale)}. ${text(locale, "Open the failed flow first, then compare the current footprint and release context in user detail.", "먼저 오류 흐름을 열고, 사용자 상세에서 현재 데이터 현황과 릴리스 문맥을 비교하세요.")}`;
  }

  if (latestSuccessfulSave) {
    return text(locale, "Recent success evidence is visible in the summary window. Use it as the first anchor, then compare it with current footprint and any remaining risk signal.", "요약 구간에 최근 성공 근거가 보입니다. 이를 첫 기준점으로 삼고 현재 데이터 현황과 남아 있는 위험 신호를 비교하세요.");
  }

  if (scenario === "accountDeletion") {
    if (summary.accountDeletionStatus === "processing") {
      return text(locale, "Deletion is already processing. Start from user detail and compare durable job state before reading raw account rows.", "삭제가 이미 진행 중입니다. 원본 계정 행을 보기 전에 사용자 상세에서 영속 작업 상태를 비교하세요.");
    }
    if (summary.accountDeletionStatus === "queued") {
      return text(locale, "Deletion is queued. Use user detail to confirm queue state and any follow-up request trace.", "삭제가 대기 중입니다. 사용자 상세에서 대기열 상태와 후속 요청 추적 정보를 확인하세요.");
    }
    if (summary.accountDeletionStatus === "succeeded") {
      return text(locale, "Deletion reached completed state in the current summary window, but no separate recent save event is exposed here.", "현재 요약 구간에서 삭제가 완료 상태에 도달했지만 별도의 최근 저장 이벤트는 노출되지 않습니다.");
    }

    return text(locale, "No account deletion workflow is visible for this user in the current summary window.", "현재 요약 구간에는 이 사용자의 계정 삭제 흐름이 보이지 않습니다.");
  }

  if (hasFootprint) {
    return text(locale, "Historical footprint exists, but no recent scenario success is visible in the current summary window. Use user detail to compare current rows and older activity.", "과거 데이터 이력은 있지만 현재 요약 구간에는 최근 시나리오 성공이 보이지 않습니다. 사용자 상세에서 현재 행과 이전 활동을 비교하세요.");
  }

  return text(locale, "No scenario footprint or recent activity is visible for this user yet.", "이 사용자에게는 아직 시나리오 데이터 이력이나 최근 활동이 보이지 않습니다.");
}

function toLatestFailureForScenario(
  summary: AdminUserSummary,
  predicate: (failure: AdminLatestFailure) => boolean,
): AdminLatestFailure | undefined {
  return dedupeFailures(
    [summary.latestFailure, ...summary.recentFailures].filter(isPresent).filter(predicate),
  ).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt))[0];
}

function toLatestSuccessfulSaveForScenario(
  summary: AdminUserSummary,
  predicate: (save: AdminLatestSuccessfulSave) => boolean,
): AdminLatestSuccessfulSave | undefined {
  return dedupeSuccessfulSaves(
    [summary.latestSuccessfulSave, ...summary.recentSuccessfulSaves].filter(isPresent).filter(predicate),
  ).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt))[0];
}

function toSaveViewModel(save: AdminLatestSuccessfulSave | undefined) {
  return save
    ? {
        event: save.event,
        occurredAtLabel: formatAdminDate(save.occurredAt),
        signalKey: save.signalKey,
        stage: save.stage,
        requestId: save.requestId,
        correlationId: save.correlationId,
        recordType: save.recordType,
        recordId: save.recordId,
        receiptId: save.receiptId,
      }
    : undefined;
}

function hasTraceLookup(lookup: AdminUserLookupInput | undefined): boolean {
  return Boolean(lookup?.requestId || lookup?.correlationId);
}

function matchesLookupTrace(
  input:
    | {
        requestId?: string;
        correlationId?: string;
      }
    | undefined,
  lookup: AdminUserLookupInput | undefined,
): boolean {
  if (!input) {
    return false;
  }

  if (lookup?.requestId && input.requestId === lookup.requestId) {
    return true;
  }

  if (lookup?.correlationId && input.correlationId === lookup.correlationId) {
    return true;
  }

  return false;
}

function resolveTraceScopedFailure(
  summary: AdminUserSummary,
  lookup: AdminUserLookupInput | undefined,
): AdminLatestFailure | undefined {
  if (!hasTraceLookup(lookup)) {
    return summary.latestFailure;
  }

  return dedupeFailures([summary.latestFailure, ...summary.recentFailures].filter(isPresent)).find(
    (failure) => matchesLookupTrace(failure, lookup),
  );
}

function resolveTraceScopedSuccessfulSave(
  summary: AdminUserSummary,
  lookup: AdminUserLookupInput | undefined,
): AdminLatestSuccessfulSave | undefined {
  if (!hasTraceLookup(lookup)) {
    return summary.latestSuccessfulSave;
  }

  return dedupeSuccessfulSaves(
    [summary.latestSuccessfulSave, ...summary.recentSuccessfulSaves].filter(isPresent),
  ).find((save) => matchesLookupTrace(save, lookup));
}

function resolveTraceScopedFailureForScenario(
  summary: AdminUserSummary,
  lookup: AdminUserLookupInput | undefined,
  predicate: (failure: AdminLatestFailure) => boolean,
): AdminLatestFailure | undefined {
  if (!hasTraceLookup(lookup)) {
    return toLatestFailureForScenario(summary, predicate);
  }

  return dedupeFailures([summary.latestFailure, ...summary.recentFailures].filter(isPresent))
    .filter(predicate)
    .find((failure) => matchesLookupTrace(failure, lookup));
}

function resolveTraceScopedSuccessfulSaveForScenario(
  summary: AdminUserSummary,
  lookup: AdminUserLookupInput | undefined,
  predicate: (save: AdminLatestSuccessfulSave) => boolean,
): AdminLatestSuccessfulSave | undefined {
  if (!hasTraceLookup(lookup)) {
    return toLatestSuccessfulSaveForScenario(summary, predicate);
  }

  return dedupeSuccessfulSaves(
    [summary.latestSuccessfulSave, ...summary.recentSuccessfulSaves].filter(isPresent),
  )
    .filter(predicate)
    .find((save) => matchesLookupTrace(save, lookup));
}

function buildScenarioTimeline(
  summary: AdminUserSummary,
  detailRoute: `/admin/users/${string}`,
  lookup: AdminUserLookupInput | undefined,
  locale: AdminDashboardLocale,
) {
  const scenarios = USER_SCENARIO_FALLBACK_ORDER.map((scenario): UserScenarioTimelineItem & {
    latestActivityAt?: string;
    sortIndex: number;
  } => {
    const latestFailure =
      scenario === "auth"
        ? resolveTraceScopedFailureForScenario(summary, lookup, isAuthFailure)
        : scenario === "pet"
        ? resolveTraceScopedFailureForScenario(summary, lookup, isPetFailure)
        : scenario === "social"
          ? resolveTraceScopedFailureForScenario(summary, lookup, isSocialFailure)
          : scenario === "chat"
            ? resolveTraceScopedFailureForScenario(summary, lookup, isChatFailure)
            : scenario === "community"
              ? resolveTraceScopedFailureForScenario(summary, lookup, isCommunityFailure)
        : scenario === "daily"
          ? resolveTraceScopedFailureForScenario(summary, lookup, isDailyFailure)
          : scenario === "health"
            ? resolveTraceScopedFailureForScenario(summary, lookup, isHealthFailure)
            : scenario === "receipt"
              ? resolveTraceScopedFailureForScenario(summary, lookup, isReceiptFailure)
              : scenario === "medication"
                ? resolveTraceScopedFailureForScenario(summary, lookup, isMedicationFailure)
                : scenario === "ride"
                  ? resolveTraceScopedFailureForScenario(summary, lookup, isRideFailure)
              : scenario === "files"
                ? resolveTraceScopedFailureForScenario(summary, lookup, isFileFailure)
                : resolveTraceScopedFailureForScenario(summary, lookup, isAccountDeletionFailure);
    const latestSuccessfulSave =
      scenario === "auth"
        ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isAuthSuccessfulSave)
        : scenario === "pet"
        ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isPetSuccessfulSave)
        : scenario === "social"
          ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isSocialSuccessfulSave)
          : scenario === "chat"
            ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isChatSuccessfulSave)
            : scenario === "community"
              ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isCommunitySuccessfulSave)
        : scenario === "daily"
          ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isDailySuccessfulSave)
          : scenario === "health"
            ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isHealthSuccessfulSave)
            : scenario === "receipt"
              ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isReceiptSuccessfulSave)
              : scenario === "medication"
                ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isMedicationSuccessfulSave)
                : scenario === "ride"
                  ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isRideSuccessfulSave)
              : scenario === "files"
                ? resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isFileSuccessfulSave)
                : resolveTraceScopedSuccessfulSaveForScenario(summary, lookup, isAccountDeletionSuccessfulSave);
    const latestActivityAt = [latestFailure?.occurredAt, latestSuccessfulSave?.occurredAt]
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => right.localeCompare(left))[0];
    const hasFootprint = toScenarioFootprintCount(summary, scenario, latestFailure, latestSuccessfulSave) > 0;
    const latestFailureViewModel = latestFailure ? toFailureViewModel(latestFailure) : undefined;

    return {
      key: scenario,
      title: toScenarioTitle(scenario, locale),
      tone: toScenarioTone(summary, scenario, latestFailure, latestSuccessfulSave, hasFootprint),
      headline: toScenarioHeadline(summary, scenario, latestFailure, latestSuccessfulSave, hasFootprint, locale),
      latestActivityLabel: toScenarioLatestActivityLabel(summary, latestFailure, latestSuccessfulSave, locale),
      footprintLabel: toScenarioFootprintLabel(summary, scenario, latestFailure, latestSuccessfulSave, locale),
      riskLabel: toScenarioRiskLabel(summary, scenario, locale),
      summary: toScenarioSummary(
        summary,
        scenario,
        latestFailure,
        latestSuccessfulSave,
        latestFailure ? 1 : 0,
        hasFootprint,
        locale,
      ),
      primaryActionLabel: latestFailureViewModel?.drilldownTo ? text(locale, "Open failed flow", "오류 흐름 열기") : text(locale, "Open detail", "상세 열기"),
      primaryActionTo: latestFailureViewModel?.drilldownTo ?? detailRoute,
      secondaryActionLabel: latestFailureViewModel?.drilldownTo ? text(locale, "Open detail", "상세 열기") : undefined,
      secondaryActionTo: latestFailureViewModel?.drilldownTo ? detailRoute : undefined,
      latestFailure: latestFailureViewModel,
      latestSuccessfulSave: toSaveViewModel(latestSuccessfulSave),
      latestActivityAt,
      sortIndex: USER_SCENARIO_FALLBACK_ORDER.indexOf(scenario),
    };
  });

  return scenarios
    .sort((left, right) => {
      if (left.latestActivityAt && right.latestActivityAt) {
        return right.latestActivityAt.localeCompare(left.latestActivityAt);
      }
      if (left.latestActivityAt) {
        return -1;
      }
      if (right.latestActivityAt) {
        return 1;
      }
      return left.sortIndex - right.sortIndex;
    })
    .map(({ latestActivityAt: _latestActivityAt, sortIndex: _sortIndex, ...item }) => item);
}

function toDailyRecordTriageViewModel(detail: AdminUserDataDetail, locale: AdminDashboardLocale) {
  const dailyFailures = dedupeFailures(
    [
      detail.user.latestFailure,
      ...detail.user.recentFailures,
    ].filter(isPresent).filter(isDailyFailure),
  ).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));
  const dailySuccessfulSaves = dedupeSuccessfulSaves(
    [
      detail.user.latestSuccessfulSave,
      ...detail.user.recentSuccessfulSaves,
    ].filter(isPresent).filter(isDailySuccessfulSave),
  ).sort((left, right) => right.occurredAt.localeCompare(left.occurredAt));

  const latestFailure = dailyFailures[0];
  const latestSuccessfulSave = dailySuccessfulSaves[0];
  const riskLevel: UserDataDetailViewModel["dailyRecordTriage"]["riskLevel"] =
    latestFailure ? "critical" : latestSuccessfulSave ? "none" : detail.user.counts.dailyRecords > 0 ? "attention" : "none";
  const summary = latestFailure
      ? text(locale, "Latest daily failure exists. Check the failed flow before reviewing raw record rows.", "최근 일상 실패가 있습니다. 원본 기록 행을 보기 전에 오류 흐름을 먼저 확인하세요.")
    : latestSuccessfulSave
      ? text(locale, "Recent confirmed daily save exists. Use this as the first anchor for daily triage.", "최근 확정된 일상 저장이 있습니다. 이를 일상 조사 시작점으로 사용하세요.")
      : detail.user.counts.dailyRecords > 0
        ? text(locale, "Historical daily records exist, but no recent confirmed daily save is visible in the current save window.", "과거 일상 기록은 있지만 현재 저장 구간에는 최근 확정 저장이 보이지 않습니다.")
        : text(locale, "No daily record history is available for this user yet.", "이 사용자에게는 아직 일상 기록 이력이 없습니다.");

  return {
    riskLevel,
    summary,
    dailyRecordCountLabel: formatAdminCount(detail.user.counts.dailyRecords),
    recentFailureCountLabel: formatAdminCount(dailyFailures.length),
    recentSuccessfulSaveCountLabel: formatAdminCount(dailySuccessfulSaves.length),
    latestFailure: latestFailure ? toFailureViewModel(latestFailure) : undefined,
    latestSuccessfulSave: toSaveViewModel(latestSuccessfulSave),
  };
}

function toCountCards(summary: AdminUserSummary, locale: AdminDashboardLocale) {
  return [
    {
      key: "pets" as const,
      label: text(locale, "Pets", "펫"),
      valueLabel: formatAdminCount(summary.counts.pets),
    },
    {
      key: "records" as const,
      label: text(locale, "Records", "기록"),
      valueLabel: formatAdminCount(summary.counts.records),
    },
    {
      key: "dailyRecords" as const,
      label: text(locale, "Daily", "일상"),
      valueLabel: formatAdminCount(summary.counts.dailyRecords),
    },
    {
      key: "healthRecords" as const,
      label: text(locale, "Health", "건강"),
      valueLabel: formatAdminCount(summary.counts.healthRecords),
    },
    {
      key: "receipts" as const,
      label: text(locale, "Receipts", "영수증"),
      valueLabel: formatAdminCount(summary.counts.receipts),
    },
    {
      key: "files" as const,
      label: text(locale, "Files", "파일"),
      valueLabel: formatAdminCount(summary.counts.files),
    },
    {
      key: "aiJobs" as const,
      label: text(locale, "AI Jobs", "AI 작업"),
      valueLabel: formatAdminCount(summary.counts.aiJobs),
    },
  ];
}

export function toUserOperationsViewModel(
  summary: AdminUserSummary,
  meta: AdminQueryMeta,
  lookup: AdminUserLookupInput = {},
  locale: AdminDashboardLocale = "ko",
): UserOperationsViewModel {
  const detailRoute = `/admin/users/${encodeURIComponent(summary.internalUserId)}` as const;
  const latestPetFailure = summary.latestFailure && isPetFailure(summary.latestFailure)
    ? summary.latestFailure
    : summary.recentFailures.find(isPetFailure);
  const localizedRiskSummary = localizeRiskSummary(summary.riskSummary, locale, latestPetFailure);
  const traceScopedFailure = resolveTraceScopedFailure(summary, lookup);
  const traceScopedSuccessfulSave = resolveTraceScopedSuccessfulSave(summary, lookup);
  const latestFailure = traceScopedFailure
    ? {
        feature: traceScopedFailure.feature,
        useCase: traceScopedFailure.useCase,
        errorCode: traceScopedFailure.errorCode,
        occurredAtLabel: formatAdminDate(traceScopedFailure.occurredAt),
        requestId: traceScopedFailure.requestId,
        correlationId: traceScopedFailure.correlationId,
        drilldownTo: buildFailureDrilldownTo(traceScopedFailure),
      }
    : undefined;
  const latestSuccessfulSave = traceScopedSuccessfulSave
    ? {
        event: traceScopedSuccessfulSave.event,
        occurredAtLabel: formatAdminDate(traceScopedSuccessfulSave.occurredAt),
        signalKey: traceScopedSuccessfulSave.signalKey,
        stage: traceScopedSuccessfulSave.stage,
        requestId: traceScopedSuccessfulSave.requestId,
        correlationId: traceScopedSuccessfulSave.correlationId,
        recordType: traceScopedSuccessfulSave.recordType,
        recordId: traceScopedSuccessfulSave.recordId,
        receiptId: traceScopedSuccessfulSave.receiptId,
      }
    : undefined;

  return {
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    freshness: toFreshness(meta),
    internalUserId: summary.internalUserId,
    userIdHash: summary.userIdHash,
    createdAtLabel: formatAdminDate(summary.createdAt),
    accountIdentityLabel: formatAdminAccountIdentityLabel(summary.accountIdentity, locale),
    providerIdentityLabel: formatAdminProviderIdentityLabel(summary.accountIdentity, locale),
    linkedProviderCountLabel: formatAdminLinkedProviderCountLabel(summary.accountIdentity, locale),
    latestDeviceInstallIdLabel: summary.latestDeviceInstallId ?? (locale === "ko" ? "기기 정보 없음" : "No device install"),
    latestPlatformLabel: summary.latestPlatform ?? (locale === "ko" ? "알 수 없음" : "unknown"),
    latestRuntimeSummaryLabel: formatAdminTelemetryRuntimeSummaryLabel(
      {
        appBuildNumber: summary.latestRuntimeAppBuildNumber,
        deviceModelNameBucket: summary.latestRuntimeDeviceModelNameBucket,
        osVersionMinorBucket: summary.latestRuntimeOsVersionMinorBucket,
        networkTypeBucket: summary.latestRuntimeNetworkTypeBucket,
        physicalDeviceBucket: summary.latestRuntimePhysicalDeviceBucket,
      },
      locale,
    ),
    latestRuntimeCoverageLabel: formatAdminTelemetryRuntimeCoverageLabel(
      {
        deviceInstallId: summary.latestDeviceInstallId ?? null,
        platform: summary.latestPlatform ?? null,
        appBuildNumber: summary.latestRuntimeAppBuildNumber,
        deviceModelNameBucket: summary.latestRuntimeDeviceModelNameBucket,
        osVersionMinorBucket: summary.latestRuntimeOsVersionMinorBucket,
        networkTypeBucket: summary.latestRuntimeNetworkTypeBucket,
        physicalDeviceBucket: summary.latestRuntimePhysicalDeviceBucket,
      },
      locale,
    ),
    lastLoginAtLabel: formatAdminDate(summary.lastLoginAt),
    termsConsentAtLabel: formatAdminDate(summary.termsConsentAt),
    accountDeletionStatusLabel: formatAdminAccountDeletionStatusLabel(summary.accountDeletionStatus ?? "none", locale),
    latestReleaseLabel: summary.latestRelease ?? "-",
    latestAppVersionLabel: summary.latestAppVersion ?? "-",
    detailRoute,
    riskSummary: localizedRiskSummary,
    triageCards: buildUserOperationsTriageCards(
      summary,
      detailRoute,
      latestFailure,
      latestSuccessfulSave,
      hasTraceLookup(lookup),
      locale,
    ),
    scenarioTimeline: buildScenarioTimeline(summary, detailRoute, lookup, locale),
    counts: toCountCards(summary, locale),
    latestFailure,
    latestSuccessfulSave,
    recentFailures: summary.recentFailures.map((failure, index) => toRecentFailureViewModel(failure, index, locale)),
    recentSuccessfulSaves: summary.recentSuccessfulSaves.map(toRecentSuccessfulSaveViewModel),
  };
}

export function toUserDataDetailViewModel(
  detail: AdminUserDataDetail,
  meta: AdminQueryMeta,
  riskSummary: AdminUserDataRiskSummary,
  locale: AdminDashboardLocale = "ko",
): UserDataDetailViewModel {
  const latestPetFailure = findLatestPetFailure(detail);
  const mediaArtifacts = detail.mediaArtifacts ?? [];
  const representativeArtifactId = detail.representativePhoto?.artifactId;
  const failureRequestIds = new Set(
    [
      detail.user.latestFailure?.requestId,
      ...detail.user.recentFailures.map((failure) => failure.requestId),
    ].filter((value): value is string => Boolean(value)),
  );
  const artifactLifecycleDrilldownMap = buildArtifactLifecycleDrilldownMap(
    detail.files,
    mediaArtifacts,
    failureRequestIds,
    locale,
  );

  return {
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    freshness: toFreshness(meta),
    internalUserId: detail.user.internalUserId,
    userIdHash: detail.user.userIdHash,
    accountIdentityLabel: formatAdminAccountIdentityLabel(detail.account, locale),
    providerIdentityLabel: formatAdminProviderIdentityLabel(detail.account, locale),
    linkedProviderCountLabel: formatAdminLinkedProviderCountLabel(detail.account, locale),
    latestReleaseLabel: detail.user.latestRelease ?? "-",
    latestAppVersionLabel: detail.user.latestAppVersion ?? "-",
    counts: toCountCards(detail.user, locale),
    riskSummary: localizeRiskSummary(riskSummary, locale, latestPetFailure),
    accountDeletionSummary: toAccountDeletionSummary(detail, riskSummary, locale),
    dailyRecordTriage: toDailyRecordTriageViewModel(detail, locale),
    uploadDiagnoses: buildUploadDiagnoses(detail, mediaArtifacts, locale),
    latestPetFailure: latestPetFailure ? toFailureViewModel(latestPetFailure) : undefined,
    latestFailure: detail.user.latestFailure
      ? toFailureViewModel(detail.user.latestFailure)
      : undefined,
    latestSuccessfulSave: toSaveViewModel(detail.user.latestSuccessfulSave),
    recentFailures: detail.user.recentFailures.map((failure, index) => toRecentFailureViewModel(failure, index, locale)),
    recentSuccessfulSaves: detail.user.recentSuccessfulSaves.map(toRecentSuccessfulSaveViewModel),
    pets: detail.pets.map(toPetViewModel),
    records: detail.records.map(toRecordViewModel),
    receipts: detail.receipts.map(toReceiptViewModel),
    files: detail.files.map((file) =>
      toFileViewModel(
        file,
        failureRequestIds,
        resolveRelatedMediaArtifactForFile(file, mediaArtifacts),
        locale,
      ),
    ),
    representativePhoto: detail.representativePhoto
      ? (() => {
          const archiveStateKey = pickMediaArchiveStateKey(detail.representativePhoto);
          const incidentAttachmentStateKey = pickMediaIncidentAttachmentStateKey(
            detail.representativePhoto,
          );

          return {
            artifactId: detail.representativePhoto.artifactId,
            sourceScenarioKey: detail.representativePhoto.sourceScenario,
            sourceScenarioLabel: formatMediaScenarioLabel(detail.representativePhoto.sourceScenario, locale),
            linkedEntityTypeLabel: formatMediaLinkedEntityTypeLabel(
              detail.representativePhoto.linkedEntityType,
              locale,
            ),
            relationTypeKey: detail.representativePhoto.relationType,
            relationTypeLabel: formatMediaRelationTypeLabel(detail.representativePhoto.relationType, locale),
            artifactRoleLabel: formatMediaArtifactRoleLabel(detail.representativePhoto.artifactRole, locale),
            contentTypeBucketLabel: detail.representativePhoto.contentTypeBucket,
            sizeBucketLabel: detail.representativePhoto.sizeBucket,
            uploadedAtLabel: formatAdminDate(detail.representativePhoto.uploadedAt),
            storageStatusLabel: formatMediaStorageStatusLabel(detail.representativePhoto.storageStatus, locale),
            previewAvailability: detail.representativePhoto.previewAvailability,
            previewAvailabilityLabel: formatMediaAvailabilityLabel(
              detail.representativePhoto.previewAvailability,
              locale,
            ),
            downloadAvailability: detail.representativePhoto.downloadAvailability,
            downloadAvailabilityLabel: formatMediaAvailabilityLabel(
              detail.representativePhoto.downloadAvailability,
              locale,
            ),
            binaryReviewPathLabel: pickArtifactBinaryReviewPathLabel(detail.representativePhoto, locale),
            binaryReviewHint: pickArtifactBinaryReviewHint(detail.representativePhoto, locale),
            latestRequestId: detail.representativePhoto.latestTraceKey?.requestId,
            latestRequestIdLabel: toLabel(detail.representativePhoto.latestTraceKey?.requestId),
            latestCorrelationId: detail.representativePhoto.latestTraceKey?.correlationId,
            latestCorrelationIdLabel: toLabel(detail.representativePhoto.latestTraceKey?.correlationId),
            latestTraceLabel: formatLatestTraceLabel(detail.representativePhoto.latestTraceKey),
            requestTraceDrilldownTo: detail.representativePhoto.latestTraceKey?.requestId
              ? `/admin/failed-flows?${new URLSearchParams({ requestId: detail.representativePhoto.latestTraceKey.requestId }).toString()}`
              : detail.representativePhoto.latestTraceKey?.correlationId
                ? `/admin/failed-flows?${new URLSearchParams({ correlationId: detail.representativePhoto.latestTraceKey.correlationId }).toString()}`
                : undefined,
            fallbackReasonLabel: formatMediaFallbackReasonLabel(
              detail.representativePhoto.fallbackReason,
              locale,
            ),
            archiveStateLabel: formatMediaArchiveStateLabel(archiveStateKey, locale),
            archiveStateHint: pickMediaArchiveStateHint(archiveStateKey, locale),
            incidentAttachmentStateLabel: formatMediaIncidentAttachmentStateLabel(
              incidentAttachmentStateKey,
              locale,
            ),
            incidentAttachmentHint: pickMediaIncidentAttachmentHint(
              incidentAttachmentStateKey,
              locale,
            ),
          };
        })()
      : undefined,
    mediaArtifacts: mediaArtifacts.map((artifact) =>
      toMediaArtifactViewModel(
        artifact,
        locale,
        mediaArtifacts.filter((candidate) => candidate.sourceScenario === artifact.sourceScenario),
        artifactLifecycleDrilldownMap.get(artifact.artifactId),
      ),
    ),
    mediaScenarioPivots: toMediaScenarioPivots(mediaArtifacts, representativeArtifactId, locale),
    mediaTimelineRows: toMediaTimelineRows(mediaArtifacts, representativeArtifactId, locale),
    mediaCoverageLedger: toMediaCoverageLedger(mediaArtifacts, representativeArtifactId, locale),
    missingPhotoSourceDisclosures: toMissingPhotoSourceDisclosures(mediaArtifacts, locale),
    photoSourceSurfaceMatrix: toPhotoSourceSurfaceMatrix(mediaArtifacts, locale),
    incidentLogArtifacts: (detail.incidentLogArtifacts ?? []).map((artifact) =>
      toIncidentLogArtifactViewModel(artifact, locale),
    ),
    aiJobs: detail.aiJobs.map((job) => toJobViewModel(job, locale)),
    account: toAccountRows(detail.account, locale),
  };
}
